import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { formatCurrency } from '../utils/formatters';
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import './ClientDashboard.css';
import SupportTickets from './SupportTickets';
import TripHistory from './trip-operations/TripHistory';

const API = '/api';

const Icon = ({ d, size = 20, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
        stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    building: ['M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z', 'M9 22V12h6v10'],
    user: ['M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2', 'M12 7a4 4 0 1 0 0 8 4 4 0 0 0 0-8z'],
    mail: ['M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z', 'M22 6l-10 7L2 6'],
    phone: 'M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.18 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.6a16 16 0 0 0 6 6l.92-.92a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z',
    map: ['M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z', 'M12 7a3 3 0 1 0 0 6 3 3 0 0 0 0-6z'],
    briefcase: ['M20 7H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z', 'M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2'],
    logout: ['M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4', 'M16 17l5-5-5-5', 'M21 12H9'],
    check: 'M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4L12 14.01l-3-3',
    trips: ['M5 17H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v3', 'M14 17H9m10 0h3m-3 0a3 3 0 1 1-6 0 3 3 0 0 1 6 0zm1-10h.01'],
    invoice: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6', 'M16 13H8', 'M16 17H8', 'M10 9H8'],
    analytics: ['M3 3v18h18', 'M18 17l-6-6-4 4-4-4'],
    staff: ['M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2', 'M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', 'M23 21v-2a4 4 0 0 0-3-3.87', 'M16 3.13a4 4 0 0 1 0 7.75'],
    plus: ['M12 5v14', 'M5 12h14'],
    edit: ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
    trash: ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2'],
    x: ['M18 6 6 18', 'M6 6l12 12'],
    search: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M21 21l-4.35-4.35'],
    chevronDown: 'M6 9l6 6 6-6',
    chevronUp: 'M18 15l-6-6-6 6',
    fileText: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6', 'M16 13H8', 'M16 17H8'],
    download: ['M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4', 'm7 10 5 5 5-5', 'M12 15V3'],
    bell: ['M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9', 'M13.73 21a2 2 0 0 1-3.46 0'],
    alertCircle: ['M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z', 'M12 8v4', 'M12 16h.01'],
    clock: ['M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z', 'M12 6V12L16 14'],
    tickets: ['M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z'],
    users: ['M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2', 'M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', 'M23 21v-2a4 4 0 0 0-3-3.87', 'M16 3.13a4 4 0 0 1 0 7.75'],
    calendar: ['M3 4v16a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2z', 'M16 2v4', 'M8 2v4', 'M3 10h18'],
};

function LocationAutocomplete({ value, onChange, placeholder, icon, required }) {
    const [suggestions, setSuggestions] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [loading, setLoading] = useState(false);
    const timeoutRef = useRef(null);

    const fetchSuggestions = async (query) => {
        if (!query || query.length < 3) {
            setSuggestions([]);
            return;
        }
        setLoading(true);
        try {
            const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&accept-language=en`);
            const data = await res.json();
            setSuggestions(data.map(d => d.display_name));
        } catch { }
        setLoading(false);
    };

    const handleInputChange = (e) => {
        const val = e.target.value;
        onChange(val);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            fetchSuggestions(val);
        }, 500);
        setShowDropdown(true);
    };

    return (
        <div className="cd-input-wrap" style={{ position: 'relative' }}>
            {icon && <Icon d={icon} size={18} color="#94a3b8" className="cd-input-icon" />}
            <input 
                className="cd-modal-input-icon" 
                style={{ border: '1px solid #cbd5e1', width: '100%', boxSizing: 'border-box' }}
                value={value} 
                onChange={handleInputChange}
                onFocus={() => { if (suggestions.length) setShowDropdown(true); }}
                onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                placeholder={placeholder}
                required={required}
                autoComplete="off"
            />
            {showDropdown && (suggestions.length > 0 || loading) && (
                <ul style={{ 
                    position: 'absolute', top: '100%', left: 0, right: 0, 
                    background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, 
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 50, 
                    listStyle: 'none', padding: 0, margin: '4px 0 0 0', 
                    maxHeight: 250, overflowY: 'auto' 
                }}>
                    {loading && <li style={{ padding: '10px 16px', fontSize: 13, color: '#94a3b8', fontStyle: 'italic' }}>Searching...</li>}
                    {!loading && suggestions.map((s, i) => (
                        <li 
                            key={i} 
                            style={{ padding: '10px 16px', fontSize: 13, color: '#334155', cursor: 'pointer', borderBottom: i < suggestions.length - 1 ? '1px solid #f1f5f9' : 'none', wordBreak: 'break-word' }}
                            onMouseDown={() => {
                                onChange(s);
                                setShowDropdown(false);
                            }}
                            onMouseEnter={e => e.target.style.background = '#f8fafc'}
                            onMouseLeave={e => e.target.style.background = 'white'}
                        >
                            {s}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export default function ClientDashboard({ client, onLogout, operatorDetails, onNavigate }) {
    const [trips, setTrips] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [clientUsers, setClientUsers] = useState([]);
    const [passengers, setPassengers] = useState([]);
    const [quotations, setQuotations] = useState([]);
    const [routes, setRoutes] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [allocations, setAllocations] = useState([]);
    const [loadingTrips, setLoadingTrips] = useState(true);
    const [loadingInvoices, setLoadingInvoices] = useState(true);
    const [loadingQuotations, setLoadingQuotations] = useState(true);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [loadingPassengers, setLoadingPassengers] = useState(true);
    const [loadingRoutes, setLoadingRoutes] = useState(true);
    const [loadingVehicles, setLoadingVehicles] = useState(true);
    const [loadingAllocations, setLoadingAllocations] = useState(true);

    const hasPerm = (perm) => {
        if (!client) return false;
        if (!client.permissions || client.permissions.includes('all')) return true;
        return client.permissions.includes(perm);
    };

    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [activeTab, setActiveTab] = useState('analytics');
    const [financeMenuOpen, setFinanceMenuOpen] = useState(false);
    const [reportsMenuOpen, setReportsMenuOpen] = useState(false);
    const [countries, setCountries] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [showNotifPanel, setShowNotifPanel] = useState(false);
    const [viewDoc, setViewDoc] = useState(null);
    const [tripStatusFilter, setTripStatusFilter] = useState('All');
    const [masterData, setMasterData] = useState([]);
    const [viewingHistoryTripId, setViewingHistoryTripId] = useState(null);
    const [editingTrip, setEditingTrip] = useState(null);
    const [isEditingProfile, setIsEditingProfile] = useState(false);
    const [profileForm, setProfileForm] = useState({
        companyName: client?.companyName || '',
        contactPerson: client?.contactPerson || '',
        phone: client?.phone || '',
        industry: client?.industry || '',
        workLocation: client?.workLocation || '',
        address: client?.address || '',
        status: client?.status || 'Active',
        password: client?.password || '',
        clientLogo: client?.clientLogo || ''
    });
    const [clientLogoFile, setClientLogoFile] = useState(null);
    const [savingProfile, setSavingProfile] = useState(false);

    const [clientDocuments, setClientDocuments] = useState(client?.documents || []);
    const [clientContracts, setClientContracts] = useState(client?.contracts || []);

    // Document Upload State
    const [showDocumentModal, setShowDocumentModal] = useState(false);
    const [documentForm, setDocumentForm] = useState({ title: '', type: '', expiryDate: '', fileName: '' });
    const [uploadingDoc, setUploadingDoc] = useState(false);
    const [savingDoc, setSavingDoc] = useState(false);

    const handleProfileSave = async () => {
        setSavingProfile(true);
        let logoFilename = profileForm.clientLogo;
        
        if (clientLogoFile) {
            const formData = new FormData();
            formData.append('document', clientLogoFile);
            try {
                const res = await fetch(`${API}/upload`, { method: 'POST', body: formData });
                const data = await res.json();
                if (res.ok && data.filename) {
                    logoFilename = data.filename;
                }
            } catch (err) {
                console.error('Logo upload error:', err);
            }
        }

        try {
            const updatedForm = { ...profileForm, clientLogo: logoFilename };
            const res = await fetch(`${API}/clients/${client._id}?isClientAction=profileUpdate`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatedForm)
            });
            if (res.ok) {
                const responseData = await res.json();
                if (responseData.client) {
                    const storedUser = JSON.parse(localStorage.getItem('ops-user') || '{}');
                    localStorage.setItem('ops-user', JSON.stringify({ ...storedUser, ...responseData.client }));
                }
                alert("Profile updated successfully!");
                window.location.reload();
            } else {
                alert("Failed to update profile.");
            }
        } catch (err) {
            console.error(err);
            alert("Network error updating profile.");
        } finally {
            setSavingProfile(false);
        }
    };

    const handleDocumentFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploadingDoc(true);
        const formData = new FormData();
        formData.append('document', file);

        try {
            const res = await fetch(`${API}/upload`, {
                method: 'POST',
                body: formData
            });
            const data = await res.json();
            if (res.ok) {
                setDocumentForm(prev => ({ ...prev, fileName: data.filename }));
            } else {
                alert("Document upload failed");
            }
        } catch (err) {
            console.error(err);
            alert("Network error during upload");
        } finally {
            setUploadingDoc(false);
        }
    };

    const saveDocument = async () => {
        if (!documentForm.title || !documentForm.fileName) {
            alert('Title and file are required');
            return;
        }

        setSavingDoc(true);
        try {
            const newDocs = [...clientDocuments, documentForm];
            const updatedClient = { ...client, documents: newDocs };
            
            const { _id, __v, createdAt, updatedAt, ...sanitized } = updatedClient;
            
            const res = await fetch(`${API}/clients/${client._id}?isClientAction=documentUpload`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(sanitized)
            });

            if (res.ok) {
                const responseData = await res.json();
                if (responseData.client) {
                    const storedUser = JSON.parse(localStorage.getItem('ops-user') || '{}');
                    localStorage.setItem('ops-user', JSON.stringify({ ...storedUser, ...responseData.client }));
                }
                alert("Document uploaded successfully!");
                window.location.reload();
            } else {
                alert("Failed to save document to profile.");
            }
        } catch (err) {
            console.error(err);
            alert("Network error saving document.");
        } finally {
            setSavingDoc(false);
        }
    };

    useEffect(() => {
        fetch(`${API}/countries`)
            .then(r => r.json())
            .then(setCountries)
            .catch(err => console.error("Error fetching countries:", err));

        fetch(`${API}/master-data`)
            .then(r => r.json())
            .then(setMasterData)
            .catch(err => console.error("Error fetching master data:", err));
    }, []);

    // Load client notifications
    const loadNotifications = useCallback(() => {
        if (!client?._id) return;
        fetch(`${API}/notifications/client/${client._id}`)
            .then(r => r.json())
            .then(data => setNotifications(Array.isArray(data) ? data : []))
            .catch(err => console.error('Error loading notifications:', err));
    }, [client]);

    useEffect(() => {
        loadNotifications();
        // Poll every 30 seconds for new notifications
        const interval = setInterval(loadNotifications, 30000);
        return () => clearInterval(interval);
    }, [loadNotifications]);

    const loadStaffData = useCallback(async () => {
        if (!client?._id) return;
        setLoadingPassengers(true);
        try {
            // Fetch both contacts and passengers in parallel
            const [pRes, clientRes] = await Promise.all([
                fetch(`${API}/passengers/${client.operatorId}`),
                fetch(`${API}/operators/${client.operatorId}/clients/${client._id}`)
            ]);

            const pData = await pRes.json();
            const myPassengers = (Array.isArray(pData) ? pData : []).filter(p => (p.clientId?._id || p.clientId) === client._id);

            let currentClient = client;
            if (clientRes.ok) {
                const refreshedClient = await clientRes.json();
                if (refreshedClient) {
                    currentClient = refreshedClient;
                    setClientDocuments(refreshedClient.documents || []);
                    setClientContracts(refreshedClient.contracts || []);
                }
            }

            // Map passengers to include source and standardized fields
            const finalPassengers = myPassengers.map(p => {
                let fN = p.firstName;
                let lN = p.lastName;
                if (!fN && p.name) {
                    const parts = p.name.split(' ');
                    fN = parts[0];
                    lN = parts.slice(1).join(' ');
                }
                return {
                    ...p,
                    _id: String(p._id),
                    firstName: fN || '',
                    lastName: lN || '',
                    primaryPhoneNumber: p.contactNumber || p.primaryPhoneNumber,
                    homeLocation: p.pickupLocation || p.homeLocation,
                    workLocation: p.dropLocation || p.workLocation,
                    source: 'db'
                };
            });

            setPassengers(finalPassengers);
        } catch (err) {
            console.error("Staff load error:", err);
        } finally {
            setLoadingPassengers(false);
        }
    }, [client]);

    const loadTrips = useCallback(() => {
        if (!client?.operatorId) return;
        setLoadingTrips(true);
        fetch(`${API}/trips/${client.operatorId}`)
            .then(r => r.json())
            .then(data => {
                setTrips((Array.isArray(data) ? data : []).filter(t => (t.clientId?._id || t.clientId) === client._id));
            })
            .catch(err => console.error("Error loading trips:", err))
            .finally(() => setLoadingTrips(false));
    }, [client]);

    const loadAllocations = useCallback(() => {
        if (!client?.operatorId) return;
        setLoadingAllocations(true);
        fetch(`${API}/passenger-allocations/${client.operatorId}`)
            .then(r => r.json())
            .then(data => {
                setAllocations(Array.isArray(data) ? data : []);
            })
            .catch(err => console.error("Error loading allocations:", err))
            .finally(() => setLoadingAllocations(false));
    }, [client]);

    useEffect(() => {
        if (!client?.operatorId) return;

        loadTrips();
        loadAllocations();

        // Load invoices
        fetch(`${API}/invoices/client/${client._id}`)
            .then(r => r.json())
            .then(data => {
                setInvoices(Array.isArray(data) ? data : []);
            })
            .catch(err => console.error("Error loading invoices:", err))
            .finally(() => setLoadingInvoices(false));

        // Load client users
        fetch(`${API}/client-users/${client._id}`)
            .then(r => r.json())
            .then(data => {
                setClientUsers(Array.isArray(data) ? data : []);
            })
            .catch(err => console.error("Error loading client users:", err))
            .finally(() => setLoadingUsers(false));

        // Load quotations
        fetch(`${API}/quotations/client/${client._id}`)
            .then(r => r.json())
            .then(data => {
                setQuotations(Array.isArray(data) ? data : []);
            })
            .catch(err => console.error("Error loading quotations:", err))
            .finally(() => setLoadingQuotations(false));

        // Load routes
        fetch(`${API}/routes/${client.operatorId}`)
            .then(r => r.json())
            .then(data => {
                const allRoutes = Array.isArray(data) ? data : [];
                setRoutes(allRoutes.filter(route => (route.clientId?._id || route.clientId) === client._id));
            })
            .catch(err => console.error("Error loading routes:", err))
            .finally(() => setLoadingRoutes(false));

        // Load vehicles
        fetch(`${API}/vehicles/${client.operatorId}`)
            .then(r => r.json())
            .then(data => {
                setVehicles(Array.isArray(data) ? data : []);
            })
            .catch(err => console.error("Error loading vehicles:", err))
            .finally(() => setLoadingVehicles(false));

        loadStaffData();
    }, [client, loadStaffData, loadTrips]);

    const loadUsers = useCallback(() => {
        setLoadingUsers(true);
        fetch(`${API}/client-users/${client._id}`)
            .then(r => r.json())
            .then(data => setClientUsers(Array.isArray(data) ? data : []))
            .catch(err => console.error("Error loading users:", err))
            .finally(() => setLoadingUsers(false));
    }, [client]);

    const handleSaveAllocation = async (allocs, tripId) => {
        try {
            // Reconcile deletions
            const existingOnTrip = allocations.filter(a => String(a.tripId?._id || a.tripId) === String(tripId));
            const keptIds = allocs.filter(a => a._id).map(a => String(a._id));
            const toDelete = existingOnTrip.filter(a => !keptIds.includes(String(a._id)));

            if (toDelete.length > 0) {
                await Promise.all(toDelete.map(a =>
                    fetch(`${API}/passenger-allocations/${a._id}?isClientAction=true&clientId=${client._id}`, { method: 'DELETE' })
                ));
            }

            // Create/Update
            await Promise.all(allocs.map(a => {
                const payload = {
                    ...a,
                    operatorId: client.operatorId,
                    clientId: client._id,
                    isClientAction: true
                };
                const sanitizeId = (id) => {
                    if (!id) return null;
                    if (typeof id === 'object') return id._id || null;
                    return id === '' ? null : id;
                };
                payload.passengerId = sanitizeId(payload.passengerId);
                payload.tripId = sanitizeId(payload.tripId);
                payload.pickupStopId = sanitizeId(payload.pickupStopId);
                payload.dropStopId = sanitizeId(payload.dropStopId);
                payload.operatorId = sanitizeId(payload.operatorId);

                if (a._id) {
                    return fetch(`${API}/passenger-allocations/${a._id}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload)
                    });
                }
                return fetch(`${API}/passenger-allocations`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
            }));

            setEditingTrip(null);
            loadTrips();
            loadAllocations();
        } catch (err) {
            console.error('Error saving allocation:', err);
            alert('Failed to save allocation changes.');
        }
    };

    const scheduledCount = trips.filter(t => t.status === 'Scheduled' || t.status === 'Requested').length;
    const ongoingCount = trips.filter(t => t.status === 'Ongoing' || t.status === 'In Progress').length;
    const completedCount = trips.filter(t => t.status === 'Completed').length;
    const pendingInvoices = invoices.filter(i => i.status !== 'Paid');
    const totalPendingInvoiced = pendingInvoices.reduce((sum, i) => sum + (Number(i.totalAmount) || 0), 0);
    const totalInvoiced = invoices.reduce((sum, i) => sum + (Number(i.totalAmount) || 0), 0);

    const tripCards = [
        { label: 'Scheduled', count: scheduledCount, icon: icons.trips, gradient: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', status: 'Scheduled' },
        { label: 'Ongoing', count: ongoingCount, icon: icons.analytics, gradient: 'linear-gradient(135deg, #b45309 0%, #f59e0b 100%)', status: 'Ongoing' },
        { label: 'Completed', count: completedCount, icon: icons.check, gradient: 'linear-gradient(135deg, #047857 0%, #10b981 100%)', status: 'Completed' },
    ];

    const statCards = [
        { 
            label: 'Pending Invoices', 
            value: loadingInvoices ? '…' : (
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                    <span>{pendingInvoices.length}</span>
                    <span style={{ fontSize: '0.45em', color: '#94a3b8', fontWeight: 700 }}>({formatCurrency(totalPendingInvoiced, operatorDetails?.country)})</span>
                </div>
            ), 
            icon: icons.invoice, color: '#d97706', bg: 'rgba(217, 119, 6, 0.08)', shadow: 'rgba(217, 119, 6, 0.2)' 
        },
        { label: 'Total Invoiced', value: loadingInvoices ? '…' : formatCurrency(totalInvoiced, operatorDetails?.country), icon: icons.briefcase, color: '#0f172a', bg: 'rgba(15, 23, 42, 0.08)', shadow: 'rgba(15, 23, 42, 0.2)' },
        { label: 'Total Passengers', value: loadingPassengers ? '…' : passengers.length, icon: icons.user, color: 'var(--cd-primary)', bg: 'rgba(59, 130, 246, 0.08)', shadow: 'rgba(59, 130, 246, 0.2)' },
    ];

    return (
        <div className={`cd-shell ${sidebarOpen ? '' : 'sidebar-closed'}`} data-theme="grey" onClick={() => { if(showNotifPanel) setShowNotifPanel(false); }}>
            {sidebarOpen && (
            <aside className="cd-sidebar">
                <div className="cd-sidebar-logo" style={{ justifyContent: 'center', padding: '24px 16px' }}>
                    <div style={{ width: '100%', maxWidth: '180px', height: '56px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        {client?.clientLogo ? (
                            <img
                                src={`/uploads/${client.clientLogo}`}
                                alt={client?.companyName}
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            />
                        ) : operatorDetails?.companyLogo ? (
                            <img
                                src={`/uploads/${operatorDetails.companyLogo}`}
                                alt={operatorDetails?.companyName}
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            />
                        ) : (
                            <div style={{ background: 'linear-gradient(135deg, #0f172a, #334155)', width: 40, height: 40, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Icon d={icons.building} size={20} color="white" />
                            </div>
                        )}
                    </div>
                </div>
                <nav className="cd-sidebar-nav">
                    {hasPerm('analytics') && (
                        <div className={`cd-nav-item ${activeTab === 'analytics' ? "active" : ""}`} onClick={() => setActiveTab('analytics')}>
                            <Icon d={icons.analytics} size={18} color="#666666" /><span>Analytics</span>
                        </div>
                    )}
                    {hasPerm('profile') && (
                        <div className={`cd-nav-item ${activeTab === 'profile' ? "active" : ""}`} onClick={() => setActiveTab('profile')}>
                            <Icon d={icons.user} size={18} color="#666666" /><span>My Profile</span>
                        </div>
                    )}
                    {hasPerm('passengers') && (
                        <div className={`cd-nav-item ${activeTab === 'staff' ? "active" : ""}`} onClick={() => setActiveTab('staff')}>
                            <Icon d={icons.staff} size={18} color="#666666" /><span>Passengers</span>
                        </div>
                    )}
                    {hasPerm('routes') && (
                        <div className={`cd-nav-item ${activeTab === 'routes' ? "active" : ""}`} onClick={() => setActiveTab('routes')}>
                            <Icon d={icons.map} size={18} color="#666666" /><span>Available Routes</span>
                        </div>
                    )}
                    {hasPerm('request') && (
                        <div className={`cd-nav-item ${activeTab === 'request_trip' ? "active" : ""}`} onClick={() => setActiveTab('request_trip')}>
                            <Icon d={icons.plus} size={18} color="#666666" /><span>Request Service</span>
                        </div>
                    )}
                    {hasPerm('trips') && (
                        <div className={`cd-nav-item ${activeTab === 'trips' ? "active" : ""}`} onClick={() => setActiveTab('trips')}>
                            <Icon d={icons.trips} size={18} color="#666666" /><span>Trips</span>
                        </div>
                    )}
                    {hasPerm('support') && (
                        <div className={`cd-nav-item ${activeTab === 'support' ? "active" : ""}`} onClick={() => setActiveTab('support')}>
                            <Icon d={icons.tickets} size={18} color="#666666" /><span>Support Tickets</span>
                        </div>
                    )}
                    {hasPerm('finance') && (
                        <div className="cd-nav-group">
                            <div className={`cd-nav-item ${(activeTab === 'finance' || activeTab === 'quotations' || activeTab === 'contracts') && !financeMenuOpen ? "active" : ""}`} onClick={() => setFinanceMenuOpen(!financeMenuOpen)}>
                                <Icon d={icons.invoice} size={18} color="#666666" />
                                <span style={{ flex: 1 }}>Finance</span>
                                <Icon d={financeMenuOpen ? icons.chevronUp : icons.chevronDown} size={14} color="#999999" />
                            </div>
                            {financeMenuOpen && (
                                <div className="cd-submenu">
                                    <div className={`cd-submenu-item ${activeTab === 'finance' ? "active" : ""}`} onClick={() => setActiveTab('finance')}>
                                        <Icon d={icons.invoice} size={14} color={activeTab === 'finance' ? '#0f172a' : '#999999'} />
                                        <span>Invoices</span>
                                    </div>
                                    <div className={`cd-submenu-item ${activeTab === 'quotations' ? "active" : ""}`} onClick={() => setActiveTab('quotations')}>
                                        <Icon d={icons.invoice} size={14} color={activeTab === 'quotations' ? '#0f172a' : '#999999'} />
                                        <span>Quotations</span>
                                    </div>
                                    <div className={`cd-submenu-item ${activeTab === 'contracts' ? "active" : ""}`} onClick={() => setActiveTab('contracts')}>
                                        <Icon d={icons.briefcase} size={14} color={activeTab === 'contracts' ? '#0f172a' : '#999999'} />
                                        <span>Contracts</span>
                                    </div>

                                </div>
                            )}
                        </div>
                    )}

                    {hasPerm('reports') && (
                        <div className="cd-nav-group">
                            <div className={`cd-nav-item ${(activeTab === 'reports_trips' || activeTab === 'reports_finance') && !reportsMenuOpen ? "active" : ""}`} onClick={() => setReportsMenuOpen(!reportsMenuOpen)}>
                                <Icon d={icons.fileText} size={18} color="#666666" />
                                <span style={{ flex: 1 }}>Reports</span>
                                <Icon d={reportsMenuOpen ? icons.chevronUp : icons.chevronDown} size={14} color="#999999" />
                            </div>
                            {reportsMenuOpen && (
                                <div className="cd-submenu">
                                    <div className={`cd-submenu-item ${activeTab === 'reports_trips' ? "active" : ""}`} onClick={() => setActiveTab('reports_trips')}>
                                        <Icon d={icons.trips} size={14} color={activeTab === 'reports_trips' ? '#0f172a' : '#999999'} />
                                        <span>Trip Intensity</span>
                                    </div>
                                    <div className={`cd-submenu-item ${activeTab === 'reports_finance' ? "active" : ""}`} onClick={() => setActiveTab('reports_finance')}>
                                        <Icon d={icons.invoice} size={14} color={activeTab === 'reports_finance' ? '#0f172a' : '#999999'} />
                                        <span>Financial Logs</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    
                    {hasPerm('users') && (
                        <div className={`cd-nav-item ${activeTab === 'users' ? "active" : ""}`} onClick={() => setActiveTab('users')}>
                            <Icon d={icons.users} size={18} color="#666666" /><span>Users</span>
                        </div>
                    )}
                </nav>
                <div className="cd-sidebar-bottom">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div className="cd-avatar" style={{ width: 36, height: 36, borderRadius: 10, background: '#1e293b', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700 }}>
                            {(client?.companyName || 'C').charAt(0).toUpperCase()}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ color: '#0f172a', fontSize: '14px', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {client?.companyName}
                            </div>
                            <div style={{ color: '#64748b', fontSize: '11px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                Managed by {operatorDetails?.companyName || operatorDetails?.operatorName || 'Operator'}
                            </div>
                        </div>
                        <button id="client-logout-btn" onClick={onLogout} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 4 }} title="Sign Out">
                            <Icon d={icons.logout} size={18} />
                        </button>
                    </div>
                </div>
            </aside>
            )}

            <div className="cd-content-area">
                {/* ── Topbar / Header ── */}
                <div style={{ padding: '16px 28px', background: 'white', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <div onClick={() => setSidebarOpen(!sidebarOpen)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M4 6h16M4 12h16M4 18h16"></path>
                            </svg>
                        </div>
                        <div style={{ fontSize: 16, fontWeight: 800, color: '#1e3a8a', textTransform: 'capitalize', letterSpacing: '0.01em' }}>
                            {activeTab === 'analytics' ? 'Dashboard Overview' :
                            activeTab === 'profile' ? 'My Profile' :
                                activeTab === 'staff' ? 'Passenger Management' :
                                    activeTab === 'routes' ? 'Available Routes' :
                                        activeTab === 'trips' ? 'Scheduled & Requested Trips' :
                                            activeTab === 'support' ? 'Support Tickets' :
                                                activeTab === 'allocations' ? 'Passenger Distribution & Allocation' :
                                                activeTab === 'users' ? 'Access & User Management' :
                                                    activeTab === 'finance' ? 'Financial Reports & Invoices' :
                                                        activeTab === 'quotations' ? 'Commercial Quotations & Proposals' :
                                                        activeTab === 'contracts' ? 'Agreements & Contracts' :
                                                    activeTab.startsWith('reports') ? 'Business Insight & Operations Reports' :
                                                        'Request New Trip'}
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                        {/* Awaiting Action Indicator */}
                        {trips.filter(t => t.status === 'PriceProposed').length > 0 && (
                            <div style={{ position: 'relative' }}>
                                <style>{`
                                    @keyframes pulse-action-red {
                                        0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4); }
                                        70% { box-shadow: 0 0 0 8px rgba(239, 68, 68, 0); }
                                        100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
                                    }
                                `}</style>
                                <button
                                    onClick={() => {
                                        setViewingHistoryTripId(null);
                                        setTripStatusFilter('PriceProposed');
                                        setActiveTab('trips');
                                    }}
                                    style={{
                                        background: '#fef2f2', border: '1px solid #ef4444', color: '#991b1b',
                                        padding: '7px 14px', borderRadius: 10, fontSize: 13, fontWeight: 800,
                                        display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                                        animation: 'pulse-action-red 2s infinite'
                                    }}
                                >
                                    <Icon d={icons.alertCircle} size={16} />
                                    Awaiting Action ({trips.filter(t => t.status === 'PriceProposed').length})
                                </button>
                            </div>
                        )}
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#64748b' }}>
                            {new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })} &middot; {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </div>
                        <div style={{ width: 1, height: 20, background: '#e2e8f0' }}></div>
                        <div onClick={() => setShowNotifPanel(p => !p)} style={{ position: 'relative', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"></path>
                            </svg>
                            {notifications.filter(n => !n.isRead).length > 0 && (
                                <div style={{ position: 'absolute', top: -4, right: -4, background: '#ef4444', color: 'white', fontSize: 10, fontWeight: 800, width: 18, height: 18, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid white' }}>
                                    {notifications.filter(n => !n.isRead).length > 9 ? '9+' : notifications.filter(n => !n.isRead).length}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <main className="cd-main">
                    {viewingHistoryTripId ? (
                        <TripHistory tripId={viewingHistoryTripId} user={client} onBack={() => setViewingHistoryTripId(null)} />
                    ) : activeTab === 'analytics' ? (
                        <>
                            {/* ── Deep Navy Hero Banner ── */}
                            <style>{`
                                @keyframes cd-pulse-ring { 0%{transform:scale(1);opacity:0.7} 70%{transform:scale(1.7);opacity:0} 100%{transform:scale(1);opacity:0} }
                                @keyframes cd-fade-up { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
                                .cd-hero-btn-primary { background: rgba(255,255,255,0.18); color: white; border: 1px solid rgba(255,255,255,0.35); padding: 12px 26px; border-radius: 12px; font-size: 14px; font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: 8px; transition: all 0.2s; font-family: 'Outfit', sans-serif; backdrop-filter: blur(8px); }
                                .cd-hero-btn-primary:hover { background: rgba(255,255,255,0.28); transform: translateY(-2px); box-shadow: 0 8px 20px rgba(0,0,0,0.15); }
                                .cd-hero-btn-secondary { background: #2563eb; color: white; border: 1px solid #3b82f6; padding: 12px 26px; border-radius: 12px; font-size: 14px; font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: 8px; transition: all 0.2s; font-family: 'Outfit', sans-serif; }
                                .cd-hero-btn-secondary:hover { background: #1d4ed8; transform: translateY(-2px); box-shadow: 0 8px 20px rgba(37,99,235,0.4); }
                                .cd-stat-mini-card { background: white; border-radius: 14px; border: 1px solid #e2e8f0; padding: 18px 20px; cursor: pointer; transition: all 0.2s ease; box-shadow: 0 4px 12px rgba(0,0,0,0.05); position: relative; overflow: hidden; animation: cd-fade-up 0.4s ease both; }
                                .cd-stat-mini-card:hover { transform: translateY(-3px); box-shadow: 0 10px 24px rgba(0,0,0,0.08) !important; }
                            `}</style>
                            <div style={{
                                background: 'linear-gradient(135deg, #0b1e3b 0%, #112d59 52%, #183d73 100%)',
                                borderRadius: 22,
                                padding: '36px 44px',
                                marginBottom: 28,
                                position: 'relative',
                                overflow: 'hidden',
                                border: '1px solid rgba(255,255,255,0.08)',
                                boxShadow: '0 20px 40px -8px rgba(11,30,59,0.45)'
                            }}>
                                {/* Luminous accent glows */}
                                <div style={{ position: 'absolute', top: -80, right: -40, width: 360, height: 360, borderRadius: '50%', background: 'radial-gradient(circle, rgba(59,130,246,0.22) 0%, transparent 65%)', pointerEvents: 'none' }} />
                                <div style={{ position: 'absolute', bottom: -100, left: 40, width: 280, height: 280, borderRadius: '50%', background: 'radial-gradient(circle, rgba(14,165,233,0.14) 0%, transparent 65%)', pointerEvents: 'none' }} />
                                <div style={{ position: 'absolute', top: 20, left: '45%', width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.12) 0%, transparent 65%)', pointerEvents: 'none' }} />

                                <div style={{ position: 'relative', zIndex: 2, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 24 }}>
                                    {/* Left: Text */}
                                    <div style={{ flex: 1 }}>
                                        <div style={{
                                            display: 'inline-flex', alignItems: 'center', gap: 7,
                                            padding: '4px 14px', borderRadius: 9999,
                                            background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)',
                                            color: '#93c5fd', fontSize: 11, fontWeight: 800,
                                            letterSpacing: '0.07em', textTransform: 'uppercase',
                                            border: '1px solid rgba(255,255,255,0.18)', marginBottom: 14
                                        }}>
                                            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#60a5fa' }} />
                                            Operational Hub
                                        </div>
                                        <h1 style={{ fontSize: 34, fontWeight: 800, color: '#ffffff', margin: '0 0 6px 0', letterSpacing: '-0.03em', lineHeight: 1.15, textShadow: '0 2px 8px rgba(0,0,0,0.25)', fontFamily: "'Outfit', sans-serif" }}>
                                            Welcome back, <span style={{ color: '#60a5fa' }}>{client?.companyName}</span>
                                        </h1>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                                            <div style={{ width: 18, height: 18, borderRadius: 5, background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <Icon d={icons.building} size={11} color="#93c5fd" />
                                            </div>
                                            <span style={{ color: '#94a3b8', fontSize: 13.5, fontWeight: 500 }}>Operated by <span style={{ color: '#cbd5e1', fontWeight: 600 }}>{operatorDetails?.companyName || operatorDetails?.operatorName || 'System Operator'}</span></span>
                                        </div>
                                        <p style={{ color: '#cbd5e1', fontSize: 14.5, margin: '0 0 26px 0', maxWidth: '580px', lineHeight: 1.65, fontWeight: 400 }}>
                                            Everything looks good! You have <strong style={{ color: '#ffffff', fontWeight: 700 }}>{scheduledCount} trips</strong> scheduled and <strong style={{ color: '#ffffff', fontWeight: 700 }}>{ongoingCount} ongoing</strong> missions for today.
                                        </p>
                                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                                            <button className="cd-hero-btn-secondary" onClick={() => setActiveTab('request_trip')}>
                                                <Icon d={icons.plus} size={16} /> Request New Trip
                                            </button>
                                            <button className="cd-hero-btn-primary" onClick={() => { setTripStatusFilter('Ongoing'); setActiveTab('trips'); }}>
                                                Track Live Status
                                            </button>
                                        </div>
                                    </div>

                                    {/* Right: System Status Card */}
                                    <div style={{
                                        background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(14px)',
                                        WebkitBackdropFilter: 'blur(14px)',
                                        border: '1px solid rgba(255,255,255,0.15)', borderRadius: 18,
                                        padding: '22px 26px', minWidth: 230, boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, paddingBottom: 14, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                                            <div>
                                                <div style={{ fontSize: 10.5, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 }}>Last Login</div>
                                                <div style={{ fontSize: 15, fontWeight: 700, color: '#ffffff' }}>Today, {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                                            </div>
                                            <div style={{ width: 38, height: 38, borderRadius: 11, background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.15)' }}>
                                                <Icon d={icons.user} size={17} color="#93c5fd" />
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                            <div style={{ position: 'relative', width: 10, height: 10, flexShrink: 0 }}>
                                                <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#10b981', position: 'absolute' }} />
                                                <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#10b981', position: 'absolute', animation: 'cd-pulse-ring 1.8s ease infinite' }} />
                                            </div>
                                            <span style={{ fontSize: 13, color: '#cbd5e1', fontWeight: 500 }}>Systems: <span style={{ color: '#6ee7b7', fontWeight: 700 }}>Online & Secure</span></span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 20, marginBottom: 28 }}>
                                {tripCards.map(c => {
                                    const accentColor = c.status === 'Scheduled' ? '#3b82f6' : c.status === 'Ongoing' ? '#f59e0b' : '#10b981';
                                    const softBg = c.status === 'Scheduled' ? '#eff6ff' : c.status === 'Ongoing' ? '#fffbeb' : '#ecfdf5';
                                    return (
                                        <div
                                            key={c.label}
                                            className="cd-stat-mini-card"
                                            onClick={() => {
                                                setTripStatusFilter(c.status);
                                                setActiveTab('trips');
                                            }}
                                            style={{ borderTop: `4px solid ${accentColor}`, cursor: 'pointer', minHeight: 108 }}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                                                    <div style={{ background: softBg, width: 30, height: 30, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <Icon d={c.icon} size={15} color={accentColor} />
                                                    </div>
                                                    <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{c.label} Trips</div>
                                                </div>
                                                <div style={{ color: '#cbd5e1', cursor: 'pointer' }}>
                                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="5" r="1"></circle><circle cx="12" cy="12" r="1"></circle><circle cx="12" cy="19" r="1"></circle></svg>
                                                </div>
                                            </div>
                                            <div style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', lineHeight: 1 }}>{loadingTrips ? '...' : c.count}</div>
                                        </div>
                                    );
                                })}
                                {statCards.map(c => (
                                    <div key={c.label} className="cd-stat-mini-card"
                                        style={{ borderTop: `4px solid ${c.color}`, minHeight: 108 }}
                                    onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.02)'; }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                <div style={{ background: c.bg, width: 28, height: 28, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <Icon d={c.icon} size={14} color={c.color} />
                                                </div>
                                                <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{c.label}</div>
                                            </div>
                                            <div style={{ color: '#94a3b8', cursor: 'pointer' }}>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="5" r="1"></circle><circle cx="12" cy="12" r="1"></circle><circle cx="12" cy="19" r="1"></circle></svg>
                                            </div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: 22, fontWeight: 800, color: '#113255', letterSpacing: '-0.02em' }}>
                                                {typeof c.value === 'string' && c.value.includes('KWD') ? (
                                                    `${c.value.split('.')[0].replace('KWD ', '')}${c.value.includes('.') ? '.' + c.value.split('.')[1] : ''} KWD`
                                                ) : c.value}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24, marginBottom: 32 }}>
                                {/* Trip Volume (Area & Line Chart) */}
                                <div style={{ background: 'white', padding: '24px', borderRadius: 20, border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)', display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ marginBottom: 24 }}>
                                        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', margin: '0 0 4px 0' }}>Trip Volume</h3>
                                        <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>Number of trips over the last 7 days</p>
                                    </div>
                                    <div style={{ flex: 1, position: 'relative', height: 280, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                                        {(() => {
                                            const tripData = [...Array(7)].map((_, i) => { 
                                                const d = new Date(); d.setDate(d.getDate() - (6 - i)); 
                                                return { 
                                                    dateStr: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), 
                                                    count: trips.filter(t => t.tripDate && new Date(t.tripDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) === d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })).length 
                                                }; 
                                            });
                                            const maxVal = Math.max(...tripData.map(d => d.count)) || 1;
                                            
                                            // Map values to 0-100 percentages for perfectly responsive absolute positioning
                                            const pointsArray = tripData.map((d, idx) => {
                                                const xPct = (idx / 6) * 100; // 7 days = 6 intervals
                                                const yPct = 100 - ((d.count / maxVal) * 100);
                                                return { xPct, yPct, dateStr: d.dateStr, count: d.count };
                                            });
                                            
                                            const pointsStr = pointsArray.map(p => `${p.xPct},${p.yPct}`).join(' ');
                                            const areaStr = `${pointsStr} 100,100 0,100`;

                                            return (
                                                <div style={{ position: 'relative', width: '100%', height: '100%', padding: '20px 20px 40px' }}>
                                                    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                                                        {/* Area Fill Gradient Definition */}
                                                        <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible' }}>
                                                            <defs>
                                                                <linearGradient id="volGradient" x1="0" y1="0" x2="0" y2="1">
                                                                    <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.2"/>
                                                                    <stop offset="100%" stopColor="#3b82f6" stopOpacity="0"/>
                                                                </linearGradient>
                                                            </defs>
                                                            <polygon points={areaStr} fill="url(#volGradient)" />
                                                            <polyline points={pointsStr} fill="none" stroke="#3b82f6" strokeWidth="3" vectorEffect="non-scaling-stroke" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                        
                                                        {/* HTML Data Points (Stays Perfectly Round) */}
                                                        {pointsArray.map((p, i) => (
                                                            <div key={i} style={{ position: 'absolute', left: `${p.xPct}%`, top: `${p.yPct}%`, width: 14, height: 14, background: 'white', border: '3px solid #3b82f6', borderRadius: '50%', transform: 'translate(-50%, -50%)', zIndex: 10, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}></div>
                                                        ))}

                                                        {/* Perfectly Aligned Labels */}
                                                        {pointsArray.map((p, i) => (
                                                            <div key={`lbl-${i}`} style={{ position: 'absolute', bottom: -35, left: `${p.xPct}%`, fontSize: 12, fontWeight: 600, color: '#64748b', transform: 'translateX(-50%)' }}>
                                                                {p.dateStr}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>
                                
                                {/* Trip Status Distribution (Donut Chart) */}
                                <div style={{ background: 'white', padding: '24px', borderRadius: 20, border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)', display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ marginBottom: 20 }}>
                                        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', margin: '0 0 4px 0' }}>Trip Status Distribution</h3>
                                        <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>Current statuses</p>
                                    </div>
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                                        {(() => {
                                            const cData = [
                                                { label: 'Scheduled', count: scheduledCount, color: '#3b82f6' },
                                                { label: 'Ongoing', count: ongoingCount, color: '#f59e0b' },
                                                { label: 'Completed', count: completedCount, color: '#10b981' }
                                            ].filter(d => d.count > 0);
                                            
                                            // Fallback for empty state
                                            const displayData = cData.length > 0 ? cData : [{ label: 'Empty', count: 1, color: '#e2e8f0' }];
                                            
                                            const total = displayData.reduce((s, d) => s + d.count, 0) || 1;
                                            let currentPct = 0;
                                            return (
                                                <>
                                                    <svg viewBox="-1.2 -1.2 2.4 2.4" style={{ width: 180, height: 180, transform: 'rotate(-90deg)' }}>
                                                        {displayData.map((d, i) => {
                                                            const pct = d.count / total;
                                                            const [sx, sy] = [Math.cos(2*Math.PI*currentPct), Math.sin(2*Math.PI*currentPct)];
                                                            currentPct += pct;
                                                            const [ex, ey] = [Math.cos(2*Math.PI*currentPct), Math.sin(2*Math.PI*currentPct)];
                                                            const largeArc = pct > 0.5 ? 1 : 0;
                                                            
                                                            if (pct === 1) return <circle key={i} cx="0" cy="0" r="1" fill="none" stroke={d.color} strokeWidth="0.35" />;
                                                            
                                                            return <path key={i} d={`M ${sx} ${sy} A 1 1 0 ${largeArc} 1 ${ex} ${ey}`} fill="none" stroke={d.color} strokeWidth="0.35" strokeLinecap="round" />;
                                                        })}
                                                    </svg>
                                                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                                                        <div style={{ fontSize: 32, fontWeight: 900, color: '#0f172a' }}>{cData.length === 0 ? 0 : total}</div>
                                                        <div style={{ fontSize: 12, color: '#64748b', fontWeight: 700, letterSpacing: '0.05em' }}>TOTAL</div>
                                                    </div>
                                                </>
                                            );
                                        })()}
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap', marginTop: 24 }}>
                                        {[
                                            { label: 'Scheduled', color: '#3b82f6' },
                                            { label: 'Ongoing', color: '#f59e0b' },
                                            { label: 'Completed', color: '#10b981' }
                                        ].map(l => (
                                            <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <div style={{ width: 10, height: 10, borderRadius: '50%', background: l.color }}></div>
                                                <span style={{ fontSize: 13, color: '#475569', fontWeight: 500 }}>{l.label}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="cd-two-col" style={{ gridTemplateColumns: '1fr' }}>
                                <div className="cd-card"><div className="cd-card-header"><Icon d={icons.trips} size={18} color="#00B2A9" /><span className="cd-card-title">Recent Trips</span></div>
                                    {loadingTrips ? <div className="cd-empty">Loading...</div> : trips.length === 0 ? <div className="cd-empty">No trips.</div> : <div className="cd-trip-list">{trips.slice(0, 5).map(t => (
                                        <div key={t._id} className="cd-trip-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #f1f5f9' }}>
                                            <div>
                                                <div className="cd-trip-name" style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>
                                                    {t.passengerName || t.tripName || t.routeId?.name || `${(t.pickupLocation || '').split(',')[0]} → ${(t.dropLocation || '').split(',')[0]}`}
                                                </div>
                                                <div className="cd-trip-date" style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                                                    {t.tripFrequency === 'Recurring' 
                                                        ? `${t.recurringDays?.map(d => d.slice(0,3).toUpperCase()).join(', ')} (${t.startDate ? new Date(t.startDate).toLocaleDateString() : '—'} to ${t.endDate ? new Date(t.endDate).toLocaleDateString() : '—'})`
                                                        : t.tripDate ? new Date(t.tripDate).toLocaleDateString() : '—'}
                                                </div>
                                            </div>
                                            <span className="cd-badge" style={{
                                                display: 'inline-block', padding: '4px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: 800, textTransform: 'uppercase',
                                                background: t.status === 'Scheduled' ? '#dcfce7' : t.status === 'Requested' ? '#fef3c7' : t.status === 'PriceProposed' ? '#e0f0ff' : t.status === 'Completed' ? '#dbeafe' : '#fee2e2',
                                                color: t.status === 'Scheduled' ? '#166534' : t.status === 'Requested' ? '#92400e' : t.status === 'PriceProposed' ? '#5b21b6' : t.status === 'Completed' ? '#1e40af' : '#991b1b'
                                            }}>
                                                {t.status === 'ClientAccepted' ? 'Accepted' : t.status === 'ClientRejected' ? 'Rejected' : t.status}
                                            </span>
                                        </div>
                                    ))}</div>}
                                </div>
                            </div>
                        </>
                    ) : activeTab === 'profile' ? (
                        <div style={{ animation: 'fadeIn 0.3s ease-out' }}>
                            <div style={{ marginBottom: 32 }}>
                                <h1 style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', margin: '0 0 8px 0' }}>My Profile</h1>
                                <p style={{ color: '#64748b', fontSize: 15, margin: 0 }}>View your company details and operational documents.</p>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 32 }}>
                                <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 24, padding: '32px', boxShadow: '0 4px 6px rgba(0,0,0,0.02)' }}>
                                    <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', margin: '0 0 24px 0', display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <div style={{ background: '#e6f0fa', padding: 8, borderRadius: 8, display: 'flex', alignItems: 'center' }}>
                                            <Icon d={icons.building} size={16} color="#16539C" />
                                        </div>
                                        Company Details
                                        {isEditingProfile ? (
                                            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                                                <button onClick={() => setIsEditingProfile(false)} disabled={savingProfile} style={{ background: '#f1f5f9', color: '#475569', border: 'none', padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
                                                <button onClick={handleProfileSave} disabled={savingProfile} style={{ background: 'var(--cd-primary)', color: 'white', border: 'none', padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: savingProfile ? 'not-allowed' : 'pointer' }}>{savingProfile ? 'Saving...' : 'Save Changes'}</button>
                                            </div>
                                        ) : (
                                            <button onClick={() => setIsEditingProfile(true)} style={{ marginLeft: 'auto', background: '#f8fafc', border: '1px solid #e2e8f0', color: '#475569', padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <Icon d={icons.edit} size={12} /> Edit
                                            </button>
                                        )}
                                    </h2>
                                    
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                                        <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 16 }}>
                                            <div style={{ width: 64, height: 64, borderRadius: 12, background: '#f1f5f9', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                                {(clientLogoFile ? URL.createObjectURL(clientLogoFile) : (client?.clientLogo ? `/uploads/${client.clientLogo}` : null)) ? (
                                                    <img src={clientLogoFile ? URL.createObjectURL(clientLogoFile) : `/uploads/${client.clientLogo}`} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                ) : (
                                                    <Icon d={icons.building} size={24} color="#94a3b8" />
                                                )}
                                            </div>
                                            {isEditingProfile && (
                                                <div>
                                                    <label style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 4 }}>Company Logo</label>
                                                    <input type="file" accept="image/*" onChange={e => setClientLogoFile(e.target.files[0])} style={{ fontSize: 13, color: '#475569' }} />
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <label style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Company Name</label>
                                            {isEditingProfile ? (
                                                <input className="cd-modal-input" style={{ marginTop: 4 }} value={profileForm.companyName} onChange={e => setProfileForm({...profileForm, companyName: e.target.value})} />
                                            ) : (
                                                <div style={{ fontSize: 15, fontWeight: 600, color: '#1e293b', marginTop: 4 }}>{client?.companyName || '-'}</div>
                                            )}
                                        </div>
                                        <div>
                                            <label style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Contact Person</label>
                                            {isEditingProfile ? (
                                                <input className="cd-modal-input" style={{ marginTop: 4 }} value={profileForm.contactPerson} onChange={e => setProfileForm({...profileForm, contactPerson: e.target.value})} />
                                            ) : (
                                                <div style={{ fontSize: 15, fontWeight: 600, color: '#1e293b', marginTop: 4 }}>{client?.contactPerson || '-'}</div>
                                            )}
                                        </div>
                                        <div>
                                            <label style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Email Address <span style={{fontSize: 10, color: '#cbd5e1'}}>(Read-only)</span></label>
                                            <div style={{ fontSize: 15, fontWeight: 600, color: '#64748b', marginTop: 4, background: isEditingProfile ? '#f8fafc' : 'transparent', padding: isEditingProfile ? '10px 14px' : 0, borderRadius: 10, border: isEditingProfile ? '1px solid #e2e8f0' : 'none' }}>{client?.email || '-'}</div>
                                        </div>
                                        <div>
                                            <label style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Phone Number</label>
                                            {isEditingProfile ? (
                                                <input className="cd-modal-input" style={{ marginTop: 4 }} value={profileForm.phone} onChange={e => setProfileForm({...profileForm, phone: e.target.value})} />
                                            ) : (
                                                <div style={{ fontSize: 15, fontWeight: 600, color: '#1e293b', marginTop: 4 }}>{client?.phone || '-'}</div>
                                            )}
                                        </div>
                                        <div>
                                            <label style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Industry</label>
                                            {isEditingProfile ? (
                                                <input className="cd-modal-input" style={{ marginTop: 4 }} value={profileForm.industry} onChange={e => setProfileForm({...profileForm, industry: e.target.value})} />
                                            ) : (
                                                <div style={{ fontSize: 15, fontWeight: 600, color: '#1e293b', marginTop: 4 }}>{client?.industry || '-'}</div>
                                            )}
                                        </div>
                                        <div>
                                            <label style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Work Location</label>
                                            {isEditingProfile ? (
                                                <input className="cd-modal-input" style={{ marginTop: 4 }} value={profileForm.workLocation} onChange={e => setProfileForm({...profileForm, workLocation: e.target.value})} />
                                            ) : (
                                                <div style={{ fontSize: 15, fontWeight: 600, color: '#1e293b', marginTop: 4 }}>{client?.workLocation || '-'}</div>
                                            )}
                                        </div>
                                        <div style={{ gridColumn: '1 / -1' }}>
                                            <label style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Address</label>
                                            {isEditingProfile ? (
                                                <textarea className="cd-modal-input" style={{ marginTop: 4, minHeight: 80, resize: 'vertical' }} value={profileForm.address} onChange={e => setProfileForm({...profileForm, address: e.target.value})} />
                                            ) : (
                                                <div style={{ fontSize: 15, fontWeight: 600, color: '#1e293b', marginTop: 4 }}>{client?.address || '-'}</div>
                                            )}
                                        </div>
                                        <div>
                                            <label style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Operational Status</label>
                                            <div style={{ marginTop: 4 }}>
                                                <span style={{ padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', background: (client?.status || 'Active') === 'Active' ? '#dcfce7' : '#fee2e2', color: (client?.status || 'Active') === 'Active' ? '#166534' : '#991b1b' }}>
                                                    {client?.status || 'Active'}
                                                </span>
                                            </div>
                                        </div>
                                        {isEditingProfile && (
                                            <div>
                                                <label style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Access Password</label>
                                                <div style={{ position: 'relative', marginTop: 4 }}>
                                                    <input 
                                                        type="password" 
                                                        className="cd-modal-input" 
                                                        value={profileForm.password} 
                                                        onChange={e => setProfileForm({...profileForm, password: e.target.value})} 
                                                        placeholder="Enter new password to change..."
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 24, padding: '32px', boxShadow: '0 4px 6px rgba(0,0,0,0.02)' }}>
                                    <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', margin: '0 0 24px 0', display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <div style={{ background: '#fef3c7', padding: 8, borderRadius: 8, display: 'flex', alignItems: 'center' }}>
                                            <Icon d={icons.fileText} size={16} color="#d97706" />
                                        </div>
                                        Company Documents
                                        <button onClick={() => setShowDocumentModal(true)} style={{ marginLeft: 'auto', background: '#f8fafc', border: '1px solid #e2e8f0', color: '#475569', padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <Icon d={icons.upload} size={12} /> Upload
                                        </button>
                                    </h2>
                                    {clientDocuments && clientDocuments.length > 0 ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                            {clientDocuments.map((doc, i) => (
                                                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 16, background: '#f8fafc', borderRadius: 12, border: '1px solid #f1f5f9' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                        <div style={{ background: '#e2e8f0', width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            <Icon d={icons.fileText} size={16} color="#475569" />
                                                        </div>
                                                        <div>
                                                            <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{doc.title || doc.type || 'Document'}</div>
                                                            {doc.expiryDate && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Expires: {new Date(doc.expiryDate).toLocaleDateString()}</div>}
                                                        </div>
                                                    </div>
                                                    <a href={`${API}/uploads/${doc.fileName}`} target="_blank" rel="noopener noreferrer" style={{ background: 'white', border: '1px solid #cbd5e1', padding: '6px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#0f172a', textDecoration: 'none', transition: 'all 0.2s' }}>View</a>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div style={{ textAlign: 'center', padding: '40px 20px', background: '#f8fafc', borderRadius: 16, border: '1px dashed #cbd5e1' }}>
                                            <Icon d={icons.fileText} size={32} color="#94a3b8" />
                                            <p style={{ color: '#64748b', fontSize: 14, marginTop: 12, marginBottom: 0 }}>No documents uploaded.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            {showDocumentModal && (
                                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 6000 }}>
                                    <div style={{ background: 'white', borderRadius: 24, width: '100%', maxWidth: 500, padding: 32, boxShadow: '0 20px 40px -10px rgba(0,0,0,0.1)' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                                            <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#1e293b' }}>Upload Document</h3>
                                            <button onClick={() => setShowDocumentModal(false)} style={{ border: 'none', background: '#f1f5f9', width: 32, height: 32, borderRadius: 10, cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon d={icons.x} size={16} /></button>
                                        </div>
                                        
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                                            <div>
                                                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 8 }}>Document Title</label>
                                                <input className="cd-modal-input" value={documentForm.title} onChange={e => setDocumentForm({...documentForm, title: e.target.value})} placeholder="e.g. Service Agreement" />
                                            </div>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                                <div>
                                                    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 8 }}>Document Type</label>
                                                    <select className="cd-modal-input" value={documentForm.type} onChange={e => setDocumentForm({...documentForm, type: e.target.value})}>
                                                        <option value="">Select Type</option>
                                                        {masterData.filter(i => i.category === 'document-types').map(t => (
                                                            <option key={t._id || t.name} value={t.name}>{t.name}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 8 }}>Expiry Date</label>
                                                    <input type="date" className="cd-modal-input" value={documentForm.expiryDate} onChange={e => setDocumentForm({...documentForm, expiryDate: e.target.value})} />
                                                </div>
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 8 }}>File Attachment *</label>
                                                {documentForm.fileName ? (
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 12, background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                            <Icon d={icons.fileText} size={16} color="#16539C" />
                                                            <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{documentForm.fileName}</span>
                                                        </div>
                                                        <button onClick={() => setDocumentForm({...documentForm, fileName: ''})} style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Remove</button>
                                                    </div>
                                                ) : (
                                                    <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px 16px', background: '#f8fafc', borderRadius: 12, border: '2px dashed #cbd5e1', cursor: 'pointer', transition: '0.2s' }} onMouseOver={e => e.currentTarget.style.borderColor = '#94a3b8'} onMouseOut={e => e.currentTarget.style.borderColor = '#cbd5e1'}>
                                                        <input type="file" style={{ display: 'none' }} onChange={handleDocumentFileChange} disabled={uploadingDoc} />
                                                        {uploadingDoc ? (
                                                            <span style={{ color: '#16539C', fontWeight: 600, fontSize: 13 }}>Uploading...</span>
                                                        ) : (
                                                            <>
                                                                <Icon d={icons.upload} size={20} color="#94a3b8" />
                                                                <span style={{ color: '#64748b', fontWeight: 600, fontSize: 13, marginTop: 8 }}>Click to select a file</span>
                                                            </>
                                                        )}
                                                    </label>
                                                )}
                                            </div>
                                        </div>
                                        
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 32 }}>
                                            <button onClick={() => setShowDocumentModal(false)} style={{ padding: '10px 20px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: 'white', fontWeight: 700, color: '#64748b', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                                            <button onClick={saveDocument} disabled={savingDoc || uploadingDoc || !documentForm.fileName || !documentForm.title} style={{ padding: '10px 20px', borderRadius: 10, border: 'none', background: 'var(--cd-primary)', color: 'white', fontWeight: 700, cursor: (savingDoc || uploadingDoc || !documentForm.fileName || !documentForm.title) ? 'not-allowed' : 'pointer', fontSize: 13, opacity: (savingDoc || uploadingDoc || !documentForm.fileName || !documentForm.title) ? 0.6 : 1 }}>
                                                {savingDoc ? 'Saving...' : 'Upload Document'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : activeTab === 'staff' ? (
                        <PassengersSection client={client} passengers={passengers} loading={loadingPassengers} onRefresh={loadStaffData} countries={countries} operatorDetails={operatorDetails} />
                    ) : activeTab === 'routes' ? (
                        <RoutesSection routes={routes} loading={loadingRoutes} />
                    ) : activeTab === 'trips' ? (
                        <TripsSection 
                            trips={trips} 
                            allocations={allocations} 
                            loading={loadingTrips || loadingAllocations} 
                            client={client} 
                            onRefresh={() => { loadTrips(); loadAllocations(); }} 
                            initialFilter={tripStatusFilter}
                            onManageAllocations={setEditingTrip}
                            onNavigate={onNavigate}
                            onViewHistory={setViewingHistoryTripId}
                            operatorDetails={operatorDetails}
                        />
                    ) : activeTab === 'users' ? (
                        <UsersSection client={client} users={clientUsers} loading={loadingUsers} onRefresh={loadUsers} />
                    ) : activeTab === 'request_trip' ? (
                        <TripRequestSection client={client} routes={routes} vehicles={vehicles} passengers={passengers} onComplete={() => { loadTrips(); loadAllocations(); setTripStatusFilter('Requested'); setActiveTab('trips'); }} />
                    ) : activeTab === 'finance' ? (
                        viewDoc ? <DocumentView type={viewDoc.type} data={viewDoc.data} client={client} country={operatorDetails?.country} onClose={() => setViewDoc(null)} /> :
                        <FinanceSection invoices={invoices} trips={trips} routes={routes} client={client} country={operatorDetails?.country} loading={loadingInvoices} onSetView={setViewDoc} />
                    ) : activeTab === 'quotations' ? (
                        viewDoc ? <DocumentView type={viewDoc.type} data={viewDoc.data} client={client} country={operatorDetails?.country} onClose={() => setViewDoc(null)} /> :
                        <QuotationsSection quotations={quotations} client={client} country={operatorDetails?.country} loading={loadingQuotations} onSetView={setViewDoc} />

                    ) : activeTab === 'contracts' ? (
                        <div style={{ animation: 'fadeIn 0.3s ease-out' }}>
                            <div style={{ marginBottom: 32 }}>
                                <h1 style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', margin: '0 0 8px 0' }}>Agreements & Contracts</h1>
                                <p style={{ color: '#64748b', fontSize: 15, margin: 0 }}>Review and download your active service agreements and contracts.</p>
                            </div>
                            <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 24, padding: '32px', boxShadow: '0 4px 6px rgba(0,0,0,0.02)' }}>
                                {clientContracts && clientContracts.length > 0 ? (
                                    <div className="cd-table-container">
                                        <table className="cd-table">
                                            <thead>
                                                <tr>
                                                    <th>Contract Details</th>
                                                    <th>Validity Period</th>
                                                    <th>Value</th>
                                                    <th>Status</th>
                                                    <th style={{ textAlign: 'right' }}>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {clientContracts.map((doc, i) => (
                                                    <tr key={i}>
                                                        <td>
                                                            <div className="table-name-cell">
                                                                <div style={{ background: '#f1f5f9', width: 40, height: 40, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                    <Icon d={icons.briefcase} size={18} color="#475569" />
                                                                </div>
                                                                <div>
                                                                    <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>{doc.title}</div>
                                                                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{doc.fileName}</div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div style={{ fontWeight: 600, color: '#334155' }}>{doc.startDate ? new Date(doc.startDate).toLocaleDateString() : 'N/A'} - {doc.endDate ? new Date(doc.endDate).toLocaleDateString() : 'N/A'}</div>
                                                        </td>
                                                        <td>
                                                            <div style={{ fontWeight: 700, color: '#10b981' }}>{doc.value ? formatCurrency(doc.value, operatorDetails?.country) : 'N/A'}</div>
                                                        </td>
                                                        <td>
                                                            <span style={{ padding: '4px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: doc.status === 'Active' ? '#dcfce7' : '#f1f5f9', color: doc.status === 'Active' ? '#166534' : '#475569' }}>
                                                                {doc.status || 'Active'}
                                                            </span>
                                                        </td>
                                                        <td style={{ textAlign: 'right' }}>
                                                            <a href={`${API}/uploads/${doc.fileName}`} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'white', border: '1px solid #cbd5e1', padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, color: '#0f172a', textDecoration: 'none', transition: 'all 0.2s' }} onMouseOver={(e) => e.currentTarget.style.borderColor = '#1e293b'} onMouseOut={(e) => e.currentTarget.style.borderColor = '#cbd5e1'}>
                                                                <Icon d={icons.download} size={14} /> Download
                                                            </a>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div style={{ textAlign: 'center', padding: '60px 20px', background: '#f8fafc', borderRadius: 16, border: '2px dashed #cbd5e1' }}>
                                        <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                                            <Icon d={icons.briefcase} size={28} color="#64748b" />
                                        </div>
                                        <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', margin: '0 0 8px' }}>No Active Contracts</h3>
                                        <p style={{ color: '#64748b', fontSize: 14, margin: 0 }}>There are currently no contracts or agreements available for your review.</p>
                                    </div>
                                )}
                            </div>
                        </div>

                    ) : activeTab === 'support' ? (
                        <SupportTickets context="client" userDetails={client} trips={trips} />
                    ) : activeTab === 'reports_trips' ? (
                        <ReportsSection type="trips" data={trips} country={operatorDetails?.country} />
                    ) : activeTab === 'reports_finance' ? (
                        <ReportsSection type="finance" data={invoices} country={operatorDetails?.country} />
                    ) : null}

                    {editingTrip && (
                        <ClientAllocationForm 
                            trip={editingTrip} 
                            allocations={allocations} 
                            masterData={masterData} 
                            passengers={passengers} 
                            client={client}
                            onClose={() => setEditingTrip(null)}
                            onSave={handleSaveAllocation}
                        />
                    )}
                </main>
                {showNotifPanel && (
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            position: 'fixed', top: 0, right: 0, height: '100vh', width: 360,
                            background: '#ffffff', boxShadow: '-4px 0 32px rgba(0,0,0,0.13)',
                            borderLeft: '1px solid #e5e7eb', zIndex: 99999,
                            display: 'flex', flexDirection: 'column', fontFamily: 'inherit'
                        }}
                    >
                        {/* Panel Header */}
                        <div style={{
                            padding: '24px 20px',
                            background: '#fff',
                            borderBottom: '1px solid #f1f5f9'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <div style={{ fontWeight: 800, fontSize: 18, color: '#0f172a', letterSpacing: '-0.02em' }}>Notifications</div>
                                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 4, fontWeight: 500 }}>
                                        {notifications.filter(n => !n.isRead).length > 0
                                            ? `You have ${notifications.filter(n => !n.isRead).length} unread updates`
                                            : 'All caught up!'}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                    {notifications.filter(n => !n.isRead).length > 0 && (
                                        <button
                                            onClick={async () => {
                                                await fetch(`${API}/notifications/mark-all-read/client/${client._id}`, { method: 'PUT' });
                                                setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
                                            }}
                                            style={{
                                                fontSize: 11, color: '#16539C', background: '#f0f7ff',
                                                border: 'none', borderRadius: 8,
                                                cursor: 'pointer', fontWeight: 700, padding: '6px 12px'
                                            }}
                                        >
                                            Mark all read
                                        </button>
                                    )}
                                    <button
                                        onClick={() => setShowNotifPanel(false)}
                                        style={{
                                            background: '#f8fafc', border: '1px solid #e2e8f0',
                                            borderRadius: 8, width: 32, height: 32,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <Icon d={icons.x} size={14} color="#64748b" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Notification List */}
                        <div style={{ flex: 1, overflowY: 'auto', background: '#fcfcfc' }}>
                            {notifications.length === 0 ? (
                                <div style={{ padding: 60, textAlign: 'center' }}>
                                    <div style={{ background: '#f8fafc', width: 64, height: 64, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                                        <Icon d={icons.bell} size={24} color="#cbd5e1" />
                                    </div>
                                    <div style={{ fontWeight: 800, fontSize: 15, color: '#1e293b' }}>No notifications yet</div>
                                    <div style={{ marginTop: 4, fontSize: 13, color: '#94a3b8' }}>Check back later for updates.</div>
                                </div>
                            ) : notifications.map((n) => {
                                const typeData = (
                                    n.type === 'trip_request_accepted' ? { color: '#10b981', icon: icons.check } :
                                    n.type === 'trip_scheduled' ? { color: '#00B2A9', icon: icons.trips } :
                                    n.type === 'trip_request_rejected' ? { color: '#ef4444', icon: icons.x } :
                                    n.type === 'trip_status_update' ? { color: '#8b5cf6', icon: icons.fileText } :
                                    { color: '#64748b', icon: icons.bell }
                                );
                                return (
                                    <div
                                        key={n._id}
                                        onClick={async () => {
                                            if (!n.isRead) {
                                                try {
                                                    await fetch(`${API}/notifications/${n._id}/read`, { method: 'PUT' });
                                                    setNotifications(prev => prev.map(notif => notif._id === n._id ? { ...notif, isRead: true } : notif));
                                                } catch (err) { console.error(err); }
                                            }
                                            setShowNotifPanel(false);
                                            setActiveTab('trips');
                                        }}
                                        style={{
                                            padding: '16px 20px', borderBottom: '1px solid #f1f5f9',
                                            background: n.isRead ? 'transparent' : '#f0f7ff',
                                            display: 'flex', gap: 16, alignItems: 'flex-start',
                                            cursor: 'pointer', transition: 'background 0.2s'
                                        }}
                                    >
                                        <div style={{
                                            width: 36, height: 36, borderRadius: 10,
                                            background: '#fff',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                            border: `1.5px solid ${typeData.color}20`,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            flexShrink: 0
                                        }}>
                                            <Icon d={typeData.icon} size={14} color={typeData.color} />
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                                                <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', lineHeight: 1.4 }}>{n.title}</div>
                                                {!n.isRead && <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--cd-primary)', flexShrink: 0, marginTop: 6 }} />}
                                            </div>
                                            <div style={{ fontSize: 12, color: '#64748b', marginTop: 4, lineHeight: 1.5 }}>{n.message}</div>
                                            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 8, fontWeight: 600 }}>{new Date(n.createdAt).toLocaleString()}</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function PassengerReportModal({ passengerId, onClose, passengers }) {
    const [report, setReport] = useState([]);
    const [loading, setLoading] = useState(true);

    const passenger = passengers?.find(p => p._id === passengerId) || {};
    const passengerName = passenger.name || passenger.firstName || 'Passenger';

    useEffect(() => {
        const fetchReport = async () => {
            try {
                const res = await fetch(`${API}/passenger-report/${passengerId}`);
                const data = await res.json();
                if (data.success) {
                    setReport(data.report || []);
                }
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        if (passengerId) fetchReport();
    }, [passengerId]);

    return (
        <div className="cd-modal-overlay" style={{ zIndex: 9999 }}>
            <div className="cd-modal" style={{ maxWidth: 700, padding: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '24px 32px', borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg, #0284c7, #2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                            <Icon d={icons.analytics} size={20} />
                        </div>
                        <div>
                            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>{passengerName}'s Attendance</h3>
                            <p style={{ margin: 0, fontSize: 13, color: '#64748b', fontWeight: 600 }}>Quick-glance date-wise trip report</p>
                        </div>
                    </div>
                    <button className="cd-icon-btn" onClick={onClose}><Icon d={icons.close} size={18} /></button>
                </div>
                
                <div style={{ padding: '32px', maxHeight: '60vh', overflowY: 'auto' }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '40px 0' }}>
                            <div style={{ width: 32, height: 32, border: '3px solid #f1f5f9', borderTopColor: '#0284c7', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
                            <div style={{ fontWeight: 600, color: '#64748b' }}>Loading report...</div>
                        </div>
                    ) : report.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px 0', background: '#f8fafc', borderRadius: 16, border: '1px dashed #cbd5e1' }}>
                            <Icon d={icons.analytics} size={48} color="#cbd5e1" strokeWidth={1} />
                            <div style={{ fontWeight: 700, color: '#475569', marginTop: 16 }}>No trip history found.</div>
                            <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 4 }}>This passenger hasn't been allocated to any trips yet.</div>
                        </div>
                    ) : (
                        <table className="cd-table" style={{ width: '100%' }}>
                            <thead>
                                <tr>
                                    <th className="cd-th" style={{ paddingLeft: 16 }}>Date</th>
                                    <th className="cd-th">Trip Name</th>
                                    <th className="cd-th">Type</th>
                                    <th className="cd-th">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {report.map((item, idx) => (
                                    <tr key={idx} className="cd-tr">
                                        <td className="cd-td" style={{ paddingLeft: 16, fontWeight: 700, color: '#1e293b' }}>
                                            {item.date ? new Date(item.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                                        </td>
                                        <td className="cd-td" style={{ fontWeight: 600, color: '#475569' }}>
                                            {item.tripName}
                                        </td>
                                        <td className="cd-td">
                                            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: '#f1f5f9', color: '#64748b', fontWeight: 700 }}>
                                                {item.frequency}
                                            </span>
                                        </td>
                                        <td className="cd-td">
                                            <span style={{ 
                                                fontSize: 12, padding: '4px 10px', borderRadius: 6, fontWeight: 700,
                                                background: item.status === 'Boarded' || item.status === 'Present' ? '#dcfce7' : item.status === 'Absent' ? '#fee2e2' : '#f1f5f9',
                                                color: item.status === 'Boarded' || item.status === 'Present' ? '#15803d' : item.status === 'Absent' ? '#b91c1c' : '#64748b'
                                            }}>
                                                {item.status || 'Pending'}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}

function PassengerImportWizard({ client, passengers, onBack, onDone, operatorDetails, countries }) {
    const [file, setFile] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const [parsedData, setParsedData] = useState([]);
    const [importing, setImporting] = useState(false);
    const [result, setResult] = useState(null);

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
        else if (e.type === "dragleave") setDragActive(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            processFile(e.dataTransfer.files[0]);
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            processFile(e.target.files[0]);
        }
    };

    const processFile = (selectedFile) => {
        setFile(selectedFile);
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const bstr = e.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws);

                if (data.length === 0) {
                    alert('The Excel file is empty.');
                    setFile(null);
                    return;
                }
                
                const standardized = data.map((row) => {
                    const mapped = {};
                    Object.keys(row).forEach(key => {
                        const cleanKey = key.trim();
                        mapped[cleanKey] = row[key];
                    });
                    return mapped;
                });

                setParsedData(standardized);
            } catch (err) {
                console.error(err);
                alert("Failed to parse Excel file.");
            }
        };
        reader.readAsBinaryString(selectedFile);
    };

    const downloadTemplate = () => {
        const templateData = [{
            'Name': 'John Doe',
            'Employee ID': 'EMP001',
            'Email': 'john@example.com',
            'Phone Code': '+1',
            'Phone Number': '9876543210',
            'Gender': 'Male',
            'Pickup Location': '123 Home St, City',
            'Drop Location': '456 Work Ave, City'
        }];
        const ws = XLSX.utils.json_to_sheet(templateData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Template");
        XLSX.writeFile(wb, "Passenger_Import_Template.xlsx");
    };

    const processImport = async () => {
        setImporting(true);
        try {
            const incomingIds = parsedData.map(r => r['Employee ID']?.toString().trim()).filter(id => id);
            const uniqueIncomingIds = new Set(incomingIds);
            if (incomingIds.length !== uniqueIncomingIds.size) {
                setResult({ success: false, message: 'Duplicate Employee IDs found within the Excel file. Please ensure all IDs are unique.' });
                setImporting(false);
                return;
            }

            const existingIds = new Set(passengers.map(p => p.employeeId).filter(id => id));
            const duplicateWithExisting = incomingIds.find(id => existingIds.has(id));
            if (duplicateWithExisting) {
                setResult({ success: false, message: `Employee ID ${duplicateWithExisting} already exists in the system. Please use unique IDs.` });
                setImporting(false);
                return;
            }

            const defaultPhoneCode = countries?.find(c => c.name === operatorDetails?.country)?.code || '+971';

            const payload = parsedData.map(row => ({
                name: row['Name'] || '',
                employeeId: row['Employee ID']?.toString().trim() || '',
                email: row['Email'] || '',
                primaryPhoneCode: row['Phone Code'] || defaultPhoneCode,
                primaryPhoneNumber: row['Phone Number']?.toString().trim() || '',
                contactNumber: row['Phone Number'] || '',
                gender: row['Gender'] || '',
                homeLocation: row['Pickup Location'] || '',
                workLocation: row['Drop Location'] || '',
                pickupLocation: row['Pickup Location'] || '',
                dropLocation: row['Drop Location'] || '',
                status: 'Active'
            }));

            const res = await fetch(`${API}/passengers/bulk`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ passengers: payload, operatorId: client.operatorId, clientId: client._id, isClientAction: true })
            });
            const data = await res.json();
            if (res.ok) {
                setResult({ success: true, message: data.message || `Successfully imported ${payload.length} passengers.` });
            } else {
                setResult({ success: false, message: data.message || 'Import failed.' });
            }
        } catch (err) {
            setResult({ success: false, message: 'Network error during import.' });
        } finally {
            setImporting(false);
        }
    };

    if (result) {
        return (
            <div style={{ animation: 'fadeUp 0.4s ease-out' }}>
                <div style={{ background: 'white', padding: '40px', borderRadius: 20, textAlign: 'center', border: '1px solid #e2e8f0' }}>
                    <div style={{ width: 64, height: 64, borderRadius: '50%', background: result.success ? '#dcfce7' : '#fee2e2', color: result.success ? '#166534' : '#991b1b', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', fontSize: 24 }}>
                        {result.success ? '✓' : '✕'}
                    </div>
                    <h2 style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', marginBottom: 12 }}>{result.success ? 'Import Complete' : 'Import Failed'}</h2>
                    <p style={{ color: '#64748b', marginBottom: 32 }}>{result.message}</p>
                    <button onClick={onDone} style={{ background: '#0f172a', color: 'white', border: 'none', padding: '12px 24px', borderRadius: 12, fontWeight: 700, cursor: 'pointer' }}>Return to Passengers</button>
                </div>
            </div>
        );
    }

    return (
        <div style={{ animation: 'fadeUp 0.4s ease-out' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>Import Passengers</h2>
                    <p style={{ margin: 0, fontSize: 13, color: '#64748b', marginTop: 4 }}>Upload an Excel file to bulk import passenger records.</p>
                </div>
                <button onClick={onBack} style={{ background: 'white', border: '1px solid #e2e8f0', padding: '8px 16px', borderRadius: 10, fontSize: 13, fontWeight: 700, color: '#475569', cursor: 'pointer' }}>Cancel</button>
            </div>

            <div style={{ background: 'white', padding: 24, borderRadius: 20, border: '1px solid #e2e8f0', marginBottom: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                    <div style={{ fontWeight: 700, color: '#1e293b' }}>1. Download Template</div>
                    <button onClick={downloadTemplate} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f8fafc', border: '1px solid #e2e8f0', padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 700, color: '#00B2A9', cursor: 'pointer' }}>
                        <Icon d={icons.download} size={14} /> Download Template
                    </button>
                </div>
                <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>Use our standardized Excel template to ensure all fields are mapped correctly during import. Do not modify the column headers.</p>
            </div>

            <div style={{ background: 'white', padding: 24, borderRadius: 20, border: '1px solid #e2e8f0' }}>
                <div style={{ fontWeight: 700, color: '#1e293b', marginBottom: 16 }}>2. Upload Data File</div>
                {!file ? (
                    <div onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop} style={{ border: `2px dashed ${dragActive ? '#00B2A9' : '#cbd5e1'}`, background: dragActive ? '#eef2ff' : '#f8fafc', borderRadius: 16, padding: '40px 20px', textAlign: 'center', transition: 'all 0.2s', cursor: 'pointer', position: 'relative' }}>
                        <input type="file" accept=".xlsx, .xls" onChange={handleFileChange} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }} />
                        <Icon d={icons.upload} size={32} color={dragActive ? '#00B2A9' : '#94a3b8'} style={{ marginBottom: 16 }} />
                        <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>Drag & drop your Excel file here</div>
                        <div style={{ fontSize: 13, color: '#64748b' }}>or click to browse from your computer</div>
                    </div>
                ) : (
                    <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 20 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <div style={{ width: 40, height: 40, borderRadius: 8, background: '#eef2ff', color: '#00B2A9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Icon d={icons.fileText} size={20} />
                                </div>
                                <div>
                                    <div style={{ fontWeight: 700, color: '#1e293b', fontSize: 14 }}>{file.name}</div>
                                    <div style={{ fontSize: 12, color: '#64748b' }}>{parsedData.length} records ready to import</div>
                                </div>
                            </div>
                            <button onClick={() => { setFile(null); setParsedData([]); }} style={{ background: 'transparent', border: 'none', color: '#ef4444', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Remove</button>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <button onClick={processImport} disabled={importing || parsedData.length === 0} style={{ background: '#0f172a', color: 'white', border: 'none', padding: '10px 24px', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: importing ? 'not-allowed' : 'pointer', opacity: importing ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                                {importing ? 'Importing...' : 'Confirm Import'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function PassengersSection({ client, passengers, loading, onRefresh, countries, operatorDetails }) {
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [modal, setModal] = useState(null);
    const [reportPassengerId, setReportPassengerId] = useState(null);
    const [showImport, setShowImport] = useState(false);

    const handleSave = async (formData) => {
        try {
            const isEdit = !!modal.data?._id;
            
            if (formData.employeeId) {
                const isDuplicate = passengers.some(p => p.employeeId === formData.employeeId && p._id !== modal.data?._id);
                if (isDuplicate) {
                    alert(`Employee ID ${formData.employeeId} already exists. Please use a unique ID.`);
                    return;
                }
            }

            // Sync with Passenger collection
            const passengerPayload = {
                ...formData,
                clientId: client._id,
                operatorId: client.operatorId,
                pickupLocation: formData.homeLocation, // Sync for trip ops
                dropLocation: formData.workLocation,
                contactNumber: formData.primaryPhoneNumber
            };

            let pRes;
            if (isEdit) {
                pRes = await fetch(`${API}/passengers/${modal.data._id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(passengerPayload) });
            } else {
                pRes = await fetch(`${API}/passengers`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...passengerPayload, isClientAction: true }) });
            }

            if (pRes.ok) { setModal(null); onRefresh(); } else alert("Error saving passenger.");
        } catch (err) { console.error(err); alert("Network error."); }
    };

    const filtered = passengers.filter(p => {
        const matchesSearch = (p.name || '').toLowerCase().includes(search.toLowerCase()) || (p.employeeId || '').toLowerCase().includes(search.toLowerCase());
        const matchesStatus = statusFilter === 'All' || (p.status || 'Active') === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const totalCount = passengers.length;
    const activeCount = passengers.filter(p => (p.status || 'Active') === 'Active').length;
    const inactiveCount = passengers.filter(p => p.status === 'Inactive').length;
    const unassignedCount = passengers.filter(p => !(p.homeLocation || p.pickupLocation) || !(p.workLocation || p.dropLocation)).length;

    const exportData = () => {
        const rows = filtered.map(p => ({
            'Name': p.name || '',
            'Employee ID': p.employeeId || '',
            'Email': p.email || '',
            'Phone Code': p.primaryPhoneCode || '',
            'Phone Number': p.primaryPhoneNumber || p.contactNumber || '',
            'Gender': p.gender || '',
            'Pickup Location': p.homeLocation || p.pickupLocation || '',
            'Drop Location': p.workLocation || p.dropLocation || '',
            'Status': p.status || 'Active'
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Passengers");
        XLSX.writeFile(wb, "Passenger_Roster.xlsx");
    };

    if (showImport) {
        return <PassengerImportWizard client={client} passengers={passengers} onBack={() => setShowImport(false)} onDone={() => { setShowImport(false); onRefresh(); }} operatorDetails={operatorDetails} countries={countries} />;
    }

    return (
        <div style={{ animation: 'fadeUp 0.4s ease-out' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28, background: 'white', padding: '20px 24px', borderRadius: 20, border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg, #00B2A9, #16539C)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 16px rgba(99, 102, 241, 0.2)' }}>
                        <Icon d={icons.staff} size={20} color="white" />
                    </div>
                    <div>
                        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>Staff & Passengers</h2>
                        <p style={{ margin: 0, fontSize: 13, color: '#64748b', fontWeight: 600 }}>Manage your organization's transit roster.</p>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <div className="cd-search-wrap" style={{ width: 260 }}>
                        <Icon d={icons.search} size={16} color="#94a3b8" />
                        <input className="cd-search-input" style={{ width: '100%' }} placeholder="Search name or ID..." value={search} onChange={e => setSearch(e.target.value)} />
                    </div>
                    <button className="cd-add-btn" style={{ background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0', padding: '0 16px', height: 42 }} onClick={exportData}>
                        <Icon d={icons.download} size={16} /> <span style={{ marginLeft: 6 }}>Export</span>
                    </button>
                    <button className="cd-add-btn" style={{ background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0', padding: '0 16px', height: 42 }} onClick={() => setShowImport(true)}>
                        <Icon d={icons.upload} size={16} /> <span style={{ marginLeft: 6 }}>Import</span>
                    </button>
                    <button className="cd-add-btn" style={{ padding: '0 20px', height: 42 }} onClick={() => setModal({ data: null })}>
                        <Icon d={icons.plus} size={16} color="white" />
                        <span>Register Passenger</span>
                    </button>
                </div>
            </div>

            {/* KPI Overview */}
            <div className="cd-stats-grid" style={{ marginTop: 0, marginBottom: 28 }}>
                <div className="cd-stat-card clickable" style={{ background: 'white', border: '1px solid #e2e8f0', borderLeft: '4px solid #16539C' }} onClick={() => setStatusFilter('All')}>
                    <div className="cd-stat-value" style={{ color: '#0f172a' }}>{totalCount}</div>
                    <div className="cd-stat-label" style={{ color: '#64748b' }}>Total Passengers</div>
                </div>
                <div className="cd-stat-card clickable" style={{ background: 'white', border: '1px solid #e2e8f0', borderLeft: '4px solid #16539C' }} onClick={() => setStatusFilter('Active')}>
                    <div className="cd-stat-value" style={{ color: '#0f172a' }}>{activeCount}</div>
                    <div className="cd-stat-label" style={{ color: '#64748b' }}>Active</div>
                </div>
                <div className="cd-stat-card clickable" style={{ background: 'white', border: '1px solid #e2e8f0', borderLeft: '4px solid #16539C' }} onClick={() => setStatusFilter('Inactive')}>
                    <div className="cd-stat-value" style={{ color: '#0f172a' }}>{inactiveCount}</div>
                    <div className="cd-stat-label" style={{ color: '#64748b' }}>Inactive</div>
                </div>
                <div className="cd-stat-card" style={{ background: 'white', border: '1px solid #e2e8f0', borderLeft: '4px solid #16539C', cursor: 'default' }}>
                    <div className="cd-stat-value" style={{ color: '#0f172a' }}>{unassignedCount}</div>
                    <div className="cd-stat-label" style={{ color: '#64748b' }}>Missing Pickup/Drop</div>
                </div>
            </div>

            <div className="cd-card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '16px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Status</span>
                    {['All', 'Active', 'Inactive'].map(s => (
                        <button key={s} onClick={() => setStatusFilter(s)} style={{
                            padding: '6px 14px', borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                            border: statusFilter === s ? 'none' : '1.5px solid #e2e8f0',
                            background: statusFilter === s ? '#0f172a' : 'white',
                            color: statusFilter === s ? 'white' : '#475569',
                            transition: 'all 0.2s'
                        }}>{s}</button>
                    ))}
                    <div style={{ marginLeft: 'auto', fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>{filtered.length} record{filtered.length !== 1 ? 's' : ''}</div>
                </div>
                {loading ? (
                    <div style={{ padding: 60, textAlign: 'center' }}>
                        <div style={{ width: 40, height: 40, border: '3px solid #f1f5f9', borderTopColor: '#00B2A9', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
                        <div style={{ fontWeight: 700, color: '#64748b' }}>Syncing Roster...</div>
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table className="cd-table">
                            <thead>
                                <tr>
                                    <th className="cd-th" style={{ paddingLeft: 24 }}>Identity</th>
                                    <th className="cd-th">System ID</th>
                                    <th className="cd-th">Communication</th>
                                    <th className="cd-th">Operational Points</th>
                                    <th className="cd-th" style={{ textAlign: 'right', paddingRight: 24 }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 ? (
                                    <tr><td colSpan="5" className="cd-empty">No matching records found.</td></tr>
                                ) : filtered.map(p => (
                                    <tr key={p._id || p.name} className="cd-tr">
                                        <td className="cd-td" style={{ paddingLeft: 24 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                {p.profileImage ? (
                                                    <img src={`${API}/uploads/${p.profileImage}`} style={{ width: 40, height: 40, borderRadius: 12, objectFit: 'cover', border: '2px solid #f1f5f9' }} alt="" />
                                                ) : (
                                                    <div style={{ width: 40, height: 40, borderRadius: 12, background: '#f8fafc', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#94a3b8', fontSize: 14 }}>
                                                        {(p.firstName || p.name || '?').charAt(0).toUpperCase()}
                                                    </div>
                                                )}
                                                <div>
                                                    <div style={{ fontWeight: 800, color: '#1e293b', fontSize: 14 }}>{p.firstName || ''} {p.lastName || ''} {!(p.firstName || p.lastName) && p.name}</div>
                                                    <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>{p.status || 'Active'}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="cd-td">
                                            <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#475569', background: '#f1f5f9', padding: '2px 6px', borderRadius: 4, fontSize: 12 }}>
                                                {p.employeeId || 'NO-ID'}
                                            </span>
                                        </td>
                                        <td className="cd-td">
                                            <div style={{ fontWeight: 600, color: '#334155' }}>{p.primaryPhoneNumber || p.contactNumber || '—'}</div>
                                            <div style={{ fontSize: 11, color: '#94a3b8' }}>{p.email || 'No Email'}</div>
                                        </td>
                                        <td className="cd-td">
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981' }} />
                                                    <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>{p.homeLocation || p.pickupLocation || 'Unset'}</span>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--cd-accent)' }} />
                                                    <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>{p.workLocation || p.dropLocation || 'Unset'}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="cd-td" style={{ textAlign: 'right', paddingRight: 24 }}>
                                            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                                <button className="cd-icon-btn" style={{ background: '#f0f9ff' }} onClick={() => setReportPassengerId(p._id)} title="View Attendance Report">
                                                    <Icon d={icons.analytics} size={14} color="#0284c7" />
                                                </button>
                                                <button className="cd-icon-btn" onClick={() => setModal({ data: p })} title="Edit Profile">
                                                    <Icon d={icons.edit} size={14} color="#00B2A9" />
                                                </button>
                                                <button className="cd-icon-btn" style={{ background: '#fff1f2' }} onClick={async () => { if (window.confirm(`Permanently remove ${p.name}?`)) { await fetch(`${API}/passengers/${p._id}`, { method: 'DELETE' }); onRefresh(); } }} title="Delete Record">
                                                    <Icon d={icons.trash} size={14} color="#e11d48" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            {modal && <PassengerForm data={modal.data} onClose={() => setModal(null)} onSave={handleSave} countries={countries} operatorDetails={operatorDetails} />}
            {reportPassengerId && <PassengerReportModal passengerId={reportPassengerId} onClose={() => setReportPassengerId(null)} passengers={passengers} />}
        </div>
    );
}

function PassengerForm({ data, onClose, onSave, countries, operatorDetails }) {
    const defaultPhoneCode = countries?.find(c => c.name === operatorDetails?.country)?.code || '+971';

    const [form, setForm] = useState(() => {
        if (data) {
            return {
                ...data,
                firstName: data.firstName || '',
                lastName: data.lastName || '',
                primaryPhoneNumber: data.primaryPhoneNumber || data.contactNumber || '',
                homeLocation: data.homeLocation || data.pickupLocation || '',
                workLocation: data.workLocation || data.dropLocation || '',
                primaryPhoneCode: data.primaryPhoneCode || defaultPhoneCode,
                gender: data.gender || ''
            };
        }
        return {
            firstName: '', lastName: '', name: '', employeeId: '', gender: '',
            primaryPhoneNumber: '', primaryPhoneCode: defaultPhoneCode,
            alternatePhoneCode: '', alternatePhoneNumber: '',
            email: '', homeLocation: '', workLocation: '',
            status: 'Active', profileImage: ''
        };
    });
    const [uploading, setUploading] = useState(false);
    const [errors, setErrors] = useState({});
    const [locationSuggestions, setLocationSuggestions] = useState([]);

    const fetchLocationSuggestions = async (query) => {
        if (!query || query.length < 3) return;
        try {
            const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&accept-language=en`);
            const data = await res.json();
            setLocationSuggestions(data.map(d => d.display_name));
        } catch { }
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        const formData = new FormData();
        formData.append('document', file);
        try {
            const res = await fetch(`${API}/upload`, { method: 'POST', body: formData });
            const result = await res.json();
            if (res.ok) setForm(f => ({ ...f, profileImage: result.filename }));
        } catch (err) { console.error(err); } finally { setUploading(false); }
    };

    const handleSaveLocal = () => {
        if (!form.primaryPhoneNumber?.trim()) {
            setErrors({ primaryPhoneNumber: true });
            alert("Please enter the primary mobile number.");
            return;
        }
        setErrors({});
        const fullForm = { ...form, name: `${form.firstName || ''} ${form.lastName || ''}`.trim() };
        onSave(fullForm);
    };

    return (
        <ModalOverlay onClose={onClose} title={data?._id ? 'Modify Passenger Profile' : 'New Passenger Registration'}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
                {/* Profile Header section */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 24, padding: '0 8px' }}>
                    <div style={{ position: 'relative' }}>
                        <div style={{ width: 100, height: 100, borderRadius: 28, background: '#f8fafc', border: '2px dashed #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', transition: 'all 0.2s' }}>
                            {form.profileImage ? (
                                <img src={`${API}/uploads/${form.profileImage}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="" />
                            ) : (
                                <Icon d={icons.user} size={32} color="#cbd5e1" />
                            )}
                        </div>
                        <label style={{ position: 'absolute', bottom: -10, right: -10, width: 36, height: 36, borderRadius: '50%', background: 'var(--cd-accent)', border: '4px solid white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 4px 10px rgba(99, 102, 241, 0.3)' }}>
                            <Icon d={icons.plus} size={14} color="white" />
                            <input type="file" onChange={handleFileChange} style={{ display: 'none' }} accept="image/*" />
                        </label>
                    </div>
                    <div>
                        <h4 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 900, color: '#0f172a' }}>{form.firstName || form.lastName ? `${form.firstName} ${form.lastName}` : 'Guest Profile'}</h4>
                        <p style={{ margin: 0, fontSize: 13, color: '#64748b', fontWeight: 600 }}>{uploading ? 'Uploading avatar...' : 'Upload a profile photo to help drivers identify the passenger.'}</p>
                    </div>
                </div>

                {/* Form Content */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                        <h5 style={{ margin: '0 0 4px', fontSize: 12, fontWeight: 800, color: '#00B2A9', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Personal Details</h5>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                            <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', fontSize: 12, fontWeight: 800, color: '#475569', marginBottom: 8 }}>First Name</label>
                                <input className="cd-modal-input" value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} placeholder="e.g. John" />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', fontSize: 12, fontWeight: 800, color: '#475569', marginBottom: 8 }}>Last Name</label>
                                <input className="cd-modal-input" value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} placeholder="e.g. Doe" />
                            </div>
                        </div>

                        <div>
                            <label style={{ display: 'block', fontSize: 12, fontWeight: 800, color: '#475569', marginBottom: 8 }}>Email Address</label>
                            <div className="cd-input-wrap">
                                <Icon d={icons.mail} size={16} color="#94a3b8" />
                                <input className="cd-modal-input-icon" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="john.doe@example.com" />
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                            <div>
                                <label style={{ display: 'block', fontSize: 12, fontWeight: 800, color: '#475569', marginBottom: 8 }}>Passenger ID</label>
                                <input className="cd-modal-input" value={form.employeeId} onChange={e => setForm({ ...form, employeeId: e.target.value })} placeholder="EMP-1234" />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: 12, fontWeight: 800, color: '#475569', marginBottom: 8 }}>Gender</label>
                                <select className="cd-modal-input" value={form.gender} onChange={e => setForm({ ...form, gender: e.target.value })}>
                                    <option value="">Select Gender</option>
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                        </div>

                        <div>
                            <label style={{ display: 'block', fontSize: 12, fontWeight: 800, color: '#475569', marginBottom: 8 }}>Operational Status</label>
                            <select className="cd-modal-input" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                                <option value="Active">Active</option>
                                <option value="Inactive">Inactive</option>
                            </select>
                        </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                        <h5 style={{ margin: '0 0 4px', fontSize: 12, fontWeight: 800, color: '#00B2A9', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Transit Logistics</h5>
                        
                        <div>
                            <label style={{ display: 'block', fontSize: 12, fontWeight: 800, color: '#475569', marginBottom: 8 }}>Primary Mobile Number</label>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <select className="cd-modal-input" style={{ width: 100 }} value={form.primaryPhoneCode} onChange={e => setForm({ ...form, primaryPhoneCode: e.target.value })}>
                                    {countries.map(c => <option key={c._id} value={c.code}>{c.code}</option>)}
                                </select>
                                <div className="cd-input-wrap" style={{ flex: 1, border: errors.primaryPhoneNumber ? '1.5px solid #ef4444' : undefined, borderRadius: errors.primaryPhoneNumber ? '10px' : undefined }}>
                                    <Icon d={icons.phone} size={16} color={errors.primaryPhoneNumber ? "#ef4444" : "#94a3b8"} />
                                    <input className="cd-modal-input-icon" value={form.primaryPhoneNumber} onChange={e => { setForm({ ...form, primaryPhoneNumber: e.target.value }); setErrors({ ...errors, primaryPhoneNumber: false }); }} placeholder="Phone number" />
                                </div>
                            </div>
                        </div>

                        <div>
                            <label style={{ display: 'block', fontSize: 12, fontWeight: 800, color: '#475569', marginBottom: 8 }}>Home Location (Pickup)</label>
                            <LocationAutocomplete 
                                value={form.homeLocation} 
                                onChange={val => setForm({ ...form, homeLocation: val })} 
                                placeholder="Search residential address..." 
                                icon={icons.map} 
                            />
                        </div>

                        <div>
                            <label style={{ display: 'block', fontSize: 12, fontWeight: 800, color: '#475569', marginBottom: 8 }}>Work Location (Dropoff)</label>
                            <LocationAutocomplete 
                                value={form.workLocation} 
                                onChange={val => setForm({ ...form, workLocation: val })} 
                                placeholder="Search office or branch address..." 
                                icon={icons.briefcase} 
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div style={{ display: 'flex', gap: 16, marginTop: 40, paddingTop: 24, borderTop: '1px solid #f1f5f9' }}>
                <button className="cd-modal-btn" style={{ flex: 1, background: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0' }} onClick={onClose}>Cancel Changes</button>
                <button className="cd-modal-btn" style={{ flex: 2, background: 'linear-gradient(135deg, #0f172a, #334155)', color: 'white', boxShadow: '0 10px 20px -5px rgba(15, 23, 42, 0.3)' }} onClick={handleSaveLocal} disabled={uploading}>
                    {uploading ? 'Processing...' : data?._id ? 'Sync Profile Changes' : 'Confirm & Register Passenger'}
                </button>
            </div>
        </ModalOverlay>
    );
}

function TripRequestSection({ client, routes, vehicles = [], onComplete, passengers = [] }) {
    const [reqType, setReqType] = useState('main'); // 'main' or 'adhoc'
    const [form, setForm] = useState({
        tripType: 'Login', tripFrequency: 'One Time',
        pickupLocation: '', dropLocation: '',
        tripDate: '', startDate: '', endDate: '', pickupTime: '', startTime: '', endTime: '',
        passengers: [{ passengerName: '', passengerPhone: '', passengerEmail: '' }],
        passengerName: '', passengerPhone: '',
        passengerEmail: '', routeId: '', recurringDays: [],
        requestedVehicleId: '', requestedSeatingCapacity: '', requestedVehicleModel: ''
    });
    const [routeSource, setRouteSource] = useState('pre'); // 'pre' or 'new'
    const [adhocDistance, setAdhocDistance] = useState(null);
    const [fetchingDistance, setFetchingDistance] = useState(false);

    useEffect(() => {
        if ((reqType === 'adhoc' || routeSource === 'new') && form.pickupLocation && form.dropLocation) {
            const fetchDist = async () => {
                setFetchingDistance(true);
                try {
                    // Quick geocode
                    const res1 = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(form.pickupLocation)}&limit=1&accept-language=en`);
                    const d1 = await res1.json();
                    const res2 = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(form.dropLocation)}&limit=1&accept-language=en`);
                    const d2 = await res2.json();
                    
                    if (d1[0] && d2[0]) {
                        const osrm = await fetch(`https://router.project-osrm.org/route/v1/driving/${d1[0].lon},${d1[0].lat};${d2[0].lon},${d2[0].lat}?overview=false`);
                        const osrmData = await osrm.json();
                        if (osrmData.routes && osrmData.routes[0]) {
                            setAdhocDistance((osrmData.routes[0].distance / 1000).toFixed(1));
                        }
                    }
                } catch (e) {
                    console.error("Distance fetch error", e);
                }
                setFetchingDistance(false);
            };
            const t = setTimeout(fetchDist, 1000);
            return () => clearTimeout(t);
        } else {
            setAdhocDistance(null);
        }
    }, [reqType, form.pickupLocation, form.dropLocation]);

    const [loading, setLoading] = useState(false);
    const [routeSearchTerm, setRouteSearchTerm] = useState('');
    const [showRouteDropdown, setShowRouteDropdown] = useState(false);
    const [locationSuggestions, setLocationSuggestions] = useState([]);
    const routeDropdownRef = useRef(null);

    const fetchLocationSuggestions = async (query) => {
        if (!query || query.length < 3) return;
        try {
            const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&accept-language=en`);
            const data = await res.json();
            setLocationSuggestions(data.map(d => d.display_name));
        } catch { }
    };

    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (routeDropdownRef.current && !routeDropdownRef.current.contains(event.target)) {
                setShowRouteDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const switchTab = (tab) => {
        setReqType(tab);
        setForm({
            tripType: 'Login', tripFrequency: 'One Time',
            pickupLocation: '', dropLocation: '',
            tripDate: '', startDate: '', endDate: '', pickupTime: '', startTime: '', endTime: '',
            passengers: [{ passengerName: '', passengerPhone: '', passengerEmail: '' }],
            passengerName: '', passengerPhone: '',
            passengerEmail: '', routeId: '', recurringDays: [],
            requestedVehicleId: '', requestedSeatingCapacity: '', requestedVehicleModel: ''
        });
        setRouteSource('pre');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const isAdhoc = reqType === 'adhoc';
            const payload = {
                ...form,
                passengerName: isAdhoc ? form.passengers.map(p => p.passengerName).join(', ') : form.passengerName,
                passengerPhone: isAdhoc ? form.passengers.map(p => p.passengerPhone).join(', ') : form.passengerPhone,
                passengerEmail: isAdhoc ? form.passengers.map(p => p.passengerEmail).filter(Boolean).join(', ') : form.passengerEmail,
                operatorId: client.operatorId,
                clientId: client._id,
                status: 'Requested',
                source: 'Client',
                tripType: isAdhoc ? 'on_demand' : form.tripType,
                billingType: isAdhoc ? 'Ad-hoc' : undefined,
                startTime: isAdhoc ? form.pickupTime : form.startTime,
                totalDistance: isAdhoc && adhocDistance ? Number(adhocDistance) : undefined
            };
            if (reqType === 'main' && routeSource === 'new') payload.routeId = null;

            const res = await fetch(`${API}/trips`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (res.ok) {
                alert("Trip request transmitted successfully!");
                onComplete();
            } else {
                alert("Transmission failed. Please verify all data points.");
            }
        } catch (err) {
            console.error(err); alert("Network connectivity issue.");
        } finally {
            setLoading(false);
        }
    };

    const toggleDay = (day) => {
        const days = form.recurringDays.includes(day) ? form.recurringDays.filter(d => d !== day) : [...form.recurringDays, day];
        setForm({ ...form, recurringDays: days });
    };

    const renderVehicleSelection = () => {
        return (
            <div style={{ marginTop: 24 }}>
                <h3 style={{ fontSize: 14, fontWeight: 800, color: '#1e293b', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ background: '#f1f5f9', width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon d={icons.trips} size={14} color="#64748b" />
                    </div>
                    Vehicle Preference
                </h3>
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 16, padding: 16 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 0 }}>
                        <div>
                            <label className="cd-req-label" style={{ fontSize: 12 }}>Seating Capacity *</label>
                            <select className="cd-modal-input" style={{ background: 'white', border: '1px solid #cbd5e1' }} value={form.requestedSeatingCapacity} onChange={e => setForm({ ...form, requestedSeatingCapacity: e.target.value })} required>
                                <option value="">Select Capacity</option>
                                {[2, 3, 4, 5, 6, 7, 8, 12, 15, 30, 50].map(cap => (
                                    <option key={cap} value={cap}>{cap} Seater</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="cd-req-label" style={{ fontSize: 12 }}>Car Model / Type</label>
                            <select className="cd-modal-input" style={{ background: 'white', border: '1px solid #cbd5e1' }} value={form.requestedVehicleModel} onChange={e => setForm({ ...form, requestedVehicleModel: e.target.value })}>
                                <option value="">Any Model</option>
                                <option value="Sedan">Sedan</option>
                                <option value="SUV">SUV</option>
                                <option value="Minivan">Minivan</option>
                                <option value="Bus">Bus</option>
                                <option value="Luxury">Luxury</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div style={{ maxWidth: 1000, margin: '0 auto', animation: 'fadeIn 0.4s ease' }}>
            <div style={{ textAlign: 'center', marginBottom: 32 }}>
                <h2 style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', margin: 0 }}>Select Request Type</h2>
                <p style={{ color: '#64748b', marginTop: 8, fontSize: 14 }}>Choose the type of service you need to schedule with your operations team.</p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 32 }}>
                {/* Regular Trip Card */}
                <div onClick={() => switchTab('main')} style={{
                    background: reqType === 'main' ? '#f0f9ff' : 'white',
                    border: reqType === 'main' ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                    borderRadius: 20, padding: 24, cursor: 'pointer', transition: 'all 0.2s',
                    boxShadow: reqType === 'main' ? '0 12px 24px rgba(59,130,246,0.1)' : '0 4px 6px rgba(0,0,0,0.02)'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ width: 48, height: 48, borderRadius: 12, background: reqType === 'main' ? '#3b82f6' : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Icon d={icons.trips} size={24} color={reqType === 'main' ? 'white' : '#64748b'} />
                        </div>
                        <div style={{ width: 24, height: 24, borderRadius: '50%', border: reqType === 'main' ? '6px solid #3b82f6' : '2px solid #cbd5e1', background: 'white' }}></div>
                    </div>
                    <h3 style={{ fontSize: 18, fontWeight: 800, color: reqType === 'main' ? '#1e3a8a' : '#1e293b', marginTop: 20, marginBottom: 8 }}>Schedule Regular Trip</h3>
                    <p style={{ fontSize: 13, color: reqType === 'main' ? '#1e40af' : '#64748b', margin: 0, lineHeight: 1.5 }}>Standard operational schedule. Ideal for recurring fixed routes, daily employee commutes, and pre-authorized logistics.</p>
                </div>

                {/* Special Trip Card */}
                <div onClick={() => switchTab('adhoc')} style={{
                    background: reqType === 'adhoc' ? '#fdf4ff' : 'white',
                    border: reqType === 'adhoc' ? '2px solid #d946ef' : '1px solid #e2e8f0',
                    borderRadius: 20, padding: 24, cursor: 'pointer', transition: 'all 0.2s',
                    boxShadow: reqType === 'adhoc' ? '0 12px 24px rgba(217,70,239,0.1)' : '0 4px 6px rgba(0,0,0,0.02)'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ width: 48, height: 48, borderRadius: 12, background: reqType === 'adhoc' ? '#d946ef' : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Icon d={icons.plus} size={24} color={reqType === 'adhoc' ? 'white' : '#64748b'} />
                        </div>
                        <div style={{ width: 24, height: 24, borderRadius: '50%', border: reqType === 'adhoc' ? '6px solid #d946ef' : '2px solid #cbd5e1', background: 'white' }}></div>
                    </div>
                    <h3 style={{ fontSize: 18, fontWeight: 800, color: reqType === 'adhoc' ? '#701a75' : '#1e293b', marginTop: 20, marginBottom: 8 }}>Request Adhoc Trip</h3>
                    <p style={{ fontSize: 13, color: reqType === 'adhoc' ? '#86198f' : '#64748b', margin: 0, lineHeight: 1.5 }}>On-demand, unscheduled transport. Ideal for immediate dispatch, VIP transfers, and one-off assignments.</p>
                </div>
            </div>

            <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 24, padding: 32, boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
                {reqType === 'main' ? (
                    <form onSubmit={handleSubmit}>
                        {/* Section 1: Logistics */}
                        <div style={{ marginBottom: 32 }}>
                            <h3 style={{ fontSize: 14, fontWeight: 800, color: '#1e293b', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{ background: '#f1f5f9', width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Icon d={icons.map} size={14} color="#64748b" />
                                </div>
                                Route & Logistics
                            </h3>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                                <div>
                                    <label className="cd-req-label">Trip Flow Direction <span style={{ color: '#ef4444' }}>*</span></label>
                                    <select className="cd-modal-input" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }} value={form.tripType} onChange={e => setForm({ ...form, tripType: e.target.value })}>
                                        <option value="Login">Login (Staff Pickup)</option>
                                        <option value="Logout">Logout (Staff Drop)</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="cd-req-label">Operational Frequency <span style={{ color: '#ef4444' }}>*</span></label>
                                    <select className="cd-modal-input" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }} value={form.tripFrequency} onChange={e => setForm({ ...form, tripFrequency: e.target.value })}>
                                        <option value="One Time">One Time (Fixed Date)</option>
                                        <option value="Recurring">Recurring (Weekly Schedule)</option>
                                    </select>
                                </div>
                                <div style={{ gridColumn: 'span 2' }}>
                                    <label className="cd-req-label">Route Selection <span style={{ color: '#ef4444' }}>*</span></label>
                                    <div style={{ display: 'flex', gap: 24, marginBottom: 16, padding: '12px 16px', background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer', color: routeSource === 'pre' ? '#0f172a' : '#64748b' }}>
                                            <input type="radio" style={{ width: 18, height: 18, accentColor: '#3b82f6' }} name="routeSource" checked={routeSource === 'pre'} onChange={() => { setRouteSource('pre'); setForm(f => ({ ...f, routeId: '', pickupLocation: '', dropLocation: '' })); }} />
                                            Pre-Authorized Route
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer', color: routeSource === 'new' ? '#0f172a' : '#64748b' }}>
                                            <input type="radio" style={{ width: 18, height: 18, accentColor: '#3b82f6' }} name="routeSource" checked={routeSource === 'new'} onChange={() => { setRouteSource('new'); setForm(f => ({ ...f, routeId: '', pickupLocation: '', dropLocation: '' })); }} />
                                            Propose New Route
                                        </label>
                                    </div>

                                    {routeSource === 'pre' ? (
                                        <>
                                            <div ref={routeDropdownRef} style={{ position: 'relative' }}>
                                                <div 
                                                    className="cd-modal-input" 
                                                    style={{ border: '1px solid #cbd5e1', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', background: 'white' }}
                                                    onClick={() => setShowRouteDropdown(!showRouteDropdown)}
                                                >
                                                    <span style={{ color: form.routeId ? '#0f172a' : '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', paddingRight: '12px' }}>
                                                        {form.routeId 
                                                            ? (() => {
                                                                const r = routes.find(x => x._id === form.routeId);
                                                                return r ? `${r.name} (${r.distance}km) — ${r.pickupLocation?.slice(0, 30)}... ➔ ${r.dropLocation?.slice(0, 30)}...` : 'Select an authorized logistics route...';
                                                            })() 
                                                            : 'Select an authorized logistics route...'}
                                                    </span>
                                                    <Icon d={icons.chevronDown} size={16} color="#64748b" />
                                                </div>
                                                
                                                {showRouteDropdown && (
                                                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, marginTop: 4, zIndex: 100, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: 300, display: 'flex', flexDirection: 'column' }}>
                                                        <div style={{ padding: '8px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc', borderRadius: '8px 8px 0 0' }}>
                                                            <div className="cd-input-wrap" style={{ margin: 0 }}>
                                                                <Icon d={icons.search} size={14} color="#94a3b8" className="cd-input-icon" />
                                                                <input 
                                                                    autoFocus
                                                                    type="text" 
                                                                    placeholder="Search by route name..." 
                                                                    value={routeSearchTerm} 
                                                                    onChange={e => setRouteSearchTerm(e.target.value)}
                                                                    style={{ width: '100%', padding: '8px 8px 8px 32px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, outline: 'none' }}
                                                                    onClick={e => e.stopPropagation()}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={{ overflowY: 'auto', padding: '4px 0' }}>
                                                            <div 
                                                                onClick={() => {
                                                                    setForm({ ...form, routeId: '', pickupLocation: '', dropLocation: '' });
                                                                    setShowRouteDropdown(false);
                                                                    setRouteSearchTerm('');
                                                                }}
                                                                style={{ padding: '10px 16px', cursor: 'pointer', fontSize: 13, color: '#64748b', borderBottom: '1px solid #f8fafc' }}
                                                                className="cd-route-option-hover"
                                                            >
                                                                Select an authorized logistics route...
                                                            </div>
                                                            {routes.filter(r => (r.name || '').toLowerCase().includes(routeSearchTerm.toLowerCase())).map(r => (
                                                                <div 
                                                                    key={r._id} 
                                                                    onClick={() => {
                                                                        setForm({ ...form, routeId: r._id, pickupLocation: r?.pickupLocation || '', dropLocation: r?.dropLocation || '' });
                                                                        setShowRouteDropdown(false);
                                                                        setRouteSearchTerm('');
                                                                    }}
                                                                    style={{ padding: '10px 16px', cursor: 'pointer', fontSize: 13, color: '#1e293b', borderBottom: '1px solid #f8fafc' }}
                                                                    className="cd-route-option-hover"
                                                                >
                                                                    {r.name} ({r.distance}km) — {r.pickupLocation?.slice(0, 30)}... ➔ {r.dropLocation?.slice(0, 30)}...
                                                                </div>
                                                            ))}
                                                            {routes.filter(r => (r.name || '').toLowerCase().includes(routeSearchTerm.toLowerCase())).length === 0 && (
                                                                <div style={{ padding: '12px 16px', fontSize: 13, color: '#64748b', textAlign: 'center' }}>No routes found</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            {form.routeId && (
                                                <div style={{ marginTop: 12, padding: '16px', background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', display: 'flex', gap: 24, boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                                    <div style={{ flex: 1 }}><div style={{ fontSize: 11, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 4 }}>Pickup Point</div><div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{form.pickupLocation}</div></div>
                                                    <div style={{ flex: 1 }}><div style={{ fontSize: 11, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 4 }}>Dropoff Point</div><div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{form.dropLocation}</div></div>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                                                <div>
                                                    <label className="cd-req-label" style={{ fontSize: 12 }}>New Pickup Location <span style={{ color: '#ef4444' }}>*</span></label>
                                                    <LocationAutocomplete 
                                                        value={form.pickupLocation} 
                                                        onChange={val => setForm({ ...form, pickupLocation: val })} 
                                                        placeholder="New pickup terminal" 
                                                        icon={icons.map} 
                                                        required 
                                                    />
                                                </div>
                                                <div>
                                                    <label className="cd-req-label" style={{ fontSize: 12 }}>New Drop Location <span style={{ color: '#ef4444' }}>*</span></label>
                                                    <LocationAutocomplete 
                                                        value={form.dropLocation} 
                                                        onChange={val => setForm({ ...form, dropLocation: val })} 
                                                        placeholder="New destination terminal" 
                                                        icon={icons.map} 
                                                        required 
                                                    />
                                                </div>
                                            </div>
                                            {form.pickupLocation && form.dropLocation && (
                                                <div style={{ marginTop: 12, fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', background: '#f8fafc', borderRadius: 8, border: '1px dashed #cbd5e1', width: 'fit-content' }}>
                                                    <Icon d={icons.map} size={14} color="#94a3b8" />
                                                    {fetchingDistance ? 'Calculating distance...' : adhocDistance ? `Estimated Distance: ${adhocDistance} KM` : 'Could not calculate distance'}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div style={{ height: 1, background: '#f1f5f9', margin: '32px 0' }}></div>

                        {/* Section 2: Timing */}
                        <div style={{ marginBottom: 32 }}>
                            <h3 style={{ fontSize: 14, fontWeight: 800, color: '#1e293b', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{ background: '#f1f5f9', width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Icon d={icons.clock} size={14} color="#64748b" />
                                </div>
                                Schedule & Timing
                            </h3>
                            {form.tripFrequency === 'One Time' ? (
                                <div style={{ marginBottom: 20 }}>
                                    <label className="cd-req-label">Execution Date <span style={{ color: '#ef4444' }}>*</span></label>
                                    <input type="date" className="cd-modal-input" style={{ border: '1px solid #cbd5e1' }} value={form.tripDate} onChange={e => setForm({ ...form, tripDate: e.target.value })} min={today} required />
                                </div>
                            ) : (
                                <div style={{ marginBottom: 24 }}>
                                    <label className="cd-req-label">Weekly Recurring Days <span style={{ color: '#ef4444' }}>*</span></label>
                                    <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                                            <button key={day} type="button" onClick={() => toggleDay(day)}
                                                style={{ flex: 1, padding: '12px 0', borderRadius: 10, border: form.recurringDays.includes(day) ? '1px solid #3b82f6' : '1px solid #e2e8f0', background: form.recurringDays.includes(day) ? '#eff6ff' : 'white', color: form.recurringDays.includes(day) ? '#2563eb' : '#64748b', fontSize: 13, fontWeight: 800, cursor: 'pointer', transition: 'all 0.2s' }}>
                                                {day.charAt(0)}
                                            </button>
                                        ))}
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                                        <div>
                                            <label className="cd-req-label" style={{ fontSize: 12 }}>Schedule Start Date <span style={{ color: '#ef4444' }}>*</span></label>
                                            <input type="date" className="cd-modal-input" style={{ border: '1px solid #cbd5e1' }} value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })} min={today} required />
                                        </div>
                                        <div>
                                            <label className="cd-req-label" style={{ fontSize: 12 }}>Schedule End Date <span style={{ color: '#ef4444' }}>*</span></label>
                                            <input type="date" className="cd-modal-input" style={{ border: '1px solid #cbd5e1' }} value={form.endDate} min={form.startDate || today} onChange={e => setForm({ ...form, endDate: e.target.value })} required />
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                                <div>
                                    <label className="cd-req-label">Start Time <span style={{ color: '#ef4444' }}>*</span></label>
                                    <input type="time" className="cd-modal-input" style={{ border: '1px solid #cbd5e1' }} value={form.startTime} onChange={e => setForm({ ...form, startTime: e.target.value })} required />
                                </div>
                                <div>
                                    <label className="cd-req-label">End Time (Est.)</label>
                                    <input type="time" className="cd-modal-input" style={{ border: '1px solid #cbd5e1' }} value={form.endTime} onChange={e => setForm({ ...form, endTime: e.target.value })} />
                                </div>
                            </div>
                        </div>

                        <div style={{ height: 1, background: '#f1f5f9', margin: '32px 0' }}></div>

                        {renderVehicleSelection()}

                        <div style={{ marginTop: 40, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <button type="submit" disabled={loading} style={{ padding: '0 64px', height: 56, borderRadius: 14, border: 'none', background: loading ? '#94a3b8' : '#3b82f6', color: 'white', fontSize: 16, fontWeight: 800, cursor: loading ? 'not-allowed' : 'pointer', boxShadow: '0 4px 12px rgba(59,130,246,0.3)', transition: 'all 0.2s' }}>
                                {loading ? 'Submitting Request...' : 'Submit Regular Trip Request'}
                            </button>
                            <p style={{ textAlign: 'center', fontSize: 13, color: '#64748b', marginTop: 16, fontWeight: 500 }}>
                                <Icon d={icons.check} size={14} color="#10b981" /> Operations will assign a vehicle and driver for dispatch once approved.
                            </p>
                        </div>
                    </form>
                ) : (
                    <form onSubmit={handleSubmit}>
                        {/* Adhoc Trip Passengers */}
                        <div style={{ marginBottom: 32 }}>
                            <h3 style={{ fontSize: 14, fontWeight: 800, color: '#1e293b', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{ background: '#fdf4ff', width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Icon d={icons.user} size={14} color="#d946ef" />
                                </div>
                                Passenger Information
                            </h3>
                            
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                {form.passengers.map((p, idx) => (
                                    <div key={idx} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 16, padding: 20, position: 'relative', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                                        {idx > 0 && (
                                            <button type="button" onClick={() => setForm({ ...form, passengers: form.passengers.filter((_, i) => i !== idx) })} style={{ position: 'absolute', top: 16, right: 16, background: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: 8, padding: '6px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s' }}>
                                                ✕ Remove
                                            </button>
                                        )}
                                        <div style={{ fontSize: 12, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 16 }}>Passenger {idx + 1}</div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                                            <div>
                                                <label className="cd-req-label" style={{ fontSize: 12 }}>Full Name <span style={{ color: '#ef4444' }}>*</span></label>
                                                <div className="cd-input-wrap">
                                                    <Icon d={icons.user} size={18} color="#94a3b8" className="cd-input-icon" />
                                                    <input 
                                                        list={`passenger-list-${idx}`}
                                                        className="cd-modal-input-icon" 
                                                        style={{ border: '1px solid #cbd5e1' }} 
                                                        value={p.passengerName} 
                                                        onChange={e => { 
                                                            const val = e.target.value;
                                                            const newP = [...form.passengers]; 
                                                            newP[idx].passengerName = val; 
                                                            
                                                            const matchedPassenger = passengers.find(pass => pass.name === val);
                                                            if (matchedPassenger) {
                                                                newP[idx].passengerPhone = matchedPassenger.primaryPhoneNumber || matchedPassenger.contactNumber || '';
                                                                newP[idx].passengerEmail = matchedPassenger.email || '';
                                                                if (idx === 0) {
                                                                    setForm(f => ({
                                                                        ...f,
                                                                        passengers: newP
                                                                    }));
                                                                    return;
                                                                }
                                                            }
                                                            setForm({ ...form, passengers: newP }); 
                                                        }} 
                                                        placeholder="Full name (Select or type new)" 
                                                        required 
                                                    />
                                                    <datalist id={`passenger-list-${idx}`}>
                                                        {passengers.map(pass => (
                                                            <option key={pass._id || Math.random()} value={pass.name} />
                                                        ))}
                                                    </datalist>
                                                </div>
                                            </div>
                                            <div>
                                                <label className="cd-req-label" style={{ fontSize: 12 }}>Phone Number <span style={{ color: '#ef4444' }}>*</span></label>
                                                <div className="cd-input-wrap"><Icon d={icons.phone} size={18} color="#94a3b8" className="cd-input-icon" /><input className="cd-modal-input-icon" style={{ border: '1px solid #cbd5e1' }} value={p.passengerPhone} onChange={e => { const newP = [...form.passengers]; newP[idx].passengerPhone = e.target.value; setForm({ ...form, passengers: newP }); }} placeholder="+971 XXXX XXXX" required /></div>
                                            </div>
                                            <div style={{ gridColumn: 'span 2' }}>
                                                <label className="cd-req-label" style={{ fontSize: 12 }}>Email Address (Optional)</label>
                                                <div className="cd-input-wrap"><Icon d={icons.mail} size={18} color="#94a3b8" className="cd-input-icon" /><input type="email" className="cd-modal-input-icon" style={{ border: '1px solid #cbd5e1' }} value={p.passengerEmail} onChange={e => { const newP = [...form.passengers]; newP[idx].passengerEmail = e.target.value; setForm({ ...form, passengers: newP }); }} placeholder="passenger@company.com" /></div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            
                            <button type="button" onClick={() => setForm({ ...form, passengers: [...form.passengers, { passengerName: '', passengerPhone: '', passengerEmail: '' }] })} style={{ marginTop: 16, background: 'transparent', color: '#d946ef', border: '1px dashed #f0abfc', borderRadius: 12, padding: '12px', fontSize: 14, fontWeight: 700, cursor: 'pointer', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'all 0.2s' }}>
                                <Icon d={icons.plus} size={16} color="#d946ef" /> Add Another Passenger
                            </button>
                        </div>

                        <div style={{ height: 1, background: '#f1f5f9', margin: '32px 0' }}></div>

                        {/* Section 2: Route & Timing */}
                        <div style={{ marginBottom: 32 }}>
                            <h3 style={{ fontSize: 14, fontWeight: 800, color: '#1e293b', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{ background: '#f1f5f9', width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Icon d={icons.map} size={14} color="#64748b" />
                                </div>
                                Location & Time
                            </h3>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                                <div style={{ gridColumn: 'span 2' }}>
                                    <label className="cd-req-label">Pickup Location <span style={{ color: '#ef4444' }}>*</span></label>
                                    <LocationAutocomplete 
                                        value={form.pickupLocation} 
                                        onChange={val => setForm({ ...form, pickupLocation: val })} 
                                        placeholder="Where to pick up the passenger" 
                                        icon={icons.map} 
                                        required 
                                    />
                                </div>
                                <div style={{ gridColumn: 'span 2' }}>
                                    <label className="cd-req-label">Drop Location <span style={{ color: '#ef4444' }}>*</span></label>
                                    <LocationAutocomplete 
                                        value={form.dropLocation} 
                                        onChange={val => setForm({ ...form, dropLocation: val })} 
                                        placeholder="Destination / drop-off point" 
                                        icon={icons.map} 
                                        required 
                                    />
                                </div>
                                {reqType === 'adhoc' && (adhocDistance || fetchingDistance) && (
                                    <div style={{ gridColumn: 'span 2', marginTop: '-8px', fontSize: 12, fontWeight: 700, color: '#00B2A9', display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <Icon d={icons.map} size={14} />
                                        {fetchingDistance ? 'Calculating distance...' : `Estimated Distance: ${adhocDistance} KM`}
                                    </div>
                                )}
                                <div>
                                    <label className="cd-req-label">Trip Date <span style={{ color: '#ef4444' }}>*</span></label>
                                    <input type="date" className="cd-modal-input" style={{ border: '1px solid #cbd5e1' }} value={form.tripDate} onChange={e => setForm({ ...form, tripDate: e.target.value })} min={today} required />
                                </div>
                                <div>
                                    <label className="cd-req-label">Pickup Time <span style={{ color: '#ef4444' }}>*</span></label>
                                    <input type="time" className="cd-modal-input" style={{ border: '1px solid #cbd5e1' }} value={form.pickupTime} onChange={e => setForm({ ...form, pickupTime: e.target.value })} required />
                                </div>
                            </div>
                        </div>

                        <div style={{ height: 1, background: '#f1f5f9', margin: '32px 0' }}></div>

                        {renderVehicleSelection()}

                        <div style={{ marginTop: 40, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <button type="submit" disabled={loading} style={{ padding: '0 64px', height: 56, borderRadius: 14, border: 'none', background: loading ? '#94a3b8' : '#d946ef', color: 'white', fontSize: 16, fontWeight: 800, cursor: loading ? 'not-allowed' : 'pointer', boxShadow: '0 4px 12px rgba(217,70,239,0.3)', transition: 'all 0.2s' }}>
                                {loading ? 'Submitting Request...' : 'Submit Adhoc Trip Request'}
                            </button>
                            <p style={{ textAlign: 'center', fontSize: 13, color: '#64748b', marginTop: 16, fontWeight: 500 }}>
                                <Icon d={icons.check} size={14} color="#10b981" /> Adhoc requests are fulfilled as on-demand transport promptly.
                            </p>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}

function RoutesSection({ routes, loading }) {
    const [stops, setStops] = useState({});
    const [loadingStops, setLoadingStops] = useState(false);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [expandedRouteId, setExpandedRouteId] = useState(null);
    const [itemsPerPage, setItemsPerPage] = useState(5);

    useEffect(() => {
        if (!routes.length) return;
        setLoadingStops(true);
        const fetchAllStops = async () => {
            const stopsData = {};
            await Promise.all(routes.map(async (r) => {
                try {
                    const res = await fetch(`${API}/route-stops/${r._id}`);
                    if (res.ok) {
                        const data = await res.json();
                        stopsData[r._id] = data;
                    }
                } catch (e) { console.error(e); }
            }));
            setStops(stopsData);
            setLoadingStops(false);
        };
        fetchAllStops();
    }, [routes]);

    const filteredRoutes = routes.filter(r => {
        const matchesSearch = r.name.toLowerCase().includes(search.toLowerCase()) || r.pickupLocation.toLowerCase().includes(search.toLowerCase()) || r.dropLocation.toLowerCase().includes(search.toLowerCase());
        const matchesStatus = statusFilter === 'All' || (r.status || 'Active') === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const totalPages = Math.ceil(filteredRoutes.length / itemsPerPage);
    const paginatedRoutes = filteredRoutes.slice((page - 1) * itemsPerPage, page * itemsPerPage);

    const totalRoutesCount = routes.length;
    const activeRoutesCount = routes.filter(r => (r.status || 'Active') === 'Active').length;
    const multiStopCount = routes.filter(r => (stops[r._id] || []).length > 0).length;
    const directCount = totalRoutesCount - multiStopCount;
    const routesWithDistance = routes.filter(r => r.distance);
    const avgDistance = routesWithDistance.length ? (routesWithDistance.reduce((sum, r) => sum + Number(r.distance || 0), 0) / routesWithDistance.length) : 0;

    return (
        <div style={{ animation: 'fadeUp 0.4s ease-out' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28, background: 'white', padding: '20px 24px', borderRadius: 20, border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{ width: 48, height: 48, borderRadius: 14, background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon d={icons.map} size={24} color="#00B2A9" />
                    </div>
                    <div>
                        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>Available Routes</h2>
                        <p style={{ margin: 0, fontSize: 13, color: '#64748b', fontWeight: 600 }}>Explore operational transit networks and waypoints.</p>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#64748b' }}>Show</span>
                        <select 
                            value={itemsPerPage} 
                            onChange={(e) => { setItemsPerPage(Number(e.target.value)); setPage(1); }}
                            style={{ padding: '8px 12px', borderRadius: 10, border: '1px solid #e2e8f0', background: '#f8fafc', fontSize: 13, fontWeight: 700, color: '#0f172a', outline: 'none', cursor: 'pointer' }}
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                        </select>
                    </div>
                    <div className="cd-search-wrap" style={{ width: 300 }}>
                        <Icon d={icons.search} size={16} color="#94a3b8" />
                        <input className="cd-search-input" style={{ width: '100%' }} placeholder="Search routes, locations..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
                    </div>
                </div>
            </div>

            {/* KPI Overview */}
            <div className="cd-stats-grid" style={{ marginTop: 0, marginBottom: 24 }}>
                <div className="cd-stat-card" style={{ background: 'white', border: '1px solid #e2e8f0', borderLeft: '4px solid #16539C', cursor: 'default' }}>
                    <div className="cd-stat-value" style={{ color: '#0f172a' }}>{totalRoutesCount}</div>
                    <div className="cd-stat-label" style={{ color: '#64748b' }}>Total Routes</div>
                </div>
                <div className="cd-stat-card clickable" style={{ background: 'white', border: '1px solid #e2e8f0', borderLeft: '4px solid #16539C' }} onClick={() => { setStatusFilter('Active'); setPage(1); }}>
                    <div className="cd-stat-value" style={{ color: '#0f172a' }}>{activeRoutesCount}</div>
                    <div className="cd-stat-label" style={{ color: '#64748b' }}>Active</div>
                </div>
                <div className="cd-stat-card" style={{ background: 'white', border: '1px solid #e2e8f0', borderLeft: '4px solid #16539C', cursor: 'default' }}>
                    <div className="cd-stat-value" style={{ color: '#0f172a' }}>{directCount}</div>
                    <div className="cd-stat-label" style={{ color: '#64748b' }}>Direct Routes</div>
                </div>
                <div className="cd-stat-card" style={{ background: 'white', border: '1px solid #e2e8f0', borderLeft: '4px solid #16539C', cursor: 'default' }}>
                    <div className="cd-stat-value" style={{ color: '#0f172a' }}>{multiStopCount}</div>
                    <div className="cd-stat-label" style={{ color: '#64748b' }}>Multi-Stop</div>
                </div>
                <div className="cd-stat-card" style={{ background: 'white', border: '1px solid #e2e8f0', borderLeft: '4px solid #16539C', cursor: 'default' }}>
                    <div className="cd-stat-value" style={{ color: '#0f172a' }}>{avgDistance ? avgDistance.toFixed(1) : '—'}</div>
                    <div className="cd-stat-label" style={{ color: '#64748b' }}>Avg Distance (KM)</div>
                </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Status</span>
                {['All', 'Active', 'Inactive'].map(s => (
                    <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }} style={{
                        padding: '6px 14px', borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                        border: statusFilter === s ? 'none' : '1.5px solid #e2e8f0',
                        background: statusFilter === s ? '#0f172a' : 'white',
                        color: statusFilter === s ? 'white' : '#475569',
                        transition: 'all 0.2s'
                    }}>{s}</button>
                ))}
            </div>

            {loading ? <div className="cd-empty">Loading Routes...</div> : filteredRoutes.length === 0 ? <div className="cd-empty">No routes found.</div> : (
                <>
                    <div style={{ background: 'white', borderRadius: 20, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                                <thead>
                                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                        <th style={{ padding: '16px 24px', fontSize: 12, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Route Details</th>
                                        <th style={{ padding: '16px 24px', fontSize: 12, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Pickup Hub</th>
                                        <th style={{ padding: '16px 24px', fontSize: 12, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Waypoints</th>
                                        <th style={{ padding: '16px 24px', fontSize: 12, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Drop Hub</th>
                                        <th style={{ padding: '16px 24px', fontSize: 12, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Metrics</th>
                                        <th style={{ padding: '16px 24px', fontSize: 12, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedRoutes.map((r, i) => (
                                        <React.Fragment key={r._id}>
                                            <tr style={{ borderBottom: (i === paginatedRoutes.length - 1 && expandedRouteId !== r._id) ? 'none' : '1px solid #f1f5f9', transition: 'background 0.2s', background: expandedRouteId === r._id ? '#f8fafc' : 'transparent' }} onMouseEnter={e => { if(expandedRouteId !== r._id) e.currentTarget.style.background = '#f8fafc'; }} onMouseLeave={e => { if(expandedRouteId !== r._id) e.currentTarget.style.background = 'transparent'; }}>
                                                <td style={{ padding: '20px 24px', minWidth: '160px' }}>
                                                    <div style={{ fontSize: 15, fontWeight: 800, color: '#475569' }}>{r.name}</div>
                                                    <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4, fontFamily: 'monospace' }}>#{r._id.slice(-8).toUpperCase()}</div>
                                                </td>
                                                <td style={{ padding: '20px 24px', minWidth: '180px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, background: '#f0fdf4', color: '#166534', padding: '8px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600, lineHeight: 1.4 }}>
                                                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', marginTop: 6, flexShrink: 0 }}></div>
                                                        <span style={{ wordBreak: 'break-word' }}>{r.pickupLocation}</span>
                                                    </div>
                                                </td>
                                                <td style={{ padding: '20px 24px', minWidth: '160px' }}>
                                                    {(stops[r._id] || []).length > 0 ? (
                                                        <button 
                                                            onClick={() => setExpandedRouteId(expandedRouteId === r._id ? null : r._id)}
                                                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: expandedRouteId === r._id ? '#3b82f6' : '#e6f0fa', color: expandedRouteId === r._id ? 'white' : '#16539C', border: 'none', padding: '8px 14px', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap' }}
                                                        >
                                                            <Icon d={icons.map} size={14} color={expandedRouteId === r._id ? "white" : "#16539C"} />
                                                            {expandedRouteId === r._id ? 'Hide Stops' : `View ${(stops[r._id] || []).length} Stops`}
                                                        </button>
                                                    ) : (
                                                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#f1f5f9', color: '#64748b', padding: '8px 14px', borderRadius: 10, fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>
                                                            <Icon d={icons.check} size={14} /> Direct Route
                                                        </div>
                                                    )}
                                                </td>
                                                <td style={{ padding: '20px 24px', minWidth: '180px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, background: '#fef2f2', color: '#991b1b', padding: '8px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600, lineHeight: 1.4 }}>
                                                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#ef4444', marginTop: 6, flexShrink: 0 }}></div>
                                                        <span style={{ wordBreak: 'break-word' }}>{r.dropLocation}</span>
                                                    </div>
                                                </td>
                                                <td style={{ padding: '20px 24px', whiteSpace: 'nowrap' }}>
                                                    <div style={{ display: 'flex', gap: 12 }}>
                                                        <div>
                                                            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Dist</div>
                                                            <div style={{ fontSize: 14, fontWeight: 800, color: '#475569' }}>{r.distance ? `${r.distance} KM` : '—'}</div>
                                                        </div>
                                                        <div>
                                                            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Time</div>
                                                            <div style={{ fontSize: 14, fontWeight: 800, color: '#475569' }}>{r.estimatedMinutes ? `${r.estimatedMinutes} M` : '—'}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td style={{ padding: '20px 24px', textAlign: 'right' }}>
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: r.status === 'Active' ? '#f0fdf4' : '#f8fafc', color: r.status === 'Active' ? '#15803d' : '#64748b', padding: '6px 12px', borderRadius: 20, fontSize: 12, fontWeight: 800, border: `1px solid ${r.status === 'Active' ? '#bbf7d0' : '#e2e8f0'}` }}>
                                                        {r.status === 'Active' && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e' }}></div>}
                                                        {r.status}
                                                    </span>
                                                </td>
                                            </tr>
                                            {expandedRouteId === r._id && (stops[r._id] || []).length > 0 && (
                                                <tr style={{ background: '#f8fafc', borderBottom: i === paginatedRoutes.length - 1 ? 'none' : '1px solid #e2e8f0' }}>
                                                    <td colSpan="6" style={{ padding: '0 24px 24px 24px' }}>
                                                        <div style={{ background: 'white', borderRadius: 16, border: '1px solid #cbd5e1', padding: '20px', boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
                                                            <h4 style={{ margin: '0 0 16px 0', fontSize: 14, fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                <Icon d={icons.map} size={16} color="#00B2A9" /> Route Sequence & Waypoints
                                                            </h4>
                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                                                                {stops[r._id].map((st, idx) => (
                                                                    <div key={st._id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#f1f5f9', padding: '8px 16px', borderRadius: 12, border: '1px solid #e2e8f0' }}>
                                                                        <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--cd-primary)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, boxShadow: '0 2px 4px rgba(59,130,246,0.3)' }}>{idx + 1}</div>
                                                                        <div style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>{st.name}</div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    {totalPages > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20 }}>
                            <div style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>Showing {((page - 1) * itemsPerPage) + 1} to {Math.min(page * itemsPerPage, filteredRoutes.length)} of {filteredRoutes.length} entries</div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button disabled={page === 1} onClick={() => setPage(page - 1)} style={{ background: page === 1 ? '#f8fafc' : 'white', border: '1px solid #e2e8f0', padding: '8px 16px', borderRadius: 10, cursor: page === 1 ? 'not-allowed' : 'pointer', color: page === 1 ? '#94a3b8' : '#0f172a', fontWeight: 700, fontSize: 13, transition: 'all 0.2s' }}>Previous</button>
                                {[...Array(totalPages)].map((_, idx) => (
                                    <button key={idx} onClick={() => setPage(idx + 1)} style={{ background: page === idx + 1 ? '#0f172a' : 'white', color: page === idx + 1 ? 'white' : '#475569', border: page === idx + 1 ? 'none' : '1px solid #e2e8f0', width: 36, height: 36, borderRadius: 10, fontWeight: 800, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}>{idx + 1}</button>
                                ))}
                                <button disabled={page === totalPages} onClick={() => setPage(page + 1)} style={{ background: page === totalPages ? '#f8fafc' : 'white', border: '1px solid #e2e8f0', padding: '8px 16px', borderRadius: 10, cursor: page === totalPages ? 'not-allowed' : 'pointer', color: page === totalPages ? '#94a3b8' : '#0f172a', fontWeight: 700, fontSize: 13, transition: 'all 0.2s' }}>Next</button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function FormGroup({ label, value, onChange, type = 'text', options = [], innerRef }) {
    return (<div style={{ marginBottom: 12 }}><label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 4 }}>{label}</label>{type === 'select' ? (<select className="cd-modal-input" value={value} onChange={e => onChange(e.target.value)}>{options.map(o => <option key={o} value={o}>{o}</option>)}</select>) : (<input ref={innerRef} className="cd-modal-input" value={value} onChange={e => onChange(e.target.value)} />)}</div>);
}

function ModalOverlay({ children, onClose, title }) {
    return (
        <div className="cd-modal-overlay" onClick={onClose}>
            <div className="cd-modal-content" onClick={e => e.stopPropagation()}>
                {/* Sticky Header */}
                <div className="cd-modal-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 6, height: 24, background: 'linear-gradient(180deg, #00B2A9, #0f172a)', borderRadius: 3 }} />
                        <h3 className="cd-modal-title">{title}</h3>
                    </div>
                    <button className="cd-modal-close" onClick={onClose}>
                        <Icon d={icons.x} size={16} color="#64748b" />
                    </button>
                </div>
                {/* Scrollable Body */}
                <div className="cd-modal-body">
                    {children}
                </div>
            </div>
        </div>
    );
}

function InfoRow({ icon, label, value }) {
    return (<div className="cd-info-row"><Icon d={icon} size={15} color="#00B2A9" /><strong>{label}:</strong><span>{value || '—'}</span></div>);
}

function TripsSection({ trips, allocations = [], loading, client, operatorDetails, onRefresh, initialFilter, onManageAllocations, onNavigate, onViewHistory }) {
    const [viewingPassengersTrip, setViewingPassengersTrip] = useState(null);
    const [tripAllocations, setTripAllocations] = useState([]);
    const [loadingTripAllocs, setLoadingTripAllocs] = useState(false);

    const downloadTripCSV = (trip) => {
        const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
        const headers = [
            'Trip ID', 'Trip Type', 'Status', 'Route Details',
            'Pickup Location', 'Drop Location',
            'Driver Name', 'Driver Phone',
            'Vehicle Reg', 'Vehicle Make', 'Vehicle Model',
            'Billing Type', 'Amount',
            'Date', 'Start Time', 'End Time'
        ];
        const row = [
            trip._id,
            trip.tripType || 'N/A',
            trip.status || 'Completed',
            trip.routeId?.name || 'Custom Route',
            trip.pickupLocation || 'N/A',
            trip.dropLocation || 'N/A',
            trip.driverId?.name || 'Unassigned',
            trip.driverId?.phoneNumber || 'N/A',
            trip.vehicleId?.registrationNumber || 'Unassigned',
            trip.vehicleId?.make || 'N/A',
            trip.vehicleId?.model || 'N/A',
            trip.billingType || 'N/A',
            trip.amount ?? trip.proposedPrice ?? 'N/A',
            trip.tripDate ? new Date(trip.tripDate).toLocaleDateString() : 'N/A',
            trip.startTime || 'N/A',
            trip.endTime || 'N/A',
        ];
        const csvContent = [
            headers.map(esc).join(','),
            row.map(esc).join(',')
        ].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `trip-sheet-${trip._id?.slice(-8) || 'export'}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const openManifest = async (trip) => {
        setViewingPassengersTrip(trip);
        setTripAllocations([]);
        setLoadingTripAllocs(true);
        try {
            const res = await fetch(`${API}/passenger-allocations/trip/${trip._id}`);
            const data = await res.json();
            setTripAllocations(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error('Failed to load trip allocations:', e);
        } finally {
            setLoadingTripAllocs(false);
        }
    };

    const [filter, setFilter] = useState(initialFilter || 'All');
    const [tripTypeFilter, setTripTypeFilter] = useState('All');
    const [dateFilter, setDateFilter] = useState(() => new Date().toISOString().split('T')[0]);
    const [passengerSearch, setPassengerSearch] = useState('');
    const [search, setSearch] = useState('');

    useEffect(() => {
        if (initialFilter) {
            setFilter(initialFilter);
            setPage(1);
        }
    }, [initialFilter]);

    const [page, setPage] = useState(1);
    const PAGE_SIZE = 10;

    const [suggestModal, setSuggestModal] = useState(null);
    const [viewAdhocPassengerTrip, setViewAdhocPassengerTrip] = useState(null);
    const [clientNotes, setClientNotes] = useState('');
    const [responding, setResponding] = useState(false);

    const proposedTrips = trips.filter(t => t.status === 'PriceProposed');
    const filtered = trips.filter(t => {
        if (filter !== 'All' && t.status !== filter) return false;
        
        if (tripTypeFilter !== 'All') {
            const tt = (t.tripType || '').toLowerCase();
            if (tripTypeFilter === 'Login' && tt !== 'login') return false;
            if (tripTypeFilter === 'Logout' && tt !== 'logout') return false;
            if (tripTypeFilter === 'Adhoc' && (!tt.includes('on_demand') && !tt.includes('adhoc'))) return false;
            if (tripTypeFilter === 'Fixed' && tt.includes('on_demand')) return false;
        }

        if (dateFilter) {
            // For recurring trips that might not have a specific tripDate but have recurringDays,
            // we could skip filtering, or filter by day of week. To be safe, we check both.
            const dStr = t.tripDate ? new Date(t.tripDate).toISOString().split('T')[0] : null;
            const filterDay = new Date(dateFilter).toLocaleDateString('en-US', { weekday: 'short' });
            
            if (t.tripFrequency === 'Recurring' && t.recurringDays?.length > 0) {
                if (!t.recurringDays.includes(filterDay)) return false;
            } else {
                if (dStr !== dateFilter) return false;
            }
        }
        
        if (search) {
            const s = search.toLowerCase();
            const matchesSearch = 
                (t.tripName || '').toLowerCase().includes(s) || 
                (t.passengerName || '').toLowerCase().includes(s) || 
                (t.pickupLocation || '').toLowerCase().includes(s) || 
                (t.dropLocation || '').toLowerCase().includes(s) ||
                (t.vehicleId?.registrationNumber || '').toLowerCase().includes(s) ||
                (t.driverId?.name || '').toLowerCase().includes(s);
            if (!matchesSearch) return false;
        }
        
        return true;
    });

    const scheduledCount = trips.filter(t => t.status === 'Scheduled').length;
    const requestedCount = trips.filter(t => t.status === 'Requested').length;
    const completedCount = trips.filter(t => t.status === 'Completed').length;
    const cancelledCount = trips.filter(t => t.status === 'Cancelled').length;

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const pageData = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const changeFilter = (f) => { setFilter(f); setPage(1); };

    const statusStyle = (s) => {
        const map = {
            'Scheduled': { bg: '#dcfce7', color: '#166534' },
            'Requested': { bg: '#fef3c7', color: '#92400e' },
            'Completed': { bg: '#dbeafe', color: '#1e40af' },
            'Cancelled': { bg: '#fee2e2', color: '#991b1b' },
            'Rejected': { bg: '#fee2e2', color: '#991b1b' },
            'In Progress': { bg: '#f0fdf4', color: '#166534' },
            'PriceProposed': { bg: '#e0f0ff', color: '#5b21b6' },
            'ClientAccepted': { bg: '#d1fae5', color: '#065f46' },
            'ClientRejected': { bg: '#fee2e2', color: '#991b1b' },
            'ClientSuggested': { bg: '#fef9c3', color: '#713f12' },
        };
        return map[s] || { bg: '#f1f5f9', color: '#475569' };
    };

    const tripTypeStyle = (t) => {
        const lower = (t || '').toLowerCase();
        if (lower === 'login') return { bg: '#dbeafe', color: '#1d4ed8', label: 'Login' };
        if (lower === 'logout') return { bg: '#fce7f3', color: '#be185d', label: 'Logout' };
        if (lower.includes('on_demand') || lower.includes('adhoc')) return { bg: '#fef3c7', color: '#b45309', label: 'Adhoc' };
        if (t) return { bg: '#f1f5f9', color: '#475569', label: t };
        return { bg: '#f1f5f9', color: '#94a3b8', label: '—' };
    };

    const statusLabel = (t) => {
        if (t.status === 'PriceProposed') return formatCurrency(t.proposedPrice || 0, operatorDetails?.country);
        if (t.status === 'ClientAccepted') return 'Accepted';
        if (t.status === 'ClientRejected') return 'Rejected';
        if (t.status === 'ClientSuggested') return 'Counter Sent';
        if (t.status === 'In Progress') return 'In Progress';
        return t.status || '—';
    };

    const handleClientResponse = async (trip, responseStatus, notes = '') => {
        setResponding(true);
        let finalStatus = responseStatus;
        // If client accepts and fleet is already assigned, directly schedule it
        if (responseStatus === 'ClientAccepted' && (trip.vehicleId || trip.vendorId || trip.isVendorMode)) {
            finalStatus = 'Scheduled';
        }

        try {
            await fetch(`${API}/trips/${trip._id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    status: finalStatus,
                    clientResponse: responseStatus === 'ClientAccepted' ? 'Accept' : responseStatus === 'ClientRejected' ? 'Reject' : 'Suggest',
                    clientNotes: notes
                })
            });
            setSuggestModal(null);
            setClientNotes('');
            if (onRefresh) onRefresh();
        } catch (err) { alert('Error responding. Please try again.'); }
        finally { setResponding(false); }
    };

    return (
        <div>
            {/* Price Proposal Cards */}
            {proposedTrips.length > 0 && (
                <div style={{ marginBottom: 32 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                        <div style={{ background: '#e0f0ff', color: '#7c3aed', padding: '6px', borderRadius: '8px', display: 'flex' }}>
                            <Icon d={icons.trips} size={16} />
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 800, color: '#11427D', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            {proposedTrips.length} Price Proposal{proposedTrips.length > 1 ? 's' : ''} Pending Action
                        </div>
                    </div>
                    {proposedTrips.map(t => (
                        <div key={t._id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: '24px', marginBottom: 16, boxShadow: '0 4px 12px rgba(0,0,0,0.03)', position: 'relative', overflow: 'hidden' }}>
                            <div style={{ position: 'absolute', top: 0, left: 0, width: 4, height: '100%', background: '#7c3aed' }} />
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 20 }}>
                                <div style={{ flex: 1, minWidth: 300 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                                        <div style={{ fontSize: 16, fontWeight: 800, color: '#1e293b' }}>{t.passengerName || 'Trip Request'}</div>
                                        <div style={{ fontSize: 11, fontWeight: 700, color: '#00B2A9', background: '#eef2ff', padding: '3px 10px', borderRadius: 20, border: '1px solid #e6f0fa' }}>
                                            {t.tripFrequency === 'Recurring' 
                                                ? <>{t.recurringDays?.length > 0 ? t.recurringDays.map(d => d.slice(0, 3).toUpperCase()).join(', ') + ' ' : ''} ({t.startDate ? new Date(t.startDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'} to {t.endDate ? new Date(t.endDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'})</>
                                                : t.tripDate ? new Date(t.tripDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'} · {t.pickupTime || t.startTime || ''}
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#64748b', fontSize: 13, marginBottom: 12 }}>
                                        <Icon d={icons.map} size={14} color="#94a3b8" />
                                        <span style={{ fontWeight: 600 }}>{(t.pickupLocation || '').split(',')[0]}</span>
                                        <span style={{ color: '#cbd5e1' }}>→</span>
                                        <span style={{ fontWeight: 600 }}>{(t.dropLocation || '').split(',')[0]}</span>
                                    </div>
                                    {t.operatorNotes && (
                                        <div style={{ display: 'flex', gap: 8, background: '#f8fafc', padding: '10px 14px', borderRadius: 10, border: '1px solid #f1f5f9', marginBottom: 16 }}>
                                            <Icon d={icons.fileText} size={14} color="#7c3aed" />
                                            <div style={{ fontSize: 12, color: '#475569', fontStyle: 'italic', lineHeight: 1.4 }}>"{t.operatorNotes}"</div>
                                        </div>
                                    )}
                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                                        <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Proposed Price:</span>
                                        <span style={{ fontSize: 24, fontWeight: 900, color: '#063A5C' }}>
                                            {formatCurrency(t.proposedPrice || 0, operatorDetails?.country)}
                                        </span>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
                                    <button 
                                        onClick={() => setViewAdhocPassengerTrip(t)} 
                                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 24px', background: '#f8fafc', color: '#0f172a', border: '1.5px solid #e2e8f0', borderRadius: 12, fontWeight: 800, fontSize: 14, cursor: 'pointer', transition: 'all 0.2s' }}
                                    >
                                        <Icon d={icons.user} size={16} color="#475569" /> View Passenger
                                    </button>
                                    <button 
                                        onClick={() => handleClientResponse(t, 'ClientAccepted')} 
                                        disabled={responding} 
                                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 24px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 12, fontWeight: 800, fontSize: 14, cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 4px 10px rgba(16,185,129,0.2)' }}
                                    >
                                        <Icon d={icons.check} size={16} color="white" /> Accept
                                    </button>
                                    <button 
                                        onClick={() => { setSuggestModal(t); setClientNotes(''); }} 
                                        disabled={responding} 
                                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 24px', background: '#fff', color: '#d97706', border: '1.5px solid #fbbf24', borderRadius: 12, fontWeight: 800, fontSize: 14, cursor: 'pointer', transition: 'all 0.2s' }}
                                    >
                                        <Icon d={icons.analytics} size={16} color="#d97706" /> Suggest
                                    </button>
                                    <button 
                                        onClick={() => handleClientResponse(t, 'ClientRejected')} 
                                        disabled={responding} 
                                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 24px', background: '#fff', color: '#ef4444', border: '1.5px solid #fecaca', borderRadius: 12, fontWeight: 800, fontSize: 14, cursor: 'pointer', transition: 'all 0.2s' }}
                                    >
                                        <Icon d={icons.x} size={16} color="#ef4444" /> Reject
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* KPI Overview */}
            <div className="cd-stats-grid" style={{ marginTop: 0, marginBottom: 24 }}>
                <div className="cd-stat-card clickable" style={{ background: 'white', border: '1px solid #e2e8f0', borderLeft: '4px solid #16539C' }} onClick={() => changeFilter('All')}>
                    <div className="cd-stat-value" style={{ color: '#0f172a' }}>{trips.length}</div>
                    <div className="cd-stat-label" style={{ color: '#64748b' }}>Total Trips</div>
                </div>
                <div className="cd-stat-card clickable" style={{ background: 'white', border: '1px solid #e2e8f0', borderLeft: '4px solid #16539C' }} onClick={() => changeFilter('Scheduled')}>
                    <div className="cd-stat-value" style={{ color: '#0f172a' }}>{scheduledCount}</div>
                    <div className="cd-stat-label" style={{ color: '#64748b' }}>Scheduled</div>
                </div>
                <div className="cd-stat-card clickable" style={{ background: 'white', border: '1px solid #e2e8f0', borderLeft: '4px solid #16539C' }} onClick={() => changeFilter('Requested')}>
                    <div className="cd-stat-value" style={{ color: '#0f172a' }}>{requestedCount}</div>
                    <div className="cd-stat-label" style={{ color: '#64748b' }}>Requested</div>
                </div>
                <div className="cd-stat-card clickable" style={{ background: 'white', border: '1px solid #e2e8f0', borderLeft: '4px solid #16539C' }} onClick={() => changeFilter('Completed')}>
                    <div className="cd-stat-value" style={{ color: '#0f172a' }}>{completedCount}</div>
                    <div className="cd-stat-label" style={{ color: '#64748b' }}>Completed</div>
                </div>
                <div className="cd-stat-card clickable" style={{ background: 'white', border: '1px solid #e2e8f0', borderLeft: '4px solid #16539C' }} onClick={() => changeFilter('Cancelled')}>
                    <div className="cd-stat-value" style={{ color: '#0f172a' }}>{cancelledCount}</div>
                    <div className="cd-stat-label" style={{ color: '#64748b' }}>Cancelled</div>
                </div>
            </div>

            {/* Main Trips Table Card */}
            <div className="cd-card" style={{ padding: 0, overflow: 'hidden' }}>
                {/* Filter Bar */}
                <div style={{ padding: '18px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                        <div style={{ display: 'inline-flex', background: '#f1f5f9', padding: 6, borderRadius: 14, gap: 4, flexWrap: 'wrap' }}>
                            {[
                                { label: 'All Trips', val: 'All', count: trips.length },
                                { label: 'Scheduled', val: 'Scheduled', count: scheduledCount },
                                { label: 'Requested', val: 'Requested', count: requestedCount },
                                { label: 'Completed', val: 'Completed', count: completedCount },
                                { label: 'Cancelled', val: 'Cancelled', count: cancelledCount },
                            ].map(({ label, val, count }) => (
                                <button key={val} onClick={() => changeFilter(val)} style={{
                                    padding: '8px 20px', borderRadius: 10, fontSize: 13, fontWeight: filter === val ? 700 : 600, cursor: 'pointer',
                                    border: 'none',
                                    background: filter === val ? 'white' : 'transparent',
                                    color: filter === val ? 'var(--cd-primary)' : '#64748b',
                                    transition: 'all 0.2s ease',
                                    boxShadow: filter === val ? '0 2px 8px -2px rgba(0,0,0,0.1), 0 1px 2px -1px rgba(0,0,0,0.05)' : 'none',
                                    display: 'flex', alignItems: 'center', gap: 8
                                }}>
                                    {label} 
                                    <span style={{ 
                                        background: filter === val ? 'var(--cd-primary-light)' : '#e2e8f0', 
                                        color: filter === val ? 'var(--cd-primary)' : '#64748b', 
                                        padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700 
                                    }}>
                                        {count}
                                    </span>
                                </button>
                            ))}
                        </div>
                        <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>{filtered.length} record{filtered.length !== 1 ? 's' : ''}</div>
                    </div>
                    
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', background: '#f8fafc', padding: '12px 16px', borderRadius: 12, border: '1px solid #e2e8f0', justifyContent: 'flex-end' }}>
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                            <div className="cd-search-wrap" style={{ position: 'relative', width: 250 }}>
                                <Icon d={icons.search} size={16} color="#94a3b8" style={{ position: 'absolute', left: 12, top: 10 }} />
                                <input 
                                    className="cd-search-input" 
                                    style={{ width: '100%', padding: '8px 12px 8px 36px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, outline: 'none' }} 
                                    placeholder="Search trips, locations..." 
                                    value={search} 
                                    onChange={e => { setSearch(e.target.value); setPage(1); }} 
                                />
                            </div>
                            <select 
                                value={tripTypeFilter} 
                                onChange={e => { setTripTypeFilter(e.target.value); setPage(1); }}
                                style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, fontWeight: 600, color: '#334155', outline: 'none', background: 'white' }}
                            >
                                <option value="All">All Trip Types</option>
                                <option value="Login">Login Only</option>
                                <option value="Logout">Logout Only</option>
                                <option value="Adhoc">Adhoc / On-Demand</option>
                            </select>
                            <input 
                                type="date" 
                                value={dateFilter} 
                                onChange={e => { setDateFilter(e.target.value); setPage(1); }}
                                style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, fontWeight: 600, color: '#334155', outline: 'none', background: 'white' }}
                            />
                            {dateFilter && (
                                <button onClick={() => { setDateFilter(''); setPage(1); }} style={{ padding: '8px 12px', background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12, fontWeight: 700, color: '#64748b', cursor: 'pointer' }}>
                                    Clear Date
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Table */}
                {loading ? (
                    <div className="cd-empty" style={{ padding: '60px 24px' }}>Loading Trip Data...</div>
                ) : filtered.length === 0 ? (
                    <div className="cd-empty" style={{ padding: '60px 24px' }}>No trips found for this filter.</div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 960 }}>
                            <thead>
                                <tr style={{ background: '#f8fafc' }}>
                                    {['Trip Type', 'Passenger', 'Date & Time', 'Route', 'Driver', 'Fleet / Vehicle', 'Status', 'Actions'].map(h => (
                                        <th key={h} style={{ padding: '12px 16px', textAlign: h === 'Actions' ? 'right' : 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {pageData.map((t, i) => {
                                    const tt = tripTypeStyle(t.tripType);
                                    const ss = statusStyle(t.status);
                                    const driver = t.driverId;
                                    const vehicle = t.vehicleId;
                                    
                                    const tripAllocations = allocations.filter(a => (a.tripId?._id || a.tripId) === t._id);
                                    const maleCount = tripAllocations.filter(a => a.passengerId?.gender === 'Male').length;
                                    const femaleCount = tripAllocations.filter(a => a.passengerId?.gender === 'Female').length;
                                    const startTimeHour = t.startTime ? parseInt(t.startTime.split(':')[0]) : -1;
                                    const isNightTrip = startTimeHour >= 18 || startTimeHour < 5;
                                    const safetyRisk = isNightTrip && femaleCount > 0;

                                    return (
                                        <tr key={t._id}
                                            style={{ borderBottom: '1px solid #f1f5f9', background: safetyRisk ? '#fce7f3' : (i % 2 === 0 ? '#fff' : '#fafbfc'), cursor: 'default', position: 'relative' }}
                                            onMouseEnter={e => e.currentTarget.style.background = safetyRisk ? '#fbcfe8' : '#f0f4ff'}
                                            onMouseLeave={e => e.currentTarget.style.background = safetyRisk ? '#fce7f3' : (i % 2 === 0 ? '#fff' : '#fafbfc')}>

                                            {/* Trip Type */}
                                            <td style={{ padding: '14px 16px', whiteSpace: 'nowrap', position: 'relative', overflow: 'hidden' }}>
                                                {safetyRisk && (
                                                    <div style={{
                                                        position: 'absolute', top: 0, left: 0, width: 45, height: 45,
                                                        overflow: 'hidden', zIndex: 2, pointerEvents: 'none'
                                                    }}>
                                                        <div style={{
                                                            position: 'absolute', top: 6, left: -14, width: 60,
                                                            background: '#db2777', color: 'white', fontSize: 7, fontWeight: 900,
                                                            textAlign: 'center', transform: 'rotate(-45deg)',
                                                            boxShadow: '0 2px 5px rgba(0,0,0,0.2)', textTransform: 'uppercase',
                                                            letterSpacing: '0.05em', padding: '1px 0'
                                                        }}>
                                                            SAFETY
                                                        </div>
                                                    </div>
                                                )}
                                                <div style={{ paddingLeft: safetyRisk ? 12 : 0 }}>
                                                    <span style={{ display: 'inline-block', padding: '4px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700, background: tt.bg, color: tt.color }}>{tt.label}</span>
                                                    {t.tripFrequency && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3, fontWeight: 600 }}>{t.tripFrequency}</div>}
                                                </div>
                                            </td>

                                            {/* Passenger */}
                                            <td style={{ padding: '14px 16px' }}>
                                                <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b' }}>
                                                    {t.passengerName ? (
                                                        t.passengerName
                                                    ) : (
                                                        <a
                                                            href="#view-passengers"
                                                            onClick={(e) => { e.preventDefault(); onManageAllocations(t); }}
                                                            style={{ color: '#00B2A9', textDecoration: 'underline', textDecorationStyle: 'dotted', cursor: 'pointer', fontWeight: 800 }}
                                                        >
                                                            Multiple Passengers
                                                        </a>
                                                    )}
                                                </div>
                                                {t.passengerPhone && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{t.passengerPhone}</div>}
                                                {(maleCount > 0 || femaleCount > 0) && (
                                                    <div style={{ fontSize: 10, color: '#94a3b8', display: 'flex', gap: 8, marginTop: 4, fontWeight: 700 }}>
                                                        {maleCount > 0 && <span style={{ color: '#0284c7' }}>M: {maleCount}</span>}
                                                        {femaleCount > 0 && <span style={{ color: '#db2777' }}>F: {femaleCount}</span>}
                                                    </div>
                                                )}
                                            </td>

                                            {/* Date & Time */}
                                            <td style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>
                                                <div style={{ fontWeight: 700, fontSize: 13, color: '#00B2A9' }}>
                                                    {t.tripFrequency === 'Recurring' && t.recurringDays?.length > 0 ? (
                                                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                                            {t.recurringDays.map(d => (
                                                                <span key={d} style={{ background: '#eef2ff', color: '#16539C', padding: '2px 6px', borderRadius: 6, fontSize: 10, border: '1px solid #c3dcf5' }}>{d.slice(0, 3).toUpperCase()}</span>
                                                            ))}
                                                        </div>
                                                    ) : t.tripDate ? new Date(t.tripDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                                </div>
                                                {t.tripFrequency === 'Recurring' && (t.startDate || t.endDate) && (
                                                    <div style={{ fontSize: 10, color: '#64748b', marginTop: 4, fontWeight: 700 }}>
                                                        {t.startDate ? new Date(t.startDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'} to {t.endDate ? new Date(t.endDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                                    </div>
                                                )}
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#94a3b8', marginTop: 4, fontWeight: 600 }}>
                                                    {(t.pickupTime || t.startTime) && <Icon d={icons.clock} size={11} color="#cbd5e1" />}
                                                    <span>
                                                        {t.pickupTime || t.startTime || ''}
                                                        {t.endTime ? ` – ${t.endTime}` : ''}
                                                    </span>
                                                </div>
                                            </td>

                                            {/* Route */}
                                            <td style={{ padding: '14px 16px', maxWidth: 200 }}>
                                                <div style={{ fontSize: 11, fontWeight: 600, color: '#334155', lineHeight: 1.5 }}>
                                                    <span style={{ color: '#059669', fontWeight: 700 }}>FROM </span>{t.pickupLocation || '—'}
                                                </div>
                                                <div style={{ fontSize: 11, fontWeight: 600, color: '#334155', lineHeight: 1.5, marginTop: 3 }}>
                                                    <span style={{ color: '#e11d48', fontWeight: 700 }}>TO </span>{t.dropLocation || '—'}
                                                </div>
                                            </td>

                                            {/* Driver */}
                                            <td style={{ padding: '14px 16px' }}>
                                                {driver ? (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <div style={{ width: 30, height: 30, borderRadius: 8, background: '#e6f0fa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: '#16539C', flexShrink: 0 }}>
                                                            {(driver.name || '?').charAt(0).toUpperCase()}
                                                        </div>
                                                        <div>
                                                            <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b' }}>{driver.name || '—'} ({driver.driverCode || driver.employeeId || driver._id?.slice(-6).toUpperCase()})</div>
                                                            {driver.phoneNumber && <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>{driver.phoneNumber}</div>}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>Pending dispatch</div>
                                                )}
                                            </td>

                                            {/* Fleet / Vehicle */}
                                            <td style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>
                                                {vehicle ? (
                                                    <div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, fontSize: 13, color: '#1e293b' }}>
                                                            <Icon d={icons.trips} size={14} color="#64748b" />
                                                            {vehicle.plateNumber || vehicle.registrationNumber || '—'} ({vehicle.vehicleCode || vehicle._id?.slice(-6).toUpperCase()})
                                                        </div>
                                                        {(vehicle.make || vehicle.model) && (
                                                            <div style={{ fontSize: 11, color: '#00B2A9', marginTop: 4, fontWeight: 600, paddingLeft: 20 }}>
                                                                {[vehicle.make, vehicle.model].filter(Boolean).join(' ')}{vehicle.year ? ` (${vehicle.year})` : ''}
                                                            </div>
                                                        )}
                                                        {vehicle.vehicleType && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2, paddingLeft: 20 }}>{vehicle.vehicleType}</div>}
                                                    </div>
                                                ) : (
                                                    <div style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>TBA</div>
                                                )}
                                            </td>

                                            {/* Status */}
                                            <td style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>
                                                <span style={{ display: 'inline-block', padding: '5px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: ss.bg, color: ss.color }}>
                                                    {statusLabel(t)}
                                                </span>
                                            </td>

                                            {/* Actions */}
                                            <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                                                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                                    {t.tripFrequency === 'Recurring' && (
                                                        <button 
                                                            onClick={() => onViewHistory(t._id)}
                                                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: '#f8fafc', color: '#0f172a', border: '1.5px solid #e2e8f0', borderRadius: 10, fontSize: 11.5, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s' }}
                                                        >
                                                            <Icon d={icons.calendar} size={13} color="#475569" /> Instances
                                                        </button>
                                                    )}
                                                    {(t.status === 'Ongoing' || t.status === 'In Progress') && (
                                                        <button 
                                                            onClick={() => onNavigate('trip-tracking', t._id)}
                                                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: 'linear-gradient(135deg, #1d4ed8, #3b82f6)', color: 'white', border: 'none', borderRadius: 10, fontSize: 11.5, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s' }}
                                                        >
                                                            <Icon d={icons.map} size={13} color="white" /> Track
                                                        </button>
                                                    )}
                                                    {t.status === 'Completed' && (
                                                        <button 
                                                            onClick={() => downloadTripCSV(t)}
                                                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: 'linear-gradient(135deg, #059669, #10b981)', color: 'white', border: 'none', borderRadius: 10, fontSize: 11.5, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s' }}
                                                        >
                                                            <Icon d={icons.download} size={13} color="white" /> Trip Sheet
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination */}
                {!loading && totalPages > 1 && (
                    <div style={{ padding: '14px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>Page {page} of {totalPages} · {filtered.length} records</div>
                        <div style={{ display: 'flex', gap: 6 }}>
                            <button disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))}
                                style={{ padding: '6px 14px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: page === 1 ? '#f8fafc' : 'white', color: page === 1 ? '#cbd5e1' : '#334155', fontWeight: 700, cursor: page === 1 ? 'default' : 'pointer', fontSize: 13 }}>
                                ← Prev
                            </button>
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                const start = Math.max(1, Math.min(page - 2, totalPages - 4));
                                const p = start + i;
                                return p <= totalPages ? (
                                    <button key={p} onClick={() => setPage(p)}
                                        style={{ padding: '6px 12px', borderRadius: 8, border: '1.5px solid', borderColor: p === page ? '#0f172a' : '#e2e8f0', background: p === page ? '#0f172a' : 'white', color: p === page ? 'white' : '#334155', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                                        {p}
                                    </button>
                                ) : null;
                            })}
                            <button disabled={page === totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                style={{ padding: '6px 14px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: page === totalPages ? '#f8fafc' : 'white', color: page === totalPages ? '#cbd5e1' : '#334155', fontWeight: 700, cursor: page === totalPages ? 'default' : 'pointer', fontSize: 13 }}>
                                Next →
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Suggest / Counter-offer Modal */}
            {suggestModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
                    <div style={{ background: '#fff', padding: '32px', borderRadius: '24px', width: '440px', boxShadow: '0 25px 50px rgba(0,0,0,0.25)' }}>
                        <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: 800, color: '#063A5C' }}>💬 Send a Suggestion</h3>
                        <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#64748b' }}>Share your counter-offer or notes. The operator will review and respond.</p>
                        <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                            <div style={{ fontSize: '12px', color: '#64748b' }}>Operator's Proposed Price:</div>
                            <div style={{ fontSize: '20px', fontWeight: 900, color: '#5b21b6' }}>{suggestModal.proposedPrice}</div>
                            {suggestModal.operatorNotes && <div style={{ fontSize: '12px', color: '#7c3aed', fontStyle: 'italic', marginTop: 4 }}>Note: "{suggestModal.operatorNotes}"</div>}
                        </div>
                        <textarea rows={4}
                            style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #e2e8f0', fontSize: '13px', lineHeight: 1.6, resize: 'vertical', outline: 'none', boxSizing: 'border-box' }}
                            placeholder="e.g. Can we do 150 instead? We have 3 passengers..."
                            value={clientNotes} onChange={e => setClientNotes(e.target.value)} />
                        <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
                            <button onClick={() => { setSuggestModal(null); setClientNotes(''); }} style={{ flex: 1, padding: '12px', borderRadius: '12px', border: '1px solid #e2e8f0', background: '#fff', fontWeight: 600, cursor: 'pointer', color: '#6b7280' }}>Cancel</button>
                            <button onClick={() => handleClientResponse(suggestModal, 'ClientSuggested', clientNotes)}
                                disabled={responding || !clientNotes.trim()}
                                style={{ flex: 2, padding: '12px', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '14px', opacity: clientNotes.trim() ? 1 : 0.5 }}>
                                {responding ? 'Sending...' : '📨 Send Suggestion'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Passenger Manifest Modal */}
            {viewingPassengersTrip && (
                <ModalOverlay
                    onClose={() => setViewingPassengersTrip(null)}
                    title="Passenger Manifest"
                >
                    <div style={{ marginBottom: 20 }}>
                        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>Trip Details:</div>
                        <div style={{ fontWeight: 700, color: '#1e293b' }}>
                            {viewingPassengersTrip.pickupLocation} → {viewingPassengersTrip.dropLocation}
                        </div>
                        <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                            {viewingPassengersTrip.tripDate ? new Date(viewingPassengersTrip.tripDate).toLocaleDateString() : 'Recurring'} | {viewingPassengersTrip.pickupTime || viewingPassengersTrip.startTime}
                        </div>
                    </div>

                    <div style={{ background: '#f8fafc', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: '#f1f5f9' }}>
                                    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Passenger</th>
                                    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Contact</th>
                                    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Pickup Location</th>
                                    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Drop Location</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loadingTripAllocs ? (
                                    <tr>
                                        <td colSpan="4" style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>Loading passenger manifest...</td>
                                    </tr>
                                ) : tripAllocations.length === 0 ? (
                                    <tr>
                                        <td colSpan="4" style={{ padding: '30px', textAlign: 'center', color: '#94a3b8', fontStyle: 'italic' }}>
                                            No passengers allocated to this trip manifest yet.
                                        </td>
                                    </tr>
                                ) : tripAllocations.map((a, idx) => (
                                    <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                        <td style={{ padding: '12px 16px' }}>
                                            <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b' }}>
                                                {a.passengerId?.name || (a.passengerId?.firstName ? `${a.passengerId.firstName} ${a.passengerId.lastName || ''}` : 'Unknown')}
                                            </div>
                                            <div style={{ fontSize: 11, color: '#94a3b8' }}>{a.passengerId?.employeeId || 'No ID'}</div>
                                        </td>
                                        <td style={{ padding: '12px 16px' }}>
                                            <div style={{ fontSize: 12, color: '#334155' }}>{a.passengerId?.contactNumber || a.passengerId?.primaryPhoneNumber || '—'}</div>
                                        </td>
                                        <td style={{ padding: '12px 16px' }}>
                                            <div style={{ fontSize: 11, color: '#059669', fontWeight: 700 }}>PICKUP</div>
                                            <div style={{ fontSize: 12, color: '#334155', fontWeight: 600 }}>
                                                {a.pickupStopId?.name
                                                    || (viewingPassengersTrip.pickupLocation)
                                                    || '—'}
                                            </div>
                                        </td>
                                        <td style={{ padding: '12px 16px' }}>
                                            <div style={{ fontSize: 11, color: '#e11d48', fontWeight: 700 }}>DROP</div>
                                            <div style={{ fontSize: 12, color: '#334155', fontWeight: 600 }}>
                                                {a.dropStopId?.name
                                                    || (viewingPassengersTrip.dropLocation)
                                                    || '—'}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </ModalOverlay>
            )}

            {/* Adhoc Trip Passenger Modal */}
            {viewAdhocPassengerTrip && (
                <ModalOverlay onClose={() => setViewAdhocPassengerTrip(null)} title="Adhoc Passenger Details">
                    <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>Name</div>
                            <div style={{ fontSize: 16, fontWeight: 600, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Icon d={icons.user} size={16} color="#00B2A9" />
                                {viewAdhocPassengerTrip.passengerName || 'N/A'}
                            </div>
                        </div>
                        <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>Phone Number</div>
                            <div style={{ fontSize: 16, fontWeight: 600, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Icon d={icons.phone} size={16} color="#10b981" />
                                {viewAdhocPassengerTrip.passengerPhone || 'N/A'}
                            </div>
                        </div>
                        <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>Email Address</div>
                            <div style={{ fontSize: 16, fontWeight: 600, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Icon d={icons.mail} size={16} color="#d946ef" />
                                {viewAdhocPassengerTrip.passengerEmail || 'N/A'}
                            </div>
                        </div>
                    </div>
                </ModalOverlay>
            )}
        </div>
    );
}

function UsersSection({ client, users, loading, onRefresh }) {
    const [editUser, setEditUser] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [activeTab, setActiveTab] = useState('account');
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ name: '', email: '', password: '', role: 'Transport Manager', status: 'Active', permissions: ['trips', 'routes', 'passengers'] });

    useEffect(() => {
        if (editUser) setForm({ ...editUser, password: '' });
        else setForm({ name: '', email: '', password: '', role: 'Transport Manager', status: 'Active' });
        setActiveTab('account');
    }, [editUser, showForm]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!editUser && users.length >= 2) {
            alert("Maximum limit of 2 users reached. Please remove an existing user to add a new one.");
            return;
        }
        setSaving(true);
        try {
            const url = editUser ? `${API}/client-users/${editUser._id}` : `${API}/client-users`;
            const method = editUser ? 'PUT' : 'POST';
            const payload = editUser ? form : { ...form, clientId: client._id, operatorId: client.operatorId };
            await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            setShowForm(false); setEditUser(null); onRefresh();
        } catch (err) { alert(err.message); }
        finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Remove this user's access?")) return;
        await fetch(`${API}/client-users/${id}`, { method: 'DELETE' });
        onRefresh();
    };

    /* ── Full-page form view ── */
    if (showForm) {
        return (
            <div style={{ background: '#f8fafc', minHeight: '100vh', fontFamily: "'Inter', sans-serif" }}>
                <style>{`
                    @keyframes ufSlide { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:none; } }
                    .uf-page { display:flex; flex-direction:column; min-height:100vh; animation:ufSlide 0.3s ease; }
                    .uf-topbar { display:flex; align-items:center; justify-content:space-between; padding:16px 32px; background:#fff; border-bottom:1px solid #e2e8f0; position:sticky; top:0; z-index:50; }
                    .uf-back-btn { display:inline-flex; align-items:center; gap:8px; border:1.5px solid #e2e8f0; background:#fff; border-radius:10px; padding:9px 18px; font-size:13px; font-weight:700; color:#475569; cursor:pointer; transition:all 0.2s; }
                    .uf-back-btn:hover { border-color:#0f172a; color:#0f172a; }
                    .uf-topbar-title { font-size:18px; font-weight:900; color:#0f172a; }
                    .uf-save-btn { display:inline-flex; align-items:center; gap:8px; border:none; background:linear-gradient(135deg,#0f172a,#063A5C); color:#fff; border-radius:12px; padding:11px 28px; font-size:13px; font-weight:800; cursor:pointer; transition:all 0.25s; box-shadow:0 6px 18px -4px rgba(15,23,42,0.3); }
                    .uf-save-btn:hover { transform:translateY(-2px); box-shadow:0 10px 24px -4px rgba(15,23,42,0.4); }
                    .uf-save-btn:disabled { opacity:0.6; cursor:not-allowed; transform:none; }
                    .uf-body { display:flex; flex:1; gap:0; }
                    .uf-sidebar { width:240px; flex-shrink:0; background:#fff; border-right:1px solid #e2e8f0; padding:28px 0; }
                    .uf-sidebar-header { padding:0 24px 20px; border-bottom:1px solid #f1f5f9; margin-bottom:12px; }
                    .uf-sidebar-header p { font-size:11px; font-weight:700; color:#94a3b8; text-transform:uppercase; letter-spacing:0.07em; margin:0 0 6px; }
                    .uf-sidebar-header h3 { font-size:15px; font-weight:800; color:#0f172a; margin:0; }
                    .uf-nav-item { display:flex; align-items:center; gap:12px; padding:12px 24px; font-size:13px; font-weight:700; color:#64748b; cursor:pointer; border-left:3px solid transparent; transition:all 0.15s; }
                    .uf-nav-item.active { color:#0f172a; border-left-color:#0f172a; background:#f8fafc; }
                    .uf-nav-item svg { flex-shrink:0; }
                    .uf-content { flex:1; padding:36px 40px; overflow-y:auto; }
                    .uf-section { background:#fff; border-radius:16px; border:1px solid #e2e8f0; box-shadow:0 2px 8px rgba(0,0,0,0.04); overflow:hidden; margin-bottom:24px; }
                    .uf-section-header { padding:22px 28px; border-bottom:1px solid #f1f5f9; }
                    .uf-section-title { font-size:16px; font-weight:800; color:#0f172a; margin:0 0 3px; }
                    .uf-section-sub { font-size:13px; color:#94a3b8; font-weight:500; margin:0; }
                    .uf-section-body { padding:28px; display:flex; flex-direction:column; gap:22px; }
                    .uf-grid { display:grid; grid-template-columns:1fr 1fr; gap:20px; }
                    .uf-field { display:flex; flex-direction:column; gap:8px; }
                    .uf-field.full { grid-column:span 2; }
                    .uf-label { font-size:11px; font-weight:800; color:#64748b; text-transform:uppercase; letter-spacing:0.07em; display:flex; align-items:center; gap:5px; }
                    .uf-label .req { color:#f43f5e; }
                    .uf-iw { position:relative; }
                    .uf-ico { position:absolute; left:14px; top:50%; transform:translateY(-50%); color:#94a3b8; pointer-events:none; }
                    .uf-input { width:100%; padding:13px 14px 13px 44px; border:1.5px solid #e2e8f0; border-radius:12px; font-size:14px; font-weight:600; color:#0f172a; background:#f8fafc; outline:none; transition:all 0.2s; font-family:inherit; box-sizing:border-box; }
                    .uf-input:focus { border-color:#0f172a; background:#fff; box-shadow:0 0 0 4px rgba(15,23,42,0.07); }
                    .uf-input::placeholder { color:#cbd5e1; font-weight:400; }
                    .uf-role-grid { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
                    .uf-role-card { border:2px solid #e2e8f0; border-radius:14px; padding:20px; cursor:pointer; transition:all 0.2s; background:#f8fafc; }
                    .uf-role-card:hover { border-color:#a5b4fc; background:#eef2ff; }
                    .uf-role-card.sel { border-color:#0f172a; background:linear-gradient(135deg,#f8fafc,#f1f5f9); }
                    .uf-role-dot { width:18px; height:18px; border-radius:50%; border:2px solid #cbd5e1; margin-bottom:14px; transition:all 0.2s; background:#fff; }
                    .uf-role-card.sel .uf-role-dot { background:#0f172a; border-color:#0f172a; box-shadow:0 0 0 3px rgba(15,23,42,0.12); }
                    .uf-role-name { font-size:14px; font-weight:800; color:#1e293b; margin-bottom:4px; }
                    .uf-role-card.sel .uf-role-name { color:#0f172a; }
                    .uf-role-desc { font-size:12px; color:#94a3b8; font-weight:500; line-height:1.5; }
                    .uf-status-row { display:flex; gap:14px; }
                    .uf-status-btn { flex:1; padding:14px; border-radius:12px; border:2px solid #e2e8f0; background:#f8fafc; cursor:pointer; font-size:13px; font-weight:700; color:#64748b; transition:all 0.2s; display:flex; align-items:center; justify-content:center; gap:10px; }
                    .uf-status-btn.active-sel { border-color:#10b981; background:#ecfdf5; color:#065f46; }
                    .uf-status-btn.inactive-sel { border-color:#ef4444; background:#fef2f2; color:#991b1b; }
                `}</style>

                <div className="uf-page">
                    {/* Top bar */}
                    <div className="uf-topbar">
                        <button className="uf-back-btn" onClick={() => { setShowForm(false); setEditUser(null); }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
                            Back to Users
                        </button>
                        <div className="uf-topbar-title">{editUser ? 'Edit User Account' : 'New User Access'}</div>
                        <button className="uf-save-btn" form="user-form" type="submit" disabled={saving}>
                            {saving ? (
                                <>
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                    {editUser ? 'Save Changes' : 'Grant Access'}
                                </>
                            )}
                        </button>
                    </div>

                    <div className="uf-body">
                        {/* Left sidebar nav */}
                        <div className="uf-sidebar">
                            <div className="uf-sidebar-header">
                                <p>USER PROFILE</p>
                                <h3>{form.name || (editUser ? 'Edit User' : 'New User')}</h3>
                            </div>
                            <div className={`uf-nav-item${activeTab === 'account' ? ' active' : ''}`} onClick={() => setActiveTab('account')}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                                Account Details
                            </div>
                            <div className={`uf-nav-item${activeTab === 'permissions' ? ' active' : ''}`} onClick={() => setActiveTab('permissions')}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                                Permissions
                            </div>

                        </div>

                        {/* Main content */}
                        <div className="uf-content">
                            <form id="user-form" onSubmit={handleSubmit}>
                                {/* Account Details tab */}
                                {activeTab === 'account' && (
                                <div className="uf-section">
                                    <div className="uf-section-header">
                                        <p className="uf-section-title">Basic Information</p>
                                        <p className="uf-section-sub">Primary account credentials for this user.</p>
                                    </div>
                                    <div className="uf-section-body">
                                        <div className="uf-grid">
                                            <div className="uf-field full">
                                                <label className="uf-label">Full Name <span className="req">*</span></label>
                                                <div className="uf-iw">
                                                    <svg className="uf-ico" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                                                    <input className="uf-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="e.g. John Doe" />
                                                </div>
                                            </div>
                                            <div className="uf-field">
                                                <label className="uf-label">Email Address <span className="req">*</span></label>
                                                <div className="uf-iw">
                                                    <svg className="uf-ico" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                                                    <input type="email" className="uf-input" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required placeholder="name@company.com" />
                                                </div>
                                            </div>
                                            <div className="uf-field">
                                                <label className="uf-label">Account Password {!editUser && <span className="req">*</span>}</label>
                                                <div className="uf-iw">
                                                    <svg className="uf-ico" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                                                    <input type="text" className="uf-input" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required={!editUser} placeholder={editUser ? 'Leave blank to keep current' : 'Set access password'} />
                                                </div>
                                            </div>
                                            <div className="uf-field full">
                                                <label className="uf-label">Account Status</label>
                                                <div className="uf-status-row" style={{ gap: 12 }}>
                                                    <button type="button" className={`uf-status-btn${form.status === 'Active' ? ' active-sel' : ''}`} onClick={() => setForm({ ...form, status: 'Active' })}>
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                                        Active
                                                    </button>
                                                    <button type="button" className={`uf-status-btn${form.status === 'Inactive' ? ' inactive-sel' : ''}`} onClick={() => setForm({ ...form, status: 'Inactive' })}>
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                                                        Inactive
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                )}

                                {/* Permissions tab */}
                                {activeTab === 'permissions' && (
                                <div className="uf-section">
                                    <div className="uf-section-header">
                                        <p className="uf-section-title">Role / Designation</p>
                                        <p className="uf-section-sub">Select the level of access and responsibility for this user.</p>
                                    </div>
                                    <div className="uf-section-body">
                                        <div className="uf-role-grid">
                                            {[
                                                { value: 'Transport Manager', desc: 'Manages transport operations, schedules, routes and fleet usage on a day-to-day basis.' },
                                                { value: 'Head of Business', desc: 'Full oversight of all business activities, financials, and administrative decisions.' }
                                            ].map(r => (
                                                <div key={r.value} className={`uf-role-card${form.role === r.value ? ' sel' : ''}`} onClick={() => setForm({ ...form, role: r.value, permissions: r.value === 'Head of Business' ? ['analytics', 'profile', 'passengers', 'routes', 'request', 'trips', 'support', 'finance', 'reports', 'users'] : ['trips', 'routes', 'passengers', 'request', 'support'] })}>
                                                    <div className="uf-role-dot" />
                                                    <div className="uf-role-name">{r.value}</div>
                                                    <div className="uf-role-desc">{r.desc}</div>
                                                </div>
                                            ))}
                                        </div>
                                        <div style={{ marginTop: 24, padding: '20px', background: '#f8fafc', border: '1.5px dashed #e2e8f0', borderRadius: 12 }}>
                                            <p style={{ margin: '0 0 16px', fontSize: 13, fontWeight: 800, color: '#0f172a' }}>Custom Access Permissions</p>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                                {[
                                                    { id: 'analytics', label: 'Analytics Dashboard' },
                                                    { id: 'profile', label: 'Company Profile' },
                                                    { id: 'passengers', label: 'Manage Passengers' },
                                                    { id: 'routes', label: 'Available Routes' },
                                                    { id: 'request', label: 'Request Service' },
                                                    { id: 'trips', label: 'Trip Management' },
                                                    { id: 'support', label: 'Support Tickets' },
                                                    { id: 'finance', label: 'Finance (Invoices, Contracts)' },
                                                    { id: 'reports', label: 'Reports' },
                                                    { id: 'users', label: 'Access & User Management' }
                                                ].map(perm => (
                                                    <label key={perm.id} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                                                        <input 
                                                            type="checkbox" 
                                                            style={{ width: 16, height: 16, accentColor: 'var(--cd-primary)', cursor: 'pointer' }}
                                                            checked={form.permissions?.includes(perm.id)}
                                                            onChange={(e) => {
                                                                const newPerms = e.target.checked 
                                                                    ? [...(form.permissions || []), perm.id]
                                                                    : (form.permissions || []).filter(p => p !== perm.id);
                                                                setForm({ ...form, permissions: newPerms });
                                                            }}
                                                        />
                                                        <span style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>{perm.label}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                )}

                            </form>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    /* ── Table / list view ── */
    return (
        <div className="cd-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <div className="cd-card-title">Authorized Dashboard Users {users.length >= 2 && <span style={{ fontSize: 12, color: '#f59e0b', marginLeft: 10, fontWeight: 600 }}>(Limit Reached: 2 Users Max)</span>}</div>
                {users.length < 2 && (
                    <button onClick={() => { setEditUser(null); setShowForm(true); }} className="cd-add-btn">
                        <Icon d={icons.plus} size={18} /> Add New User
                    </button>
                )}
            </div>

            {loading ? <div className="cd-empty">Loading Users...</div> : users.length === 0 ? <div className="cd-empty">No additional users managed for this account.</div> : (
                <div style={{ overflowX: 'auto' }}>
                    <table className="cd-table">
                        <thead>
                            <tr>
                                <th className="cd-th">User Information</th>
                                <th className="cd-th">Assigned Role</th>
                                <th className="cd-th">Account Status</th>
                                <th className="cd-th">Created On</th>
                                <th className="cd-th">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(u => (
                                <tr key={u._id} className="cd-tr">
                                    <td className="cd-td">
                                        <div style={{ fontSize: 14, fontWeight: 800, color: '#1e293b' }}>{u.name}</div>
                                        <div style={{ fontSize: 11, color: '#64748b' }}>{u.email}</div>
                                    </td>
                                    <td className="cd-td">
                                        <div style={{ fontSize: 12, fontWeight: 700, color: '#00B2A9', background: '#eef2ff', padding: '4px 10px', borderRadius: 20, display: 'inline-block' }}>
                                            {u.role}
                                        </div>
                                    </td>
                                    <td className="cd-td">
                                        <span className="cd-badge" style={{ background: u.status === 'Active' ? '#dcfce7' : '#fee2e2', color: u.status === 'Active' ? '#166534' : '#991b1b' }}>
                                            {u.status}
                                        </span>
                                    </td>
                                    <td className="cd-td">{new Date(u.createdAt).toLocaleDateString()}</td>
                                    <td className="cd-td">
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            <button onClick={() => { setEditUser(u); setShowForm(true); }} className="cd-icon-btn"><Icon d={icons.analytics} size={14} /></button>
                                            <button onClick={() => handleDelete(u._id)} className="cd-icon-btn" style={{ color: '#ef4444' }}><Icon d={icons.logout} size={14} /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}



function DocumentView({ type, data, client, onClose, country }) {
    const printDoc = () => window.print();
    const isInv = type === 'invoice';
    const num = isInv ? data.invoiceNumber : data.quotationNumber;
    const date = new Date(isInv ? (data.issueDate || data.date) : data.issueDate).toLocaleDateString('en-GB');
    const dueDate = isInv && data.dueDate ? new Date(data.dueDate).toLocaleDateString('en-GB') : '';
    const [activeTab, setActiveTab] = useState(isInv ? 'Invoice' : 'Quotation');

    return (
        <div style={{ background: 'white', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <div className="no-print" style={{ padding: '12px 24px', background: 'white', borderBottom: '1px solid #eef2f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 100 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                    <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#64748b', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                        <Icon d={icons.chevronDown} size={16} style={{ transform: 'rotate(90deg)' }} /> Back
                    </button>
                    <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid transparent' }}>
                        {[(isInv ? 'Invoice' : 'Quotation'), 'Activity Log'].map(t => (
                            <button key={t} onClick={() => setActiveTab(t)} style={{ 
                                padding: '12px 24px', border: 'none', background: 'transparent', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                                borderBottom: activeTab === t ? '2px solid #0f172a' : '2px solid transparent',
                                color: activeTab === t ? '#0f172a' : '#94a3b8'
                            }}>{t}</button>
                        ))}
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                    <button onClick={printDoc} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 16px', fontSize: 12, fontWeight: 700, color: '#334155', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                        <Icon d={icons.analytics} size={14} /> Edit
                    </button>
                    <button onClick={printDoc} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 16px', fontSize: 12, fontWeight: 700, color: '#334155', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                        <Icon d={icons.analytics} size={14} /> Print
                    </button>
                    <button onClick={printDoc} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 16px', fontSize: 12, fontWeight: 700, color: '#334155', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                        <Icon d={icons.analytics} size={14} /> Download PDF
                    </button>
                    <button style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 16px', fontSize: 12, fontWeight: 700, color: '#334155', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                        More <Icon d={icons.chevronDown} size={12} />
                    </button>
                </div>
            </div>

            <div style={{ flex: 1, padding: '60px 24px', background: '#f8fafc', display: 'flex', justifyContent: 'center' }}>
                <div id="printable-doc" style={{ background: 'white', width: '210mm', minHeight: '297mm', padding: '80px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', boxSizing: 'border-box', borderRadius: 4 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 60 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{ width: 44, height: 44, background: '#10b981', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, color: 'white', fontSize: 26 }}>G</div>
                            <div style={{ fontSize: 32, fontWeight: 900, color: '#0f172a', letterSpacing: '-1.5px' }}>GoRidez</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: 12, color: '#64748b', fontWeight: 700, marginBottom: 4 }}>Date: {date}</div>
                            <div style={{ fontSize: 12, color: '#64748b', fontWeight: 700 }}>Ref: {num}</div>
                        </div>
                    </div>

                    <div style={{ marginBottom: 50 }}>
                        <div style={{ fontSize: 11, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 10, letterSpacing: '0.05em' }}>To</div>
                        <div style={{ fontSize: 16, fontWeight: 900, color: '#0f172a', marginBottom: 4 }}>M/S: {client.companyName}</div>
                        <div style={{ fontSize: 13, color: '#64748b', fontWeight: 500, lineHeight: 1.5 }}>
                            {client.address || 'Kuwait City, Kuwait'}<br />
                            Attr: {client.contactPerson || 'Fakhruddin Lathiwaia'}<br />
                            Tenure: 1 Year
                        </div>
                    </div>

                    <div style={{ padding: '20px 0', borderTop: '2px solid #00B2A9', marginBottom: 40 }}>
                        <div style={{ fontSize: 14, fontWeight: 900, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{ width: 4, height: 20, background: 'var(--cd-accent)' }}></div>
                            SUBJECT: <span style={{ textTransform: 'uppercase' }}>{isInv ? 'Staff Transportation Charges' : 'Transportation Services Proposal'} - {new Date().toLocaleString('default', { month: 'long' })} {new Date().getFullYear()}</span>
                        </div>
                    </div>

                    <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.6, marginBottom: 32 }}>
                        GoRidez Co. by Auto Capital Co., Al-Rai, Kuwait, would like to express to you their best wishes, and it is a pleasure to offer you our best services. Kindly find below our best offer as per your kind request.
                    </p>

                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
                        <thead>
                            <tr style={{ background: '#f8fafc' }}>
                                <th style={{ textAlign: 'left', padding: '14px 12px', fontSize: 10, fontWeight: 800, color: '#475569', borderBottom: '1.5px solid #e2e8f0', width: 40 }}>S.NO</th>
                                <th style={{ textAlign: 'left', padding: '14px 12px', fontSize: 10, fontWeight: 800, color: '#475569', borderBottom: '1.5px solid #e2e8f0' }}>{isInv ? 'FLEET & ROUTE' : 'FLEET TYPE'}</th>
                                <th style={{ textAlign: 'left', padding: '14px 12px', fontSize: 10, fontWeight: 800, color: '#475569', borderBottom: '1.5px solid #e2e8f0' }}>FROM / TO</th>
                                <th style={{ textAlign: 'center', padding: '14px 12px', fontSize: 10, fontWeight: 800, color: '#475569', borderBottom: '1.5px solid #e2e8f0' }}>QTY/TRIPS</th>
                                <th style={{ textAlign: 'right', padding: '14px 12px', fontSize: 10, fontWeight: 800, color: '#475569', borderBottom: '1.5px solid #e2e8f0' }}>PRICE</th>
                                <th style={{ textAlign: 'right', padding: '14px 12px', fontSize: 10, fontWeight: 800, color: '#475569', borderBottom: '1.5px solid #e2e8f0' }}>TOTAL</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                                <td style={{ padding: '20px 12px', fontSize: 13, color: '#1e293b', fontWeight: 600 }}>1</td>
                                <td style={{ padding: '20px 12px', fontSize: 13, color: '#1e293b', fontWeight: 700 }}>7-Seater Renault Dokker</td>
                                <td style={{ padding: '20px 12px', fontSize: 12, color: '#64748b' }}>Various Locations</td>
                                <td style={{ padding: '20px 12px', fontSize: 13, color: '#1e293b', fontWeight: 700, textAlign: 'center' }}>1</td>
                                <td style={{ padding: '20px 12px', fontSize: 13, color: '#1e293b', fontWeight: 700, textAlign: 'right' }}>{Number(data.totalAmount).toLocaleString()}</td>
                                <td style={{ padding: '20px 12px', fontSize: 14, color: '#0f172a', fontWeight: 900, textAlign: 'right' }}>{Number(data.totalAmount).toLocaleString()}</td>
                            </tr>
                        </tbody>
                    </table>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', padding: '12px 20px', border: '1px solid #e2e8f0', borderRadius: 4 }}>
                        <div style={{ fontSize: 12, fontWeight: 800, color: '#475569', textTransform: 'uppercase' }}>Four Hundred Twenty Kuwaiti Dinars Only</div>
                        <div style={{ display: 'flex', gap: 40 }}>
                            <div style={{ fontSize: 12, fontWeight: 800, color: '#475569' }}>TOTAL</div>
                            <div style={{ fontSize: 16, fontWeight: 900, color: '#0f172a' }}>{Number(data.totalAmount).toLocaleString()}</div>
                        </div>
                    </div>

                    <div style={{ marginTop: 40 }}>
                        <div style={{ fontSize: 12, fontWeight: 800, color: '#1e293b', marginBottom: 12 }}>Details:</div>
                        <ul style={{ fontSize: 12, color: '#475569', paddingLeft: 20, lineHeight: 1.8 }}>
                            <li>Mobilization will be 10 working days from the date of signing the contract.</li>
                            <li>Vehicles covered with comprehensive insurance.</li>
                            <li>Quoted rate includes vehicle, regular preventive maintenance as provided, and offers options in pricing with replacement.</li>
                            <li>GPS tracking systems in all vehicles to enhance safety and service quality, in accordance with our company standards.</li>
                        </ul>
                    </div>

                    <div style={{ marginTop: 40 }}>
                        <div style={{ fontSize: 12, fontWeight: 800, color: '#1e293b', marginBottom: 12 }}>Terms & Conditions:</div>
                        <ul style={{ fontSize: 12, color: '#475569', paddingLeft: 20, lineHeight: 1.8 }}>
                            <li>The rate quoted is based on a full calendar month, irrespective of the actual number of working days in the month.</li>
                            <li>Payments - 15 days from the date of submission of the invoice.</li>
                            <li>Quotation validity: 7 days.</li>
                        </ul>
                    </div>

                    <div style={{ marginTop: 60, fontSize: 12, color: '#64748b' }}>
                        Best Regards,<br />
                        <span style={{ fontWeight: 800, color: '#1e293b' }}>Accounts Department</span><br />
                        GoRidez Kuwait
                    </div>
                </div>
            </div>
            <style>{`
                @media print {
                    .no-print, .cd-sidebar, .cd-header { display: none !important; }
                    body { background: white !important; }
                    .cd-layout { display: block !important; }
                    .cd-main { padding: 0 !important; margin: 0 !important; width: 100% !important; overflow: visible !important; }
                    .cd-modal-overlay { padding: 0 !important; background: white !important; backdrop-filter: none !important; position: absolute !important; inset: 0 !important; overflow: visible !important; }
                    .cd-modal-content { box-shadow: none !important; width: 100% !important; height: auto !important; margin: 0 !important; padding: 0 !important; overflow: visible !important; }
                    #printable-doc { box-shadow: none !important; margin: 0 !important; width: 100% !important; padding: 0 !important; }
                }
            `}</style>
        </div>
    );
}

function FinanceSection({ invoices, trips, routes, country, client, loading, onSetView }) {
    const [subTab, setSubTab] = useState('invoices');
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('All');

    const totalPaid = invoices.filter(i => i.status === 'Paid').reduce((sum, i) => sum + (Number(i.totalAmount) || 0), 0);
    const totalPending = invoices.filter(i => i.status !== 'Paid').reduce((sum, i) => sum + (Number(i.totalAmount) || 0), 0);
    const totalInvoiced = invoices.reduce((sum, i) => sum + (Number(i.totalAmount) || 0), 0);

    const serviceStats = routes.map(r => {
        const routeTrips = trips.filter(t => (t.routeId?._id || t.routeId) === r._id);
        const lastTrip = routeTrips.length > 0 ? routeTrips.sort((a, b) => new Date(b.tripDate) - new Date(a.tripDate))[0] : null;
        return {
            ...r,
            tripCount: routeTrips.length,
            lastTripDate: lastTrip ? lastTrip.tripDate : null,
            completedCount: routeTrips.filter(t => t.status === 'Completed').length
        };
    }).filter(s => s.tripCount > 0);

    const filteredInvoices = invoices.filter(inv => {
        const matchesSearch = (inv.invoiceNumber || '').toLowerCase().includes(search.toLowerCase());
        const matchesFilter = filter === 'All' || inv.status === filter;
        return matchesSearch && matchesFilter;
    });

    return (
        <div className="cd-card" style={{ background: '#fcfcfc', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32, paddingBottom: 20, borderBottom: '2px solid #f1f5f9' }}>
                <div>
                    <h3 style={{ margin: 0, fontSize: 24, fontWeight: 900, color: '#0f172a' }}>Financial Statement</h3>
                    <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 13, fontWeight: 600 }}>Billing cycle and service consumption summary.</p>
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                    <button onClick={() => setSubTab('invoices')} className={`cd-filter-btn ${subTab === 'invoices' ? "active" : ""}`} style={{ padding: '10px 24px', borderRadius: 12 }}>Raised Invoices</button>
                    <button onClick={() => setSubTab('services')} className={`cd-filter-btn ${subTab === 'services' ? "active" : ""}`} style={{ padding: '10px 24px', borderRadius: 12 }}>Services Logged</button>
                </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, marginBottom: 32 }}>
                <div style={{ background: 'white', padding: 20, borderRadius: 16, border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: 8 }}>Total Lifecycle Value</div>
                    <div style={{ fontSize: 22, fontWeight: 900, color: '#1e293b' }}>{formatCurrency(totalInvoiced, country)}</div>
                </div>
                <div style={{ background: '#ecfdf5', padding: 20, borderRadius: 16, border: '1px solid #d1fae5', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: '#065f46', textTransform: 'uppercase', marginBottom: 8 }}>Settled Invoices</div>
                    <div style={{ fontSize: 22, fontWeight: 900, color: '#047857' }}>{formatCurrency(totalPaid, country)}</div>
                </div>
                <div style={{ background: '#fff7ed', padding: 20, borderRadius: 16, border: '1px solid #ffedd5', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: '#9a3412', textTransform: 'uppercase', marginBottom: 8 }}>Current Arrears</div>
                    <div style={{ fontSize: 22, fontWeight: 900, color: '#c2410c' }}>{formatCurrency(totalPending, country)}</div>
                </div>
            </div>
            {subTab === 'invoices' ? (
                <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                        <div style={{ display: 'flex', gap: 10 }}>
                            {['All', 'Paid', 'Unpaid', 'Overdue'].map(f => (
                                <button key={f} onClick={() => setFilter(f)} className={`cd-filter-btn ${filter === f ? "active" : ""}`} style={{ fontSize: 12, padding: '6px 16px' }}>{f}</button>
                            ))}
                        </div>
                        <div className="cd-search-wrap" style={{ width: 300 }}><Icon d={icons.search} size={14} color="#94a3b8" /><input className="cd-search-input" placeholder="Search Invoice #..." value={search} onChange={e => setSearch(e.target.value)} /></div>
                    </div>
                    {loading ? <div className="cd-empty">Retrieving Ledger...</div> : filteredInvoices.length === 0 ? <div className="cd-empty">No billing records discovered.</div> : (
                        <div style={{ overflowX: 'auto' }}>
                            <table className="cd-table">
                                <thead>
                                    <tr>
                                        <th className="cd-th">Document Reference</th>
                                        <th className="cd-th">Issue Date</th>
                                        <th className="cd-th">Maturity</th>
                                        <th className="cd-th">Gross Amount</th>
                                        <th className="cd-th">Status</th>
                                        <th className="cd-th" style={{ textAlign: 'right' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredInvoices.map(inv => (
                                        <tr key={inv._id} className="cd-tr">
                                            <td className="cd-td">
                                                <div style={{ fontSize: 14, fontWeight: 800, color: '#1e293b' }}>{inv.invoiceNumber}</div>
                                                <div style={{ fontSize: 10, color: '#94a3b8', letterSpacing: '0.05em' }}>UID: {inv._id.slice(-8).toUpperCase()}</div>
                                            </td>
                                            <td className="cd-td">{inv.issueDate ? new Date(inv.issueDate).toLocaleDateString() : '—'}</td>
                                            <td className="cd-td">{inv.dueDate ? new Date(inv.dueDate).toLocaleDateString() : '—'}</td>
                                            <td className="cd-td"><div style={{ fontSize: 15, fontWeight: 900, color: '#0f172a' }}>{formatCurrency(Number(inv.totalAmount) || 0, country)}</div></td>
                                            <td className="cd-td">
                                                <span className="cd-badge" style={{ background: inv.status === 'Paid' ? '#dcfce7' : inv.status === 'Overdue' ? '#fee2e2' : '#f0f9ff', color: inv.status === 'Paid' ? '#166534' : inv.status === 'Overdue' ? '#991b1b' : '#0369a1' }}>{inv.status}</span>
                                            </td>
                                            <td className="cd-td" style={{ textAlign: 'right' }}><button onClick={() => onSetView({ type: 'invoice', data: inv })} className="cd-icon-btn"><Icon d={icons.analytics} size={14} /></button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table className="cd-table">
                        <thead>
                            <tr>
                                <th className="cd-th">Service Detail / Route</th>
                                <th className="cd-th">Volume (Trips)</th>
                                <th className="cd-th">Completed</th>
                                <th className="cd-th">Last Deployment</th>
                                <th className="cd-th">Allocation</th>
                            </tr>
                        </thead>
                        <tbody>
                            {serviceStats.map(svc => (
                                <tr key={svc._id} className="cd-tr">
                                    <td className="cd-td">
                                        <div style={{ fontSize: 14, fontWeight: 800, color: '#1e293b' }}>{svc.name}</div>
                                        <div style={{ fontSize: 11, color: '#64748b' }}>{svc.pickupLocation.slice(0, 20)}... ➔ {svc.dropLocation.slice(0, 20)}...</div>
                                    </td>
                                    <td className="cd-td"><div style={{ fontSize: 16, fontWeight: 900, color: '#00B2A9' }}>{svc.tripCount}</div></td>
                                    <td className="cd-td"><div style={{ fontSize: 14, fontWeight: 700, color: '#10b981' }}>{svc.completedCount}</div></td>
                                    <td className="cd-td">{svc.lastTripDate ? new Date(svc.lastTripDate).toLocaleDateString() : '—'}</td>
                                    <td className="cd-td"><div style={{ fontSize: 11, fontWeight: 700, color: '#334155', background: '#f1f5f9', padding: '4px 8px', borderRadius: 6, display: 'inline-block' }}>{svc.distance ? `${svc.distance} KM` : 'Fixed Route'}</div></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function QuotationsSection({ quotations, client, country, loading, onSetView }) {
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('All');

    const filtered = quotations.filter(q => {
        const matchesSearch = (q.quotationNumber || '').toLowerCase().includes(search.toLowerCase());
        const matchesFilter = filter === 'All' || q.status === filter;
        return matchesSearch && matchesFilter;
    });

    return (
        <div className="cd-card" style={{ background: '#fcfcfc', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32, paddingBottom: 20, borderBottom: '2px solid #f1f5f9' }}>
                <div>
                    <h3 style={{ margin: 0, fontSize: 24, fontWeight: 900, color: '#0f172a' }}>Commercial Proposals</h3>
                    <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 13, fontWeight: 600 }}>Review formal price quotations and service proposals.</p>
                </div>
                <div className="cd-search-wrap" style={{ width: 300 }}><Icon d={icons.search} size={14} color="#94a3b8" /><input className="cd-search-input" placeholder="Search Quotation #..." value={search} onChange={e => setSearch(e.target.value)} /></div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 24 }}>
                {['All', 'Draft', 'Sent', 'Accepted', 'Declined'].map(f => (
                    <button key={f} onClick={() => setFilter(f)} className={`cd-filter-btn ${filter === f ? "active" : ""}`} style={{ fontSize: 12, padding: '6px 16px' }}>{f}</button>
                ))}
            </div>
            {loading ? <div className="cd-empty">Retrieving Proposals...</div> : quotations.length === 0 ? <div className="cd-empty">No commercial proposals issued yet.</div> : (
                <div style={{ overflowX: 'auto' }}>
                    <table className="cd-table">
                        <thead>
                            <tr>
                                <th className="cd-th">Proposal Reference</th>
                                <th className="cd-th">Issue Date</th>
                                <th className="cd-th">Validity Date</th>
                                <th className="cd-th">Quoted Amount</th>
                                <th className="cd-th">Status</th>
                                <th className="cd-th" style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(q => (
                                <tr key={q._id} className="cd-tr">
                                    <td className="cd-td"><div style={{ fontSize: 14, fontWeight: 800, color: '#1e293b' }}>{q.quotationNumber}</div><div style={{ fontSize: 10, color: '#94a3b8', letterSpacing: '0.05em' }}>UID: {q._id.slice(-8).toUpperCase()}</div></td>
                                    <td className="cd-td">{q.issueDate ? new Date(q.issueDate).toLocaleDateString() : '—'}</td>
                                    <td className="cd-td">{q.validUntil ? new Date(q.validUntil).toLocaleDateString() : '—'}</td>
                                    <td className="cd-td"><div style={{ fontSize: 15, fontWeight: 900, color: '#0f172a' }}>{formatCurrency(Number(q.totalAmount) || 0, country)}</div></td>
                                    <td className="cd-td"><span className="cd-badge" style={{ background: q.status === 'Accepted' ? '#dcfce7' : q.status === 'Declined' ? '#fee2e2' : q.status === 'Sent' ? '#f0f9ff' : '#f1f5f9', color: q.status === 'Accepted' ? '#166534' : q.status === 'Declined' ? '#991b1b' : q.status === 'Sent' ? '#0369a1' : '#475569' }}>{q.status}</span></td>
                                    <td className="cd-td" style={{ textAlign: 'right' }}><div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}><button onClick={() => onSetView({ type: 'quotation', data: q })} className="cd-icon-btn" title="View / Print"><Icon d={icons.analytics} size={14} /></button></div></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function ReportsSection({ type, data, country }) {
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');

    const filtered = data.filter(item => {
        const itemDate = new Date(item.tripDate || item.issueDate);
        const from = dateFrom ? new Date(dateFrom) : null;
        const to = dateTo ? new Date(dateTo) : null;
        if (from && itemDate < from) return false;
        if (to && itemDate > to) return false;
        if (statusFilter !== 'All' && item.status !== statusFilter) return false;
        return true;
    });

    const exportToCSV = () => {
        const header = type === 'trips' ? "Date,Reference,Amount/Category,Status\n" : "Date,Invoice#,Amount,Status\n";
        const rows = filtered.map(item => {
            const date = new Date(item.tripDate || item.issueDate).toLocaleDateString().replace(/,/g, '');
            const ref = (type === 'trips' ? (item.passengerName || item.routeName) : item.invoiceNumber).replace(/,/g, '');
            const amt = type === 'trips' ? 'Operational' : item.totalAmount;
            return `${date},${ref},${amt},${item.status}`;
        }).join("\n");
        const blob = new Blob([header + rows], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `report_${type}_${new Date().toISOString().split('T')[0]}.csv`; a.click();
    };

    return (
        <div className="cd-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <div style={{ fontSize: 16, fontWeight: 800 }}>Detailed {type === 'trips' ? 'Operational' : 'Financial'} Report</div>
                <button onClick={exportToCSV} className="cd-add-btn" style={{ background: '#10b981' }}><Icon d={icons.analytics} size={16} /> Export CSV</button>
            </div>
            <div style={{ display: 'flex', gap: 16, marginBottom: 24, background: '#f8fafc', padding: 20, borderRadius: 16, border: '1px solid #e2e8f0' }}>
                <div style={{ flex: 1 }}><label style={{ display: 'block', fontSize: 12, fontWeight: 800, color: '#64748b', marginBottom: 8 }}>From Date</label><input type="date" className="cd-modal-input" value={dateFrom} onChange={e => setDateFrom(e.target.value)} /></div>
                <div style={{ flex: 1 }}><label style={{ display: 'block', fontSize: 12, fontWeight: 800, color: '#64748b', marginBottom: 8 }}>To Date</label><input type="date" className="cd-modal-input" value={dateTo} onChange={e => setDateTo(e.target.value)} /></div>
                <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 800, color: '#64748b', marginBottom: 8 }}>Filter Status</label>
                    <select className="cd-modal-input" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                        <option value="All">All Statuses</option>
                        {type === 'trips' ? (
                            <>
                                <option value="Scheduled">Scheduled</option>
                                <option value="Completed">Completed</option>
                            </>
                        ) : (
                            <>
                                <option value="Paid">Paid</option>
                                <option value="Unpaid">Unpaid</option>
                            </>
                        )}
                    </select>
                </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
                <table className="cd-table">
                    <thead><tr><th className="cd-th">Date</th><th className="cd-th">Reference</th><th className="cd-th">Classification</th><th className="cd-th">Status</th></tr></thead>
                    <tbody>
                        {filtered.map((item, idx) => (
                            <tr key={idx} className="cd-tr">
                                <td className="cd-td">{new Date(item.tripDate || item.issueDate).toLocaleDateString()}</td>
                                <td className="cd-td" style={{ fontWeight: 800 }}>{type === 'trips' ? (item.passengerName || item.routeName) : item.invoiceNumber}</td>
                                <td className="cd-td">{type === 'trips' ? 'Service Deployment' : formatCurrency(Number(item.totalAmount) || 0, country)}</td>
                                <td className="cd-td"><span className="cd-badge" style={{ background: item.status === 'Completed' || item.status === 'Paid' ? '#dcfce7' : '#f1f5f9', color: item.status === 'Completed' || item.status === 'Paid' ? '#166534' : '#475569' }}>{item.status}</span></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function shortenAddress(addr) {
    if (!addr) return '—';
    return addr.length > 30 ? addr.slice(0, 30) + '...' : addr;
}

function ClientAllocationForm({ client, trip, passengers, masterData, allocations, onClose, onSave }) {
    const [selectedPassengers, setSelectedPassengers] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [routeStops, setRouteStops] = useState([]);
    const [pSearch, setPSearch] = useState('');
    const [loadingStops, setLoadingStops] = useState(false);

    const capacity = React.useMemo(() => {
        if (!trip) return 0;
        if (trip.vehicleId?.seatingCapacity > 0) return trip.vehicleId.seatingCapacity;
        const vType = trip.vehicleId?.vehicleType || trip.vehicleType;
        const master = masterData.find(m => m.category === 'vehicle-types' && m.name === vType);
        return master?.seatingCapacity || 0;
    }, [trip, masterData]);

    useEffect(() => {
        const existing = allocations.filter(a => (a.tripId?._id || a.tripId) === trip._id);
        setSelectedPassengers(existing.map(a => a.passengerId?._id || a.passengerId));
        setAssignments(existing.map(a => ({
            passengerId: a.passengerId?._id || a.passengerId,
            name: a.passengerId?.name,
            pickupStopId: a.pickupStopId?._id || a.pickupStopId || '',
            dropStopId: a.dropStopId?._id || a.dropStopId || '',
            _id: a._id,
            tripId: trip._id
        })));
        if (trip.routeId) {
            setLoadingStops(true);
            fetch(`${API}/route-stops/${trip.routeId._id || trip.routeId}`)
                .then(r => r.json()).then(setRouteStops).catch(err => console.error(err)).finally(() => setLoadingStops(false));
        }
    }, [trip, allocations]);

    useEffect(() => {
        setAssignments(prev => {
            return selectedPassengers.map(pid => {
                const existing = prev.find(a => a.passengerId === pid);
                if (existing) return existing;
                const p = passengers.find(x => x._id === pid);
                const existingAlloc = (allocations || []).find(a => (a.tripId?._id || a.tripId) === trip._id && (a.passengerId?._id || a.passengerId) === pid);
                return { passengerId: pid, name: p?.name || 'Unknown', pickupStopId: existingAlloc?.pickupStopId?._id || existingAlloc?.pickupStopId || '', dropStopId: existingAlloc?.dropStopId?._id || existingAlloc?.dropStopId || '', _id: existingAlloc?._id };
            });
        });
    }, [selectedPassengers, passengers, allocations, trip]);

    const togglePassenger = (pid) => {
        setSelectedPassengers(prev => {
            if (prev.includes(pid)) return prev.filter(id => id !== pid);
            if (capacity > 0 && prev.length >= capacity) { alert(`Vehicle capacity of ${capacity} reached!`); return prev; }
            return [...prev, pid];
        });
    };

    const handleAssignmentChange = (pid, field, value) => {
        setAssignments(prev => prev.map(a => a.passengerId === pid ? { ...a, [field]: value } : a));
    };

    const handleSubmit = () => {
        const final = assignments.map(a => ({ ...a, tripId: trip._id, allocationType: trip.tripFrequency || 'Daily' }));
        onSave(final, trip._id);
    };

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 99999, background: 'white', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '12px 24px', background: 'white', borderBottom: '1px solid #eef2f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                    <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#64748b', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                        <Icon d={icons.chevronDown} size={16} style={{ transform: 'rotate(90deg)' }} /> Back
                    </button>
                    <div>
                        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: '#0f172a' }}>{trip.routeId?.name || trip.routeName}</h2>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Passenger Allocation & Manifest</div>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                    <button onClick={onClose} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 16px', fontSize: 12, fontWeight: 700, color: '#64748b', cursor: 'pointer' }}>Discard Changes</button>
                    <button onClick={handleSubmit} style={{ background: '#0f172a', border: 'none', borderRadius: 8, padding: '8px 20px', fontSize: 12, fontWeight: 700, color: 'white', cursor: 'pointer', boxShadow: '0 4px 12px rgba(15,23,42,0.2)' }}>Confirm {selectedPassengers.length} Allocations</button>
                </div>
            </div>
            <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                <div style={{ width: 360, borderRight: '1px solid #f1f5f9', background: '#f8fafc', padding: '32px 24px', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <div style={{ fontSize: 14, fontWeight: 800, color: '#1e293b' }}>Select Passengers</div>
                        <div style={{ fontSize: 11, fontWeight: 900, color: selectedPassengers.length >= capacity ? '#ef4444' : '#00B2A9', background: 'white', padding: '4px 8px', borderRadius: 6, border: '1px solid #e2e8f0' }}>{selectedPassengers.length} / {capacity || '∞'}</div>
                    </div>
                    <div className="cd-search-wrap" style={{ marginBottom: 20 }}>
                        <Icon d={icons.search} size={14} color="#94a3b8" />
                        <input type="text" placeholder="Search manifest..." value={pSearch} onChange={e => setPSearch(e.target.value)} className="cd-search-input" />
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', paddingRight: 4 }}>
                        {passengers.filter(p => p.name.toLowerCase().includes(pSearch.toLowerCase())).map(p => (
                            <div key={p._id} onClick={() => togglePassenger(p._id)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderRadius: 12, cursor: 'pointer', background: selectedPassengers.includes(p._id) ? 'white' : 'transparent', border: `1px solid ${selectedPassengers.includes(p._id) ? '#00B2A9' : 'transparent'}`, marginBottom: 8, transition: 'all 0.2s', boxShadow: selectedPassengers.includes(p._id) ? '0 2px 8px rgba(99,102,241,0.08)' : 'none' }}>
                                <div style={{ width: 20, height: 20, borderRadius: 6, border: '2px solid #e2e8f0', borderColor: selectedPassengers.includes(p._id) ? '#00B2A9' : '#e2e8f0', background: selectedPassengers.includes(p._id) ? '#00B2A9' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {selectedPassengers.includes(p._id) && <Icon d={icons.check} size={12} color="white" />}
                                </div>
                                <div>
                                    <div style={{ fontWeight: 700, fontSize: 13, color: selectedPassengers.includes(p._id) ? '#0f172a' : '#475569' }}>{p.name}</div>
                                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{p.employeeId || 'STAFF'}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <div style={{ flex: 1, padding: '40px', overflowY: 'auto', background: 'white' }}>
                    {selectedPassengers.length === 0 ? (
                        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                            <Icon d={icons.user} size={48} color="#e2e8f0" style={{ marginBottom: 16 }} />
                            <div style={{ fontSize: 16, fontWeight: 700 }}>No Passengers Selected</div>
                            <p style={{ fontSize: 13, margin: '8px 0 0' }}>Select passengers from the left panel to assign pickup/drop points.</p>
                        </div>
                    ) : (
                        <div style={{ maxWidth: 800, margin: '0 auto' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 32 }}>
                                <div style={{ padding: '8px 16px', background: '#eef2ff', color: '#00B2A9', borderRadius: 8, fontSize: 12, fontWeight: 800 }}>Manifest Detail</div>
                                <div style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>Configure boarding points for each selected passenger</div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16 }}>
                                {assignments.map(a => (
                                    <div key={a.passengerId} style={{ background: 'white', padding: '24px', borderRadius: 20, border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 24, transition: 'all 0.2s', hover: { borderColor: '#00B2A9' } }}>
                                        <div style={{ width: 44, height: 44, background: '#f8fafc', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, color: '#0f172a', fontSize: 18, border: '1px solid #f1f5f9' }}>{a.name.charAt(0)}</div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: 800, color: '#0f172a', marginBottom: 4, fontSize: 15 }}>{a.name}</div>
                                            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Passenger Manifest</div>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '200px 200px', gap: 16 }}>
                                            <div>
                                                <label style={{ fontSize: 10, fontWeight: 800, color: '#94a3b8', display: 'block', marginBottom: 6, textTransform: 'uppercase' }}>Boarding Point</label>
                                                <select value={a.pickupStopId} onChange={e => handleAssignmentChange(a.passengerId, 'pickupStopId', e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, fontWeight: 600, color: '#334155', background: '#f8fafc' }}>
                                                    <option value="">(Default: {shortenAddress(trip.routeId?.pickupLocation || trip.pickupLocation || 'System')})</option>
                                                    {routeStops.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
                                                </select>
                                            </div>
                                            <div>
                                                <label style={{ fontSize: 10, fontWeight: 800, color: '#94a3b8', display: 'block', marginBottom: 6, textTransform: 'uppercase' }}>Drop-off Point</label>
                                                <select value={a.dropStopId} onChange={e => handleAssignmentChange(a.passengerId, 'dropStopId', e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, fontWeight: 600, color: '#334155', background: '#f8fafc' }}>
                                                    <option value="">(Default: {shortenAddress(trip.routeId?.dropLocation || trip.dropLocation || 'System')})</option>
                                                    {routeStops.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
